import registerClientAccountGoalDrawer from './admin/clientAccountGoalDrawer';
import registerMessageReplyComposer from './admin/messageReplyComposer';
import { markIncomingMessageListRead, setIncomingMessageListReadState, syncConfirmedMessageListReads } from './messageListReadState';
import { computed, createApp, h, markRaw } from 'vue';
import { ref } from 'vue';
import {
    Dialog as HDialog, DialogOverlay as HDialogOverlay, DialogPanel as HDialogPanel, DialogTitle as HDialogTitle, DialogDescription as HDialogDescription,
    Disclosure, DisclosureButton, DisclosurePanel,
    Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions,
    Menu as HMenu, MenuButton as HMenuButton, MenuItems as HMenuItems, MenuItem as HMenuItem,
    Popover, PopoverButton, PopoverGroup, PopoverPanel,
    RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption,
    Switch as HSwitch, SwitchGroup as HSwitchGroup, SwitchLabel as HSwitchLabel,
    TabGroup, TabList, Tab, TabPanels, TabPanel,
    TransitionRoot, TransitionChild,
    } from "@headlessui/vue"

import axios from 'axios'
import { Carousel as VCCarousel, Slide as VCSlide, Pagination as VCPagination, Navigation as VCNavigation } from 'vue3-carousel'
import Chart from 'chart.js'
import GphApiClient from 'giphy-js-sdk-core'
import { gsap } from 'gsap';
import heic2any from './heicConversion'
import PonoLitepieDatepicker, { LitepieDatepicker } from './ponoLitepieDatepicker';
import TrainingPlanQuickPreview from './components/admin2026/TrainingPlanQuickPreview.vue';
import TrainingPlanQuickPreviewNote from './components/admin2026/TrainingPlanQuickPreviewNote.vue';
import ClientProgressReport from './components/ClientProgressReport.vue';
import moment from 'moment'
import numeral from 'numeral'
import { QuillEditor } from '@vueup/vue-quill'
import RecordRTC from 'recordrtc'
import 'emoji-picker-element'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import admin2026GlobalSearchMixin from './admin/admin2026GlobalSearchMixin'
import { openAdmin2026ListboxWithTypeahead } from './admin2026ListboxTypeahead'
import { admin2026FloatingMenus } from './admin2026FloatingMenus'
import helpMixins from './mixins/helpMixins'
import aiAnswersDrawerMixin from './mixins/aiAnswersDrawer'
import './featureVideoPlayer'
import './websiteLeadCaptureTracking'
import { normalizeLeadCapturePresentation, websiteLeadCapturePresentationMethods } from './websiteLeadCapturePresentation'
import './admin/automationMap'
import './admin/webLinksMap'
import './admin/websitePagePonoAiEditor'
import './admin/staffActivityTracking'
import {
    ARTICLE_TABLE_BLOT_NAME,
    ARTICLE_TABLE_CLASS_NAME,
    readArticleTableNodeValue,
    writeArticleTableNodeAttributes,
} from './articleTableShared'
import {
    ARTICLE_MERMAID_BLOT_NAME,
    ARTICLE_MERMAID_CLASS_NAME,
    readArticleMermaidNodeValue,
    writeArticleMermaidNodeAttributes,
} from './articleMermaidShared'

import '@vueup/vue-quill/dist/vue-quill.bubble.css'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import 'vue3-carousel/dist/carousel.css'

// axios config
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.headers.common['X-CSRF-TOKEN'] = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
if (typeof window !== 'undefined') {
    window.axios = axios;
}

const admin2026SentSound = (() => {
    let context = null;

    const prepare = () => {
        if (typeof window === 'undefined' || !window.vAdmin2026Chrome) return;
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;

        try {
            context = context || new AudioContextClass();
            if (context.state === 'suspended') context.resume().catch(() => {});
        } catch (error) {}
    };

    const play = () => {
        if (!context || context.state !== 'running') return;

        try {
            const start = context.currentTime;
            [[660, 0], [880, 0.075]].forEach(([frequency, offset]) => {
                const oscillator = context.createOscillator();
                const gain = context.createGain();
                oscillator.type = 'sine';
                oscillator.frequency.value = frequency;
                gain.gain.setValueAtTime(0.0001, start + offset);
                gain.gain.exponentialRampToValueAtTime(0.045, start + offset + 0.012);
                gain.gain.exponentialRampToValueAtTime(0.0001, start + offset + 0.15);
                oscillator.connect(gain);
                gain.connect(context.destination);
                oscillator.start(start + offset);
                oscillator.stop(start + offset + 0.16);
            });
        } catch (error) {}
    };

    return { prepare, play };
})();

const productFinderExternalControlsRuntime = (() => {
    const rootSelector = '[data-product-finder-root][data-product-finder-mode="1"]';
    const trainingRootSelector = rootSelector + '[data-product-finder-layout="training_plan_cards"]';

    const normalizePathKey = (value) => {
        return String(value || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const normalizeCategoryId = (categoryId) => {
        if (categoryId === null || categoryId === undefined || categoryId === '')
        {
            return 'all';
        }

        return String(categoryId);
    };

    const normalizeFilterLabel = (value) => {
        return String(value || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, ' ');
    };

    const canHandleLinkClick = (event, link) => {
        if (!event || !link)
        {
            return false;
        }

        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        {
            return false;
        }

        let target = link.getAttribute('target');
        if (target && target !== '_self')
        {
            return false;
        }

        return true;
    };

    const trainingRoots = () => {
        if (typeof document === 'undefined')
        {
            return [];
        }

        return Array.from(document.querySelectorAll(trainingRootSelector));
    };

    const elementsWithId = (id) => {
        if (typeof document === 'undefined' || !id)
        {
            return [];
        }

        let elements = [];
        let firstMatch = document.getElementById(id);
        if (firstMatch)
        {
            elements.push(firstMatch);
        }

        try
        {
            let escapedId = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
                ? CSS.escape(id)
                : String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

            Array.from(document.querySelectorAll('[id="' + escapedId + '"]')).forEach((element) => {
                if (elements.indexOf(element) === -1)
                {
                    elements.push(element);
                }
            });
        }
        catch (e) {}

        return elements;
    };

    const rootFromTarget = (target) => {
        if (!target)
        {
            return null;
        }

        let root = target.matches && target.matches(trainingRootSelector)
            ? target
            : null;

        if (!root && typeof target.querySelector === 'function')
        {
            root = target.querySelector(trainingRootSelector);
        }

        if (!root && typeof target.closest === 'function')
        {
            root = target.closest(trainingRootSelector);
        }

        return root || null;
    };

    const rootForExternalControl = (control) => {
        if (!control || typeof document === 'undefined')
        {
            return null;
        }

        let href = control.getAttribute('href') || '';
        if (href && typeof window !== 'undefined')
        {
            try
            {
                let targetUrl = new URL(href, window.location.href);
                if (targetUrl.hash)
                {
                    let targetId = decodeURIComponent(targetUrl.hash.slice(1));
                    let targets = elementsWithId(targetId);
                    for (let i = 0; i < targets.length; i++)
                    {
                        let root = rootFromTarget(targets[i]);
                        if (root)
                        {
                            return root;
                        }
                    }
                }
            }
            catch (e) {}
        }

        return trainingRoots()[0] || null;
    };

    const pathFilters = (root) => {
        if (!root)
        {
            return [];
        }

        if (Array.isArray(root._productFinderPathFiltersFallback))
        {
            return root._productFinderPathFiltersFallback;
        }

        let filters = [];
        let configJson = root.getAttribute('data-product-finder-path-filters-json') || '';
        let config = configJson === '' ? root.querySelector('[data-product-finder-path-filters]') : null;
        if (configJson !== '' || config)
        {
            try
            {
                let parsed = JSON.parse(configJson !== '' ? configJson : (config.textContent || '[]'));
                if (Array.isArray(parsed))
                {
                    filters = parsed.map((filter) => {
                        let categoryIds = Array.isArray(filter.category_ids) ? filter.category_ids : [];
                        let tagIds = Array.isArray(filter.tag_ids) ? filter.tag_ids : [];

                        return {
                            key: normalizePathKey(filter.key),
                            label: filter.label || filter.key || '',
                            category_ids: categoryIds.map((id) => String(id)).filter((id) => id !== ''),
                            tag_ids: tagIds.map((id) => String(id)).filter((id) => id !== ''),
                        };
                    }).filter((filter) => {
                        return filter.key && (filter.category_ids.length || filter.tag_ids.length);
                    });
                }
            }
            catch (e)
            {
                filters = [];
            }
        }

        root._productFinderPathFiltersFallback = filters;
        return filters;
    };

    const pathFilterForKey = (root, key) => {
        let normalizedKey = normalizePathKey(key);
        if (!normalizedKey)
        {
            return null;
        }

        return pathFilters(root).find((filter) => {
            return filter.key === normalizedKey;
        }) || null;
    };

    const categoryForFilterLink = (root, value) => {
        if (!root)
        {
            return null;
        }

        let requestedValue = String(value || '').trim();
        let requestedLabel = normalizeFilterLabel(requestedValue);

        if (!requestedValue)
        {
            return null;
        }

        let controls = Array.from(root.querySelectorAll('[data-product-finder-filter]'));
        for (let i = 0; i < controls.length; i++)
        {
            let control = controls[i];
            let categoryId = normalizeCategoryId(control.getAttribute('data-category-id') || control.getAttribute('data-product-finder-category-option'));
            let controlLabel = normalizeFilterLabel(control.getAttribute('data-product-finder-filter-label') || control.textContent || '');

            if (requestedValue === categoryId || requestedLabel === controlLabel)
            {
                return {
                    categoryId: categoryId,
                    href: control.getAttribute('href') || '',
                };
            }
        }

        return null;
    };

    const updateCount = (root, count) => {
        if (!root)
        {
            return;
        }

        let parsedCount = parseInt(count, 10);
        if (Number.isNaN(parsedCount))
        {
            parsedCount = 0;
        }

        let countRoot = root.querySelector('[data-product-finder-count]');
        if (countRoot)
        {
            countRoot.setAttribute('data-product-finder-count', String(parsedCount));
        }

        let countValue = root.querySelector('[data-product-finder-count-value]');
        if (countValue)
        {
            countValue.textContent = String(parsedCount);
        }

        let countLabel = root.querySelector('[data-product-finder-count-label]');
        if (countLabel)
        {
            countLabel.textContent = parsedCount === 1 ? 'result' : 'results';
        }

        let emptyState = root.querySelector('[data-product-finder-empty-state]');
        if (emptyState)
        {
            emptyState.classList.toggle('hidden', parsedCount > 0);
        }
    };

    const updateActiveControls = (root, categoryId) => {
        if (!root)
        {
            return;
        }

        let normalizedCategoryId = normalizeCategoryId(categoryId);
        let activePathKey = normalizePathKey(root.getAttribute('data-product-finder-active-path-key') || '');
        let hasActiveFilter = !!activePathKey || normalizedCategoryId !== 'all';

        Array.from(root.querySelectorAll('[data-product-finder-filter]')).forEach((control) => {
            let controlCategoryId = normalizeCategoryId(control.getAttribute('data-category-id') || control.getAttribute('data-product-finder-category-option'));
            let active = !activePathKey && controlCategoryId === normalizedCategoryId;

            if (active)
            {
                control.setAttribute('aria-current', 'true');
            }
            else
            {
                control.removeAttribute('aria-current');
            }
        });

        Array.from(root.querySelectorAll('[data-product-finder-filter-clear]')).forEach((control) => {
            control.classList.toggle('hidden', !hasActiveFilter);
            if (hasActiveFilter)
            {
                control.removeAttribute('hidden');
            }
            else
            {
                control.setAttribute('hidden', 'hidden');
                control.removeAttribute('aria-current');
            }
        });

        if (typeof document !== 'undefined')
        {
            Array.from(document.querySelectorAll('[data-path-filter]')).forEach((control) => {
                let controlRoot = rootForExternalControl(control);
                if (controlRoot !== root)
                {
                    return;
                }

                let controlPathKey = normalizePathKey(control.getAttribute('data-path-filter') || '');
                if (activePathKey && controlPathKey === activePathKey)
                {
                    control.setAttribute('aria-current', 'true');
                }
                else
                {
                    control.removeAttribute('aria-current');
                }
            });
        }
    };

    const setLoading = (root, loading) => {
        if (!root)
        {
            return;
        }

        let loader = root.querySelector('[data-product-finder-loading]');
        if (loader)
        {
            loader.classList.toggle('hidden', !loading);
        }
    };

    const dispatchProductFinderEvent = (root, name, detail={}) => {
        if (!root || typeof CustomEvent === 'undefined')
        {
            return;
        }

        root.dispatchEvent(new CustomEvent('pono:product-finder:' + name, {
            bubbles: true,
            detail: Object.assign({
                rootId: root.id || '',
            }, detail),
        }));
    };

    const scrollFocus = (root) => {
        if (!root)
        {
            return;
        }

        if (!root.hasAttribute('tabindex'))
        {
            root.setAttribute('tabindex', '-1');
        }

        if (typeof root.scrollIntoView === 'function')
        {
            root.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }

        window.setTimeout(() => {
            if (typeof root.focus === 'function')
            {
                root.focus({ preventScroll: true });
            }
        }, 250);
    };

    const pushHistory = (root, categoryId, href, pathKey='') => {
        if (typeof window === 'undefined' || !window.history || typeof window.history.pushState !== 'function' || !href)
        {
            return;
        }

        try
        {
            let targetUrl = new URL(href, window.location.href);
            if (targetUrl.origin !== window.location.origin || targetUrl.href === window.location.href)
            {
                return;
            }

            window.history.pushState({
                productFinder: true,
                productFinderRootId: root && root.id ? root.id : '',
                productFinderCategoryId: normalizeCategoryId(categoryId),
                productFinderPathKey: normalizePathKey(pathKey),
            }, '', targetUrl.href);
        }
        catch (e) {}
    };

    const fallbackToHref = (href) => {
        if (!href || typeof window === 'undefined')
        {
            return;
        }

        window.setTimeout(() => {
            window.location.href = href;
        }, 0);
    };

    const applyClientFilter = (root, categoryId, options={}) => {
        if (!root)
        {
            return 0;
        }

        let normalizedCategoryId = normalizeCategoryId(categoryId);
        let pathFilter = options.pathFilter || null;
        let visibleCount = 0;

        Array.from(root.querySelectorAll('[data-product-finder-card]')).forEach((card) => {
            let categoryIds = (card.getAttribute('data-category-ids') || '')
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id !== '');
            let tagIds = (card.getAttribute('data-tag-ids') || '')
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id !== '');
            let visible = false;

            if (pathFilter)
            {
                let pathCategoryIds = Array.isArray(pathFilter.category_ids) ? pathFilter.category_ids.map((id) => String(id)) : [];
                let pathTagIds = Array.isArray(pathFilter.tag_ids) ? pathFilter.tag_ids.map((id) => String(id)) : [];
                visible = categoryIds.some((id) => pathCategoryIds.indexOf(id) !== -1)
                    || tagIds.some((id) => pathTagIds.indexOf(id) !== -1);
            }
            else
            {
                visible = normalizedCategoryId === 'all' || categoryIds.indexOf(normalizedCategoryId) !== -1;
            }

            card.classList.toggle('hidden', !visible);
            if (visible)
            {
                card.removeAttribute('hidden');
                visibleCount++;
            }
            else
            {
                card.setAttribute('hidden', 'hidden');
            }
        });

        root.setAttribute('data-product-finder-active-category-id', pathFilter || normalizedCategoryId === 'all' ? '' : normalizedCategoryId);
        root.setAttribute('data-product-finder-active-path-key', pathFilter ? pathFilter.key : '');
        root.setAttribute('data-product-finder-active-path-label', pathFilter ? (pathFilter.label || '') : '');
        updateActiveControls(root, normalizedCategoryId);
        updateCount(root, visibleCount);

        return visibleCount;
    };

    const loadRemoteResults = (root, categoryId, href, options={}) => {
        if (!root)
        {
            return;
        }

        let resultsUrl = root.getAttribute('data-product-finder-results-url') || '';
        if (!resultsUrl || typeof window === 'undefined' || typeof window.fetch !== 'function')
        {
            fallbackToHref(href);
            return;
        }

        let normalizedCategoryId = normalizeCategoryId(categoryId);
        let pathKey = normalizePathKey(options.pathKey || '');
        let requestUrl;

        try
        {
            requestUrl = new URL(resultsUrl, window.location.origin);
            requestUrl.searchParams.set('category_id', normalizedCategoryId);
            if (pathKey)
            {
                requestUrl.searchParams.set('path_key', pathKey);
            }
            else
            {
                requestUrl.searchParams.delete('path_key');
            }
        }
        catch (e)
        {
            fallbackToHref(href);
            return;
        }

        if (root._productFinderFallbackAbortController)
        {
            root._productFinderFallbackAbortController.abort();
            root._productFinderFallbackAbortController = null;
        }

        let fetchOptions = {
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        };

        if (typeof AbortController !== 'undefined')
        {
            root._productFinderFallbackAbortController = new AbortController();
            fetchOptions.signal = root._productFinderFallbackAbortController.signal;
        }

        setLoading(root, true);

        window.fetch(requestUrl.toString(), fetchOptions)
            .then((response) => {
                if (!response.ok)
                {
                    throw new Error('Product finder request failed.');
                }

                return response.json();
            })
            .then((data) => {
                if (!data || !data.success || typeof data.html !== 'string')
                {
                    throw new Error('Product finder response failed.');
                }

                let currentResults = root.querySelector('[data-product-finder-results]');
                let template = document.createElement('template');
                template.innerHTML = data.html.trim();
                let nextResults = template.content.querySelector('[data-product-finder-results]');

                if (currentResults && nextResults)
                {
                    currentResults.replaceWith(nextResults);
                }
                else if (currentResults)
                {
                    currentResults.innerHTML = data.html;
                }

                let activeCategoryId = normalizeCategoryId(data.active_category_id || normalizedCategoryId);
                let activePathKey = normalizePathKey(data.active_path_key || pathKey);
                root.setAttribute('data-product-finder-active-category-id', activePathKey || activeCategoryId === 'all' ? '' : activeCategoryId);
                root.setAttribute('data-product-finder-active-path-key', activePathKey);
                root.setAttribute('data-product-finder-active-path-label', activePathKey ? (data.active_path_label || '') : '');
                updateActiveControls(root, activeCategoryId);
                updateCount(root, parseInt(data.count, 10));
                setLoading(root, false);

                if (activePathKey)
                {
                    dispatchProductFinderEvent(root, 'path-filter', {
                        pathKey: activePathKey,
                        pathLabel: data.active_path_label || '',
                    });
                }
                else
                {
                    dispatchProductFinderEvent(root, 'filter', {
                        categoryId: activeCategoryId,
                    });
                }
            })
            .catch(() => {
                setLoading(root, false);
                fallbackToHref(href);
            });
    };

    const applyPath = (root, pathKey, options={}) => {
        if (!root)
        {
            return false;
        }

        let pathFilter = options.pathFilter || pathFilterForKey(root, pathKey);
        if (!pathFilter)
        {
            return false;
        }

        if (root.getAttribute('data-product-finder-remote') === '1')
        {
            loadRemoteResults(root, 'all', options.href || '', Object.assign({}, options, {
                pathKey: pathFilter.key,
                pathFilter: pathFilter,
            }));
            return true;
        }

        if (root.getAttribute('data-product-finder-client-side') === '1')
        {
            applyClientFilter(root, 'all', {
                pathFilter: pathFilter,
            });
            dispatchProductFinderEvent(root, 'path-filter', {
                pathKey: pathFilter.key,
                pathLabel: pathFilter.label || '',
            });

            if (options.scrollFocus)
            {
                scrollFocus(root);
            }

            if (options.pushHistory)
            {
                pushHistory(root, 'all', options.href || '', pathFilter.key);
            }

            return true;
        }

        return false;
    };

    const applyCategory = (root, categoryId, options={}) => {
        if (!root)
        {
            return false;
        }

        let normalizedCategoryId = normalizeCategoryId(categoryId);

        if (root.getAttribute('data-product-finder-remote') === '1')
        {
            loadRemoteResults(root, normalizedCategoryId, options.href || '', options);
            return true;
        }

        if (root.getAttribute('data-product-finder-client-side') === '1')
        {
            applyClientFilter(root, normalizedCategoryId);
            dispatchProductFinderEvent(root, 'filter', {
                categoryId: normalizedCategoryId,
            });

            if (options.scrollFocus)
            {
                scrollFocus(root);
            }

            if (options.pushHistory)
            {
                pushHistory(root, normalizedCategoryId, options.href || '');
            }

            return true;
        }

        return false;
    };

    const handleClick = (event) => {
        if (!event || !event.target || typeof event.target.closest !== 'function')
        {
            return false;
        }

        let pathControl = event.target.closest('[data-path-filter]');
        if (pathControl)
        {
            let root = rootForExternalControl(pathControl);
            let pathKey = pathControl.getAttribute('data-path-filter') || '';
            let pathFilter = pathFilterForKey(root, pathKey);
            if (!root || !pathFilter || !canHandleLinkClick(event, pathControl))
            {
                return false;
            }

            event.preventDefault();
            applyPath(root, pathFilter.key, {
                href: pathControl.getAttribute('href') || '',
                pathFilter: pathFilter,
                pushHistory: true,
                scrollFocus: true,
            });
            return true;
        }

        let filterLink = event.target.closest('[data-filter-link]');
        if (filterLink)
        {
            let root = rootForExternalControl(filterLink);
            let requestedFilter = filterLink.getAttribute('data-filter-link') || filterLink.textContent || '';
            let categoryMatch = categoryForFilterLink(root, requestedFilter);
            if (!root || !categoryMatch || !canHandleLinkClick(event, filterLink))
            {
                return false;
            }

            event.preventDefault();
            applyCategory(root, categoryMatch.categoryId, {
                href: categoryMatch.href || filterLink.getAttribute('href') || '',
                pushHistory: true,
                scrollFocus: true,
            });
            return true;
        }

        return false;
    };

    const install = () => {
        if (typeof document === 'undefined' || typeof window === 'undefined')
        {
            return;
        }

        if (window.PonoProductFinderExternalControlsInstalled)
        {
            return;
        }

        window.PonoProductFinderExternalControlsInstalled = true;
        document.addEventListener('click', handleClick, true);
    };

    return {
        applyCategory,
        applyClientFilter,
        applyPath,
        handleClick,
        install,
        normalizePathKey,
        rootForExternalControl,
    };
})();

if (typeof window !== 'undefined')
{
    window.PonoProductFinderExternalControls = productFinderExternalControlsRuntime;
    productFinderExternalControlsRuntime.install();
}

// gsap
gsap.registerPlugin(ScrollToPlugin);
gsap.registerPlugin(ScrollTrigger);

// moment config
moment.updateLocale('en', {
    week: {
        dow: 1    // set first day of week to Monday
    }
});

// enable spell checker in quill
import Quill from 'quill'
const Inline = Quill.import('blots/inline');
class CustomColor extends Inline {
  constructor(domNode, value) {
    super(domNode, value);

    // Map <font> properties
    domNode.style.color = domNode.color;

    const span = this.replaceWith(new Inline(Inline.create()));

    span.children.forEach(child => {
      if (child.attributes) child.attributes.copy(span);
      if (child.unwrap) child.unwrap();
    });

    this.remove();

    return span;
  }
}

CustomColor.blotName = "customColor";
CustomColor.tagName = "FONT";
Quill.register(CustomColor, true);

if (!(Quill.imports && Quill.imports['formats/divider'])) {
    const BlockEmbed = Quill.import('blots/block/embed');
    class DividerBlot extends BlockEmbed {}
    DividerBlot.blotName = 'divider';
    DividerBlot.tagName = 'HR';
    Quill.register('formats/divider', DividerBlot, true);
}

if (!(Quill.imports && Quill.imports[`formats/${ARTICLE_MERMAID_BLOT_NAME}`])) {
    const BlockEmbed = Quill.import('blots/block/embed');

    class ArticleMermaidBlot extends BlockEmbed {
        static create(value) {
            const node = super.create();
            writeArticleMermaidNodeAttributes(node, value);
            return node;
        }

        static value(node) {
            return readArticleMermaidNodeValue(node);
        }
    }

    ArticleMermaidBlot.blotName = ARTICLE_MERMAID_BLOT_NAME;
    ArticleMermaidBlot.className = ARTICLE_MERMAID_CLASS_NAME;
    ArticleMermaidBlot.tagName = 'DIV';

    Quill.register(`formats/${ARTICLE_MERMAID_BLOT_NAME}`, ArticleMermaidBlot, true);
}

if (!(Quill.imports && Quill.imports[`formats/${ARTICLE_TABLE_BLOT_NAME}`])) {
    const BlockEmbed = Quill.import('blots/block/embed');

    class ArticleTableBlot extends BlockEmbed {
        static create(value) {
            const node = super.create();
            writeArticleTableNodeAttributes(node, value);
            return node;
        }

        static value(node) {
            return readArticleTableNodeValue(node);
        }
    }

    ArticleTableBlot.blotName = ARTICLE_TABLE_BLOT_NAME;
    ArticleTableBlot.className = ARTICLE_TABLE_CLASS_NAME;
    ArticleTableBlot.tagName = 'DIV';

    Quill.register(`formats/${ARTICLE_TABLE_BLOT_NAME}`, ArticleTableBlot, true);
}

function insertDividerFromToolbar() {
    const quill = this && this.quill ? this.quill : null;
    if (!quill) return;

    const range = quill.getSelection(true);
    const insertIndex = range ? range.index : Math.max(0, quill.getLength() - 1);
    quill.insertEmbed(insertIndex, 'divider', true, 'user');
    quill.insertText(insertIndex + 1, '\n', 'user');
    quill.setSelection(insertIndex + 2, 0, 'silent');
}

let quillOptions = {
    contentType: 'QuillEditor',
    modules: {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'header': 1 }, { 'header': 2 }],
                [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
                [{ 'align': [] }],
                [{ 'indent': '-1'}, { 'indent': '+1' }],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                ['link', 'image', 'video', 'divider'],
                ['clean'],
            ],
            handlers: {
                divider: insertDividerFromToolbar,
            },
        },
    },
    placeholder: 'Start typing here...',
    theme: 'bubble',
    scrollingContainer: 'html',
};
let quillOptionsNoFiles = {
    contentType: 'QuillEditor',
    modules: {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'header': 1 }, { 'header': 2 }],
                [{ 'size': ['small', false, 'large', 'huge'] }],
                [{ 'align': [] }],
                [{ 'indent': '-1'}, { 'indent': '+1' }],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                ['link', 'divider'],
                ['clean'],
            ],
            handlers: {
                divider: insertDividerFromToolbar,
            },
        },
    },
    placeholder: 'Start typing here...',
    theme: 'bubble',
    scrollingContainer: 'html',
};
let quillOptionsOnlyFormatting = {
    contentType: 'QuillEditor',
    modules: {
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'header': 1 }, { 'header': 2 }],
                [{ 'size': ['small', false, 'large', 'huge'] }],
                ['divider', 'clean'],
            ],
            handlers: {
                divider: insertDividerFromToolbar,
            },
        },
    },
    placeholder: 'Start typing here...',
    theme: 'bubble',
    scrollingContainer: 'html',
};
QuillEditor.props.globalOptions.default = () => quillOptions;

const runtimeAppMixins = (typeof appMixins !== 'undefined' && appMixins) ? appMixins : {};
const runtimeAdminMixins = (typeof adminMixins !== 'undefined' && adminMixins) ? adminMixins : {};
const runtimeAdmin2026SupportBoot = (
    typeof window !== 'undefined'
    && window.vAdmin2026Support
    && typeof window.vAdmin2026Support === 'object'
) ? window.vAdmin2026Support : {
    enabled: false,
    store_url: '',
    history_url: '',
    has_history: false,
    types: [],
    context: null,
    context_payload: '',
    context_signature: '',
};
const runtimeAdmin2026LayoutMixins = (
    typeof window !== 'undefined'
    && window.vAdmin2026GlobalSearch
    && window.vAdmin2026GlobalSearch.enabled
    && !(
        runtimeAdminMixins
        && runtimeAdminMixins.methods
        && typeof runtimeAdminMixins.methods.openDashboardGlobalSearch === 'function'
    )
) ? admin2026GlobalSearchMixin : {};

// create app
const app = createApp({
    mixins: [runtimeAppMixins, runtimeAdmin2026LayoutMixins, (typeof window !== 'undefined' && window.admin2026MediaGalleryMixin) || {}, runtimeAdminMixins, helpMixins, aiAnswersDrawerMixin],
    components: {
    },
    data() {
        return {
            vueAppLoading: true,
            isAdminPopoverOpen: false,
            admin2026SidebarCompact: typeof window !== 'undefined' && window.vAdmin2026Sidebar ? !!window.vAdmin2026Sidebar.compact : false,
            admin2026SidebarPersisting: false,
            admin2026SidebarToggleHovered: false,
            admin2026SupportBoot: runtimeAdmin2026SupportBoot,
            admin2026SupportDrawerOpen: false,
            admin2026SupportDrawerPreviousFocusEl: null,
            admin2026SupportSubmitting: false,
            admin2026SupportError: '',
            admin2026SupportTypeOptions: Array.isArray(runtimeAdmin2026SupportBoot.types)
                ? runtimeAdmin2026SupportBoot.types
                : [],
            admin2026SupportForm: {
                support_ticket_type_id: '',
                subject: '',
                body: '',
                regarding_current_screen: true,
            },
            admin2026SupervisorModeDrawerOpen: false,
            admin2026SupervisorModeSaving: false,
            admin2026SupervisorModeValue: typeof window !== 'undefined' && window.vAdmin2026SupervisorMode && window.vAdmin2026SupervisorMode.is_supervisor ? 'on' : 'off',
            admin2026SupervisorModePersistedValue: typeof window !== 'undefined' && window.vAdmin2026SupervisorMode && window.vAdmin2026SupervisorMode.is_supervisor ? 'on' : 'off',
            admin2026OnboardingActive: false,
            admin2026OnboardingCompleting: false,
            admin2026OnboardingResetting: false,
            admin2026OnboardingStepIndex: 0,
            admin2026OnboardingBounds: {
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                visible: false,
            },
            admin2026OnboardingCalloutStyle: {},
            admin2026OnboardingDeferredCallbacks: [],

            Chart: Chart,
            giphy: GphApiClient('wD3vtsmjViV020rB6fXEEeNXzh03fc3R'),
            gsap: gsap,
            heic2any: heic2any,
            moment: moment,
            numeral: numeral,
            RecordRTC: RecordRTC,
            quillOptionsNoFiles: quillOptionsNoFiles,
            quillOptionsOnlyFormatting: quillOptionsOnlyFormatting,

            darkMode: 2,
            account2026Appearance: 'light',
            account2026AppearancePreference: '',
            account2026FrameDrawerNeedsRefresh: false,
            bottomFloatOff: false,
            bottomNavOn: false,
            poweredByPonoFixedOn: true,
            overlayOn: false,
            overlayBottomOn: false,
            ponoai_option: 0,
            scrollDownOn: true,
            treeExpanded: false,
            formTouched: false,
            admin2026DirtyForms: {},
            admin2026PendingUnsavedDrawerCloseMethod: '',
            isHovered: [],
            dashboardSearch: typeof vdashboardSearch !== 'undefined' ? vdashboardSearch : 0,
            carouselActiveIndex: 0,

            adminMenuOpen: [false,false,false,false,false,false,false],
            adminListFilterVisible: false,
            admin2026CategoryNamesVisible: false,
            productFiltersDrawerOpenId: null,
            productFiltersDrawerPreviousFocusEl: null,
            productLeadCaptureModalOpen: false,
            productLeadCaptureModalKey: null,
            productLeadCaptureForms: {},
            websiteLeadCaptureBoot: null,
            websiteLeadCapturePopupOpen: false,
            websiteLeadCaptureActiveKey: null,
            websiteLeadCaptureSuccessPopupOpen: false,
            websiteLeadCaptureSuccessState: null,
            websiteLeadCaptureForms: {},
            websiteLeadCapturePageViewDone: false,
            websiteLeadCaptureDelayTimer: null,
            websiteLeadCaptureScrollHandler: null,
            websiteLeadCaptureExpanded: false,
            websiteLeadCaptureViewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
            websiteLeadCaptureViewportHeight: typeof window !== 'undefined' ? window.innerHeight : 768,
            admin2026ProfilePhotoFileName: '',
            admin2026ClientsSubmenuBadges: {
                counts: {},
                error: false,
                loaded: false,
                loading: false,
                url: '',
            },

            linksOpenInSameTab: true,            // automatically handle link targets to open in the same tab most of the time, see addEventListener in mounted

            modalOpen: [],
            modalImagePreviewParents: {},

            modalOkOpen: false,                  // ok modal
            modalOkTitle: '',
            modalOkContent: '',
            modalOkContentSlot: false,
            modalOkHeaderLink: null,
            modalOkBtn: '',
            modalOkFn: '',
            modalOkId1: null,
            modalOkId2: null,
            modalOkId3: null,

            modalWarningOpen: false,             // warning modal
            modalWarningTitle: '',
            modalWarningContent: '',
            modalWarningBtn: '',

            modalConfirmOpen: false,             // confirm modal
            modalConfirmFn: '',
            modalConfirmId1: null,
            modalConfirmId2: null,
            modalConfirmId3: null,
            modalConfirmTitle: '',
            modalConfirmContent: '',
            modalConfirmBtn1: '',
            modalConfirmBtn2: '',
            modalConfirmSecondaryAction: null,
            modalConfirmContentSlot: false,
            modalConfirmActionsVisible: true,
            modalConfirmKeepOpenOnConfirm: false,
            modalConfirmShowCloseButton: false,
            modalConfirmBusy: false,

            modalNotificationsOpen: false,
            modalNotificationDetailsOpen: false,
            modalNotificationDetailsData: null,
            modalPlaceholdersOpen: false,
            modalPlaceholdersAboveDrawer: false,
            modalPonoAIopen: false,
            modalPonoupgradeOpen: false,

            modalAsset1Src: '',                  // asset modals
            modalAsset1Name: '',                 // asset modals
            modalAsset1Rotation: 0,
            modalAsset1HeicSource: '',
            modalAsset1Frame: '',
            modalAsset1Open: false,
            modalAsset11Src: '',
            modalAsset11Open: false,
            modalAsset13Converting: false,
            modalAsset13Options: {},
            modalAsset13Src: '',
            modalAsset13Open: false,
            modalAsset14Src: '',
            modalAsset14Open: false,
            modalAsset15Src: '',
            modalAsset15Open: false,
            modalAsset16Src: '',
            modalAsset16Open: false,
            modalAsset17Src: '',
            modalAsset17Name: '',
            modalAsset17EmbedSrc: '',
            modalAsset17IsPdf: false,
            modalAsset17Sandbox: '',
            modalAsset17SupportsEmbed: true,
            modalAsset17Message: '',
            modalAsset17Open: false,

            passwordInputVisible: true,            // show/hide password in all password input boxes

            toastApp: '',                          // toast notifications
            toastAdmin: '',
            toastAdminIcon: 'check',
            toastAdminTimeoutId: null,

            notifications: null,
            notificationsRecent: null,
            notificationTypes: null,
            notificationTypeFilter: null,
            notificationGroupLabels: [],
            notificationGroupLabelFilter: 0,
            keepInTouchCount: 0,
            keepInTouchOverdueCount: 0,
            followUps: [],
            visibleNotificationCount: 50,
            unreadNotificationCount: typeof vunreadNotificationCount !== 'undefined' ? vunreadNotificationCount : 0,
            recentNotificationCount: typeof vrecentNotificationCount !== 'undefined' ? vrecentNotificationCount : 0,
            autoMarkNotificationsAsRead: typeof vautoMarkNotificationsAsRead !== 'undefined' ? vautoMarkNotificationsAsRead : 0,
            fullEditMode: typeof vFullEditMode !== 'undefined' ? vFullEditMode : false,

            alertCountDown: 0,                                             // seconds before dismissing b-alert

            acceptBox: null,
            signatureBox: null,

            file_upload: null,
            main_asset: 0,
            preview_asset: 0,
            remove_assets: '',
            uploadAssets: [],
            uploadAssetsCount: 0,
            uploadAssetsRemoved: [],
            fileUploadDragActive: {},

            addLoom: '',
            uploadLooms: [],
            addLoom2: '',
            uploadLooms2: [],
            addYoutube: '',
            uploadYoutubes: [],
            addYoutube2: '',
            uploadYoutubes2: [],
            addYoutube5: '',
            uploadYoutubes5: [],
            addVimeo: '',
            uploadVimeos: [],
            addVimeo2: '',
            uploadVimeos2: [],
            addVimeo5: '',
            uploadVimeos5: [],
            addWistia: '',
            uploadWistias: [],
            addWistia2: '',
            uploadWistias2: [],
            addWistia5: '',
            uploadWistias5: [],

            scrollY: 0,

            userNotesClicked: [],                        // admin has clicked on a note to expand it
            userNotesDismissed: [],                      // highligted note has been dismissed by admin
            userNotesHidden: false,

            sendMessageVisible: false,

            productOptionTrackInventory: true,
            productOptionInventory: 0,
            productOptionInventoryLoaded: false,
        }
    },
    methods:
    {
        admin2026OpenListboxWithTypeahead(event)
        {
            return openAdmin2026ListboxWithTypeahead(event, (callback) => this.$nextTick(callback));
        },

        // PonoAI modal
        openPonoAImodal()
        {
            this.ponoai_option = 0;
            this.modalPonoAIopen = true;
            this.attachModalPonoAIDrawerListeners();
            this.$forceUpdate();
            this.blurFocus();
        },

        // close PonoAI modal
        closeModalPonoAI()
        {
            this.detachModalPonoAIDrawerListeners();
            this.modalPonoAIopen = false;
            this.$forceUpdate();
        },

        guardedDrawerBackdropPointerIsPrimary(event)
        {
            return !!(
                event
                && event.target === event.currentTarget
                && (typeof event.button !== 'number' || event.button === 0)
                && (typeof event.isPrimary !== 'boolean' || event.isPrimary === true)
            );
        },

        guardedDrawerBackdropPointerDown(stateKey, event)
        {
            if (!this.guardedDrawerBackdropPointerIsPrimary(event)) {
                this[stateKey] = null;
                return;
            }

            this[stateKey] = {
                pointerId: typeof event.pointerId === 'number' ? event.pointerId : null,
                clientX: Number.isFinite(Number(event.clientX)) ? Number(event.clientX) : 0,
                clientY: Number.isFinite(Number(event.clientY)) ? Number(event.clientY) : 0,
                startedAt: Number.isFinite(Number(event.timeStamp)) ? Number(event.timeStamp) : Date.now(),
            };
        },

        guardedDrawerBackdropPointerUp(stateKey, event, closeFn)
        {
            const pointer = this[stateKey] || null;

            this[stateKey] = null;

            if (!pointer || !this.guardedDrawerBackdropPointerIsPrimary(event)) {
                return;
            }

            if (pointer.pointerId !== null && typeof event.pointerId === 'number' && event.pointerId !== pointer.pointerId) {
                return;
            }

            const clientX = Number.isFinite(Number(event.clientX)) ? Number(event.clientX) : 0;
            const clientY = Number.isFinite(Number(event.clientY)) ? Number(event.clientY) : 0;
            const endedAt = Number.isFinite(Number(event.timeStamp)) ? Number(event.timeStamp) : Date.now();

            if (Math.abs(clientX - pointer.clientX) > 6 || Math.abs(clientY - pointer.clientY) > 6 || Math.abs(endedAt - pointer.startedAt) > 750) {
                return;
            }

            if (typeof closeFn === 'function') {
                closeFn();
            }
        },

        resetGuardedDrawerBackdropPointer(stateKey)
        {
            this[stateKey] = null;
        },

        attachModalPonoAIDrawerListeners()
        {
            if (typeof document === 'undefined' || this._modalPonoAIDrawerKeydownHandler) {
                return;
            }

            this._modalPonoAIDrawerKeydownHandler = (event) => {
                this.handleModalPonoAIDrawerKeydown(event);
            };

            document.addEventListener('keydown', this._modalPonoAIDrawerKeydownHandler, true);
        },

        detachModalPonoAIDrawerListeners()
        {
            if (typeof document !== 'undefined' && this._modalPonoAIDrawerKeydownHandler) {
                document.removeEventListener('keydown', this._modalPonoAIDrawerKeydownHandler, true);
            }

            this._modalPonoAIDrawerKeydownHandler = null;
            this.resetModalPonoAIBackdropPointer();
        },

        handleModalPonoAIDrawerKeydown(event)
        {
            if (!event || event.key !== 'Escape' || !this.modalPonoAIopen) {
                return;
            }

            if (typeof event.preventDefault === 'function') {
                event.preventDefault();
            }
            if (typeof event.stopPropagation === 'function') {
                event.stopPropagation();
            }
            if (typeof event.stopImmediatePropagation === 'function') {
                event.stopImmediatePropagation();
            }

            this.closeModalPonoAI();
        },

        handleModalPonoAIDialogClose()
        {
            // Ignore generic Headless UI close requests for this drawer.
            // Explicit Escape, backdrop, and button handlers own closing.
        },

        handleModalPonoAIBackdropPointerDown(event)
        {
            this.guardedDrawerBackdropPointerDown('_modalPonoAIBackdropPointer', event);
        },

        handleModalPonoAIBackdropPointerUp(event)
        {
            if (!this.modalPonoAIopen) {
                this.resetModalPonoAIBackdropPointer();
                return;
            }

            this.guardedDrawerBackdropPointerUp(
                '_modalPonoAIBackdropPointer',
                event,
                () => this.closeModalPonoAI()
            );
        },

        resetModalPonoAIBackdropPointer()
        {
            this.resetGuardedDrawerBackdropPointer('_modalPonoAIBackdropPointer');
        },

        // click on a submenu in the admin menu
        clickAdminMenu(item)
        {
            if (this.adminMenuOpen[item])
            {
                this.adminMenuOpen[item] = false;
            }
            else
            {
                this.adminMenuOpen = [false,false,false,false,false,false,false];
                this.adminMenuOpen[item] = true;
            }
        },

        isAdminRoute()
        {
            return !!(window.location && window.location.pathname && window.location.pathname.startsWith('/pono-admin'));
        },

        admin2026DrawerFocusEnabled()
        {
            return typeof window !== 'undefined' && !!window.vAdmin2026Chrome;
        },

        admin2026ClientsSubmenuBadgeCount(key)
        {
            const state = this.admin2026ClientsSubmenuBadges || {};
            const counts = state.counts || {};
            const count = Number(counts[key]);

            return Number.isFinite(count) && count > 0 ? count : 0;
        },

        admin2026ClientsSubmenuBadgeText(key)
        {
            const count = this.admin2026ClientsSubmenuBadgeCount(key);

            return count > 0 ? new Intl.NumberFormat().format(count) : '';
        },

        admin2026ClientsSubmenuBadgeLabel(key)
        {
            const count = this.admin2026ClientsSubmenuBadgeCount(key);
            const formatted = count > 0 ? new Intl.NumberFormat().format(count) : '0';

            return formatted + ' need attention';
        },

        admin2026ClientsLoadSubmenuBadges()
        {
            if (typeof document === 'undefined') {
                return;
            }

            const shell = document.querySelector('[data-admin2026-clients-submenu-badges-url]');

            if (!shell) {
                return;
            }

            const state = this.admin2026ClientsSubmenuBadges || {};
            const url = state.url || shell.getAttribute('data-admin2026-clients-submenu-badges-url') || '';

            if (!url || state.loading || state.loaded) {
                return;
            }

            state.url = url;
            state.loading = true;
            state.error = false;

            this.axios.get(url).then((response) => {
                const data = response && response.data ? response.data : {};

                if (!data.success) {
                    state.error = true;

                    return;
                }

                const rawCounts = data.submenu_counts || data.counts || {};
                const counts = {};

                ['clients', 'chat', 'messages', 'check-ins', 'forms'].forEach((badgeKey) => {
                    const value = Number(rawCounts[badgeKey]);
                    counts[badgeKey] = Number.isFinite(value) && value > 0 ? value : 0;
                });

                if (!counts.clients && typeof data.total !== 'undefined') {
                    const total = Number(data.total);
                    counts.clients = Number.isFinite(total) && total > 0 ? total : 0;
                }

                state.counts = counts;
                state.loaded = true;

                if (this.admin2026ClientsNeedsAttention) {
                    this.admin2026ClientsNeedsAttention.total = counts.clients || 0;
                    this.admin2026ClientsNeedsAttention.loaded = true;
                    this.admin2026ClientsNeedsAttention.error = false;
                }
            }).catch(() => {
                state.error = true;
            }).finally(() => {
                state.loading = false;
            });
        },

        admin2026ElementIsVisible(element)
        {
            if (!element || typeof element.getClientRects !== 'function' || !element.getClientRects().length) {
                return false;
            }

            if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
                const style = window.getComputedStyle(element);

                if (style.display === 'none' || style.visibility === 'hidden') {
                    return false;
                }
            }

            return true;
        },

        admin2026DrawerFieldSelector()
        {
            return [
                'input:not([type="hidden"]):not([disabled]):not([readonly])',
                'select:not([disabled])',
                'textarea:not([disabled]):not([readonly])',
                '[contenteditable="true"]',
                '.ql-editor[contenteditable="true"]',
            ].join(',');
        },

        admin2026ResolveElement(target)
        {
            if (typeof document === 'undefined' || !target) {
                return null;
            }

            if (typeof target === 'string') {
                return document.querySelector(target);
            }

            if (Array.isArray(target)) {
                return target[0] || null;
            }

            if (target && target.$el) {
                return target.$el;
            }

            return target;
        },

        admin2026FindInitialDrawerField(surface)
        {
            if (!surface || typeof surface.querySelectorAll !== 'function') {
                return null;
            }

            const fieldSelector = this.admin2026DrawerFieldSelector();
            const forms = [];

            if (typeof surface.matches === 'function' && surface.matches('form')) {
                forms.push(surface);
            }

            surface.querySelectorAll('form').forEach((form) => {
                forms.push(form);
            });

            for (const form of forms) {
                if (!this.admin2026ElementIsVisible(form)) {
                    continue;
                }

                const field = Array.from(form.querySelectorAll(fieldSelector)).find((candidate) => {
                    return this.admin2026ElementIsVisible(candidate);
                });

                if (field) {
                    return field;
                }
            }

            return null;
        },

        admin2026FindRequestedDrawerField(surface, selector)
        {
            if (!surface || !selector || typeof surface.querySelectorAll !== 'function') {
                return null;
            }

            const candidates = [];

            try {
                if (typeof surface.matches === 'function' && surface.matches(selector)) {
                    candidates.push(surface);
                }

                surface.querySelectorAll(selector).forEach((element) => {
                    candidates.push(element);
                });
            }
            catch (e) {
                return null;
            }

            for (const candidate of candidates) {
                if (!this.admin2026ElementIsVisible(candidate)) {
                    continue;
                }

                if (
                    candidate.id === 'message-content'
                ) {
                    const editor = typeof candidate.querySelector === 'function'
                        ? candidate.querySelector('.ql-editor[contenteditable="true"], [contenteditable="true"]')
                        : null;

                    if (
                        editor
                        && typeof editor.focus === 'function'
                        && this.admin2026ElementIsVisible(editor)
                    ) {
                        return editor;
                    }

                    if (
                        candidate.firstChild
                        && typeof candidate.firstChild.focus === 'function'
                        && this.admin2026ElementIsVisible(candidate.firstChild)
                    ) {
                        return candidate.firstChild;
                    }
                }

                return candidate;
            }

            return null;
        },

        admin2026FocusElement(element)
        {
            if (!element || typeof element.focus !== 'function') {
                return false;
            }

            try {
                element.focus({ preventScroll: true });
            }
            catch (e) {
                element.focus();
            }

            return true;
        },

        admin2026FindDrawerSurfaceFocusTarget(surface)
        {
            if (!surface) {
                return null;
            }

            if (typeof surface.matches === 'function' && surface.matches('.admin2026-drawer-panel')) {
                return surface;
            }

            if (typeof surface.querySelector === 'function') {
                const focusTargets = Array.from(surface.querySelectorAll('[data-admin2026-drawer-focus-target="true"], .admin2026-drawer-panel'));

                return focusTargets.find((focusTarget) => {
                    return this.admin2026ElementIsVisible(focusTarget);
                }) || null;
            }

            return null;
        },

        admin2026ElementHasDrawerMarker(element)
        {
            if (!element) {
                return false;
            }

            if (typeof element.querySelector === 'function' && element.querySelector('.admin2026-drawer-panel')) {
                return true;
            }

            const labelledBy = typeof element.getAttribute === 'function' ? String(element.getAttribute('aria-labelledby') || '') : '';

            if (labelledBy.toLowerCase().includes('drawer')) {
                return true;
            }

            if (!element.attributes) {
                return false;
            }

            return Array.from(element.attributes).some((attribute) => {
                return attribute
                    && typeof attribute.name === 'string'
                    && attribute.name.indexOf('data-') === 0
                    && (
                        attribute.name.toLowerCase().includes('drawer')
                        || String(attribute.value || '').toLowerCase().includes('drawer')
                    );
            });
        },

        admin2026FocusFirstFieldOrSurface(target, options = {})
        {
            if (!this.admin2026DrawerFocusEnabled() || typeof document === 'undefined') {
                return;
            }

            const delay = Number.isFinite(Number(options.delay)) ? Number(options.delay) : 50;

            this.$nextTick(() => {
                setTimeout(() => {
                    const surface = this.admin2026ResolveElement(target);

                    if (!surface || !this.admin2026ElementIsVisible(surface)) {
                        return;
                    }

                    const activeElement = document.activeElement || null;

                    if (
                        options.onlyIfFocusOutside !== false
                        && activeElement
                        && activeElement !== document.body
                        && activeElement !== document.documentElement
                        && typeof surface.contains === 'function'
                        && surface.contains(activeElement)
                    ) {
                        return;
                    }

                    const preferField = options.preferField === true || options.focusField === true;
                    const requestedField = preferField && options.focusSelector
                        ? this.admin2026FindRequestedDrawerField(surface, options.focusSelector)
                        : null;
                    const firstField = preferField
                        ? (requestedField || this.admin2026FindInitialDrawerField(surface))
                        : null;
                    const surfaceFocusTarget = firstField ? null : this.admin2026FindDrawerSurfaceFocusTarget(surface);
                    const focusTarget = firstField || surfaceFocusTarget || surface;

                    if (!firstField && focusTarget && typeof focusTarget.hasAttribute === 'function' && !focusTarget.hasAttribute('tabindex')) {
                        focusTarget.setAttribute('tabindex', '-1');
                    }

                    this.admin2026FocusElement(focusTarget);
                }, Math.max(0, delay));
            });
        },

        admin2026VisibleDrawerDialogs()
        {
            if (!this.admin2026DrawerFocusEnabled() || typeof document === 'undefined') {
                return [];
            }

            return Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]')).filter((dialog) => {
                return this.admin2026ElementIsVisible(dialog)
                    && this.admin2026ElementHasDrawerMarker(dialog);
            });
        },

        admin2026FocusLatestDrawer(options = {})
        {
            const delay = Number.isFinite(Number(options.delay)) ? Number(options.delay) : 50;

            this.$nextTick(() => {
                setTimeout(() => {
                    const drawers = this.admin2026VisibleDrawerDialogs();
                    const drawer = drawers.length ? drawers[drawers.length - 1] : null;

                    if (!drawer) {
                        return;
                    }

                    if (options.markFocused !== false && drawer.dataset) {
                        drawer.dataset.admin2026InitialFocusApplied = 'true';
                    }

                    this.admin2026FocusFirstFieldOrSurface(drawer, {
                        delay: 0,
                        onlyIfFocusOutside: options.onlyIfFocusOutside !== false,
                        preferField: options.preferField === true || options.focusField === true,
                        focusSelector: options.focusSelector || '',
                    });
                }, Math.max(0, delay));
            });
        },

        admin2026FocusDrawerBySelector(selector, options = {})
        {
            this.admin2026FocusFirstFieldOrSurface(selector, options);
        },

        admin2026ScheduleInitialDrawerFocus(delay = 50)
        {
            if (!this.admin2026DrawerFocusEnabled() || typeof window === 'undefined') {
                return;
            }

            if (this._admin2026DrawerFocusTimer) {
                clearTimeout(this._admin2026DrawerFocusTimer);
            }

            this._admin2026DrawerFocusTimer = setTimeout(() => {
                const pendingDrawers = this.admin2026VisibleDrawerDialogs().filter((drawer) => {
                    return !(drawer.dataset && drawer.dataset.admin2026InitialFocusApplied === 'true');
                });

                if (!pendingDrawers.length) {
                    return;
                }

                pendingDrawers.forEach((drawer) => {
                    if (drawer.dataset) {
                        drawer.dataset.admin2026InitialFocusApplied = 'true';
                    }
                });

                this.admin2026FocusFirstFieldOrSurface(pendingDrawers[pendingDrawers.length - 1], {
                    delay: 0,
                    onlyIfFocusOutside: true,
                });
                this.admin2026FocusFirstFieldOrSurface(pendingDrawers[pendingDrawers.length - 1], {
                    delay: 360,
                    onlyIfFocusOutside: true,
                });
            }, Math.max(0, Number(delay) || 0));
        },

        admin2026InstallDrawerFocusObserver()
        {
            if (
                !this.admin2026DrawerFocusEnabled()
                || typeof document === 'undefined'
                || typeof MutationObserver === 'undefined'
                || this._admin2026DrawerFocusObserver
            ) {
                return;
            }

            const root = document.getElementById('app') || document.body;

            if (!root) {
                return;
            }

            this._admin2026DrawerFocusObserver = new MutationObserver(() => {
                this.admin2026ScheduleInitialDrawerFocus(60);
            });

            this._admin2026DrawerFocusObserver.observe(root, {
                childList: true,
                subtree: true,
            });

            this.admin2026ScheduleInitialDrawerFocus(60);
        },

        admin2026FocusMinimalChromeDrawerFrame()
        {
            if (
                !this.admin2026DrawerFocusEnabled()
                || typeof window === 'undefined'
                || !window.vAdmin2026MinimalChrome
            ) {
                return;
            }

            const params = new URLSearchParams(window.location.search || '');

            if (params.get('drawer') !== '1') {
                return;
            }

            const path = String(window.location.pathname || '').toLowerCase();
            const prefersFieldFocus = (
                path.includes('/workspaces/clients/accounts/')
                && (
                    path.includes('/messages/compose')
                    || path.includes('/compose-message')
                    || path.includes('/reply')
                    || /\/chat\/?$/.test(path)
                )
            );
            const focusSelector = /\/chat\/?$/.test(path)
                ? '#content'
                : '#message-content .ql-editor, #message-content [contenteditable="true"], #message-content';

            [80, 250].forEach((delay) => {
                this.admin2026FocusFirstFieldOrSurface('[data-admin2026-client-account-drawer-frame="true"], [data-admin2026-shell="minimal"]', {
                    delay: delay,
                    onlyIfFocusOutside: true,
                    preferField: prefersFieldFocus,
                    focusSelector: prefersFieldFocus ? focusSelector : '',
                });
            });
        },

        getAdminProfileMenu()
        {
            const menuRef = this.$refs ? this.$refs.adminProfileMenu : null;

            if (Array.isArray(menuRef)) {
                return menuRef[0] || null;
            }

            return menuRef || null;
        },

        syncAdminProfileMenuState()
        {
            const adminProfileMenu = this.getAdminProfileMenu();

            this.isAdminPopoverOpen = !!(adminProfileMenu && adminProfileMenu.open);
        },

        admin2026SidebarToggleLabel()
        {
            return this.admin2026SidebarCompact ? 'Expand sidebar' : 'Collapse sidebar';
        },

        admin2026SetSidebarToggleHovered(hovered)
        {
            this.admin2026SidebarToggleHovered = !!hovered;
        },

        admin2026ApplySidebarState()
        {
            if (typeof document === 'undefined')
            {
                return;
            }

            const compact = !!this.admin2026SidebarCompact;
            const layout = compact ? 'compact' : 'default';

            document.querySelectorAll('[data-admin2026-sidebar-shell="true"]').forEach((element) => {
                element.setAttribute('data-admin2026-sidebar-layout', layout);
            });

            document.querySelectorAll('[data-admin2026-edge-chrome-preview="true"]').forEach((element) => {
                element.setAttribute('data-admin2026-edge-sidebar-layout', layout);
            });

            document.querySelectorAll('[data-admin2026-nav-surface="desktop"]').forEach((element) => {
                element.setAttribute('data-admin2026-nav-mode', layout);
            });

            if (typeof window !== 'undefined' && window.vAdmin2026Sidebar)
            {
                window.vAdmin2026Sidebar.compact = compact;
            }
        },

        async admin2026ToggleSidebar()
        {
            if (this.admin2026SidebarPersisting)
            {
                return;
            }

            const sidebarBoot = typeof window !== 'undefined' ? (window.vAdmin2026Sidebar || null) : null;
            const storeUrl = sidebarBoot && sidebarBoot.store_url ? sidebarBoot.store_url : null;

            if (!storeUrl)
            {
                return;
            }

            const previousCompact = !!this.admin2026SidebarCompact;
            const nextCompact = !previousCompact;

            this.admin2026SidebarCompact = nextCompact;
            this.admin2026SidebarToggleHovered = false;
            this.admin2026SidebarPersisting = true;
            this.admin2026ApplySidebarState();

            try
            {
                const response = await this.axios.post(storeUrl, {
                    compact: nextCompact,
                    return_to: window.location.href,
                }, {
                    headers: {
                        Accept: 'application/json',
                    },
                });

                if (!(response && response.data && response.data.success))
                {
                    throw new Error('Sidebar preference response was not successful.');
                }

                this.admin2026SidebarCompact = !!response.data.compact;
                this.admin2026ApplySidebarState();
            }
            catch (error)
            {
                this.admin2026SidebarCompact = previousCompact;
                this.admin2026ApplySidebarState();
                this.openModalWarning('ERROR', 'There was a connection problem and the sidebar preference could not be saved. Please try again.');
            }
            finally
            {
                this.admin2026SidebarPersisting = false;
            }
        },

        admin2026SupervisorModeBoot()
        {
            return typeof window !== 'undefined' && window.vAdmin2026SupervisorMode
                ? window.vAdmin2026SupervisorMode
                : {};
        },

        admin2026SupervisorModeIsOn()
        {
            return this.admin2026SupervisorModePersistedValue === 'on';
        },

        openAdmin2026SupervisorModeDrawer()
        {
            this.admin2026SupervisorModeValue = this.admin2026SupervisorModePersistedValue;
            this.admin2026SupervisorModeDrawerOpen = true;

            this.$nextTick(() => {
                const selectedRadio = document.querySelector('[data-admin2026-supervisor-mode-radio="' + this.admin2026SupervisorModeValue + '"]');
                const closeButton = document.querySelector('[data-admin2026-supervisor-mode-close="true"]');
                const focusTarget = selectedRadio || closeButton;

                if (focusTarget instanceof HTMLElement)
                {
                    focusTarget.focus();
                }
            });
        },

        closeAdmin2026SupervisorModeDrawer()
        {
            if (this.admin2026SupervisorModeSaving)
            {
                return;
            }

            this.admin2026SupervisorModeDrawerOpen = false;
            this.admin2026SupervisorModeValue = this.admin2026SupervisorModePersistedValue;
        },

        async applyAdmin2026SupervisorMode(nextValue)
        {
            const boot = this.admin2026SupervisorModeBoot();
            const endpoint = boot && boot.update_url ? boot.update_url : '';

            if (!['on', 'off'].includes(nextValue) || !endpoint || this.admin2026SupervisorModeSaving)
            {
                return;
            }

            this.admin2026SupervisorModeValue = nextValue;

            if (this.admin2026SupervisorModeValue === this.admin2026SupervisorModePersistedValue)
            {
                this.closeAdmin2026SupervisorModeDrawer();
                return;
            }

            this.admin2026SupervisorModeSaving = true;

            try
            {
                const response = await this.axios.post(endpoint, {
                    supervisor_mode: this.admin2026SupervisorModeValue === 'on',
                }, {
                    headers: {
                        Accept: 'application/json',
                    },
                });

                if (!(response && response.data && response.data.success))
                {
                    throw new Error('Supervisor Mode response was not successful.');
                }

                this.admin2026SupervisorModePersistedValue = response.data.is_supervisor ? 'on' : 'off';
                this.admin2026SupervisorModeValue = this.admin2026SupervisorModePersistedValue;
                this.admin2026SupervisorModeDrawerOpen = false;

                this.showOverlay();
                window.location.reload();
            }
            catch (error)
            {
                this.admin2026SupervisorModeValue = this.admin2026SupervisorModePersistedValue;
                this.openModalWarning('ERROR', 'There was a connection problem and Supervisor Mode could not be saved. Please try again.');
            }
            finally
            {
                this.admin2026SupervisorModeSaving = false;
            }
        },

        admin2026ScreenOnboardingBoot()
        {
            if (typeof window === 'undefined' || !window.vAdmin2026ScreenOnboarding)
            {
                return null;
            }

            return window.vAdmin2026ScreenOnboarding;
        },

        admin2026ScreenOnboardingSteps()
        {
            const boot = this.admin2026ScreenOnboardingBoot();

            return boot && Array.isArray(boot.steps) ? boot.steps : [];
        },

        admin2026ScreenOnboardingIsPending()
        {
            const boot = this.admin2026ScreenOnboardingBoot();

            return !!(boot && !boot.completed && this.admin2026ScreenOnboardingSteps().length);
        },

        admin2026RunAfterScreenOnboarding(callback)
        {
            if (typeof callback !== 'function')
            {
                return false;
            }

            if (!this.admin2026ScreenOnboardingIsPending())
            {
                return false;
            }

            this.admin2026OnboardingDeferredCallbacks.push(callback);

            if (!this.admin2026OnboardingActive && !this.admin2026OnboardingCompleting)
            {
                this.admin2026StartScreenOnboardingIfAvailable();
            }

            return true;
        },

        admin2026FlushScreenOnboardingDeferredCallbacks()
        {
            const callbacks = Array.isArray(this.admin2026OnboardingDeferredCallbacks)
                ? this.admin2026OnboardingDeferredCallbacks.splice(0)
                : [];

            if (!callbacks.length)
            {
                return;
            }

            this.$nextTick(() => {
                callbacks.forEach((callback) => {
                    if (typeof callback === 'function')
                    {
                        callback.call(this);
                    }
                });
            });
        },

        admin2026OnboardingCurrentStep()
        {
            return this.admin2026ScreenOnboardingSteps()[this.admin2026OnboardingStepIndex] || {};
        },

        admin2026RecordScreenOnboardingEvent(eventName, attributes = {})
        {
            if (typeof window === 'undefined') {
                return false;
            }

            const record = () => window.PonoStaffActivity
                && typeof window.PonoStaffActivity.recordOnboardingEvent === 'function'
                && window.PonoStaffActivity.recordOnboardingEvent(eventName, attributes);

            if (record()) {
                return true;
            }

            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', record, {once: true});

                return true;
            }

            return false;
        },

        admin2026OnboardingCurrentTitle()
        {
            return (this.admin2026OnboardingCurrentStep().title || '').toString();
        },

        admin2026OnboardingCurrentBody()
        {
            return (this.admin2026OnboardingCurrentStep().body || '').toString();
        },

        admin2026OnboardingProgressLabel()
        {
            const steps = this.admin2026ScreenOnboardingSteps();
            const total = steps.length;

            if (!total)
            {
                return '';
            }

            return 'Step ' + (this.admin2026OnboardingStepIndex + 1) + ' of ' + total;
        },

        admin2026OnboardingPrimaryLabel()
        {
            return this.admin2026OnboardingIsFinalStep() ? 'Got it' : 'Next';
        },

        admin2026OnboardingHasMultipleSteps()
        {
            return this.admin2026ScreenOnboardingSteps().length > 1;
        },

        admin2026OnboardingCanGoBack()
        {
            return this.admin2026OnboardingStepIndex > 0;
        },

        admin2026OnboardingCanGoNext()
        {
            return this.admin2026OnboardingStepIndex < this.admin2026ScreenOnboardingSteps().length - 1;
        },

        admin2026OnboardingIsFinalStep()
        {
            const steps = this.admin2026ScreenOnboardingSteps();

            return steps.length > 0 && this.admin2026OnboardingStepIndex >= steps.length - 1;
        },

        admin2026StartScreenOnboardingIfAvailable()
        {
            const boot = this.admin2026ScreenOnboardingBoot();
            const steps = this.admin2026ScreenOnboardingSteps();

            if (!boot || !steps.length || boot.completed)
            {
                return false;
            }

            if (this.admin2026OnboardingActive)
            {
                return true;
            }

            this.admin2026OnboardingStepIndex = 0;
            this.admin2026OnboardingActive = true;
            this.admin2026AttachScreenOnboardingListeners();
            this.admin2026RecordScreenOnboardingEvent('tour_started', {
                step_key: steps[0].key || '',
                control_key: boot.key || '',
                outcome: 'started',
            });
            this.admin2026RecordScreenOnboardingEvent('tour_step_viewed', {
                step_key: steps[0].key || '',
                control_key: boot.key || '',
                outcome: 'viewed',
            });

            this.$nextTick(() => {
                this.admin2026ScrollScreenOnboardingToTop();

                window.setTimeout(() => {
                    this.admin2026RefreshScreenOnboardingTarget();
                    this.admin2026FocusScreenOnboardingButton();
                }, 0);
            });

            return true;
        },

        admin2026FindScreenOnboardingTarget(step)
        {
            if (typeof document === 'undefined' || !step || !step.target)
            {
                return null;
            }

            try
            {
                return document.querySelector(step.target);
            }
            catch (e)
            {
                return null;
            }
        },

        admin2026FindScreenOnboardingTargets(step)
        {
            const targets = [];
            const primaryTarget = this.admin2026FindScreenOnboardingTarget(step);

            if (primaryTarget)
            {
                targets.push(primaryTarget);
            }

            const additionalTargetSelectors = step && Array.isArray(step.additional_targets)
                ? step.additional_targets
                : [];

            additionalTargetSelectors.forEach((selector) => {
                if (typeof document === 'undefined' || typeof selector !== 'string' || !selector.trim())
                {
                    return;
                }

                try
                {
                    const additionalTarget = document.querySelector(selector);

                    if (additionalTarget && !targets.includes(additionalTarget))
                    {
                        targets.push(additionalTarget);
                    }
                }
                catch (e)
                {
                    // Ignore invalid optional selectors without affecting the primary tour target.
                }
            });

            return targets;
        },

        admin2026ScreenOnboardingViewportTopInset()
        {
            if (typeof document === 'undefined' || typeof window === 'undefined')
            {
                return 16;
            }

            const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
            const header = document.querySelector('.admin2026-header-shell');

            if (!header || typeof header.getBoundingClientRect !== 'function')
            {
                return 16;
            }

            const rect = header.getBoundingClientRect();

            if (!rect || rect.bottom <= 0 || rect.top >= viewportHeight)
            {
                return 16;
            }

            return Math.min(
                Math.max(16, Math.ceil(rect.bottom) + 12),
                Math.max(16, viewportHeight - 80)
            );
        },

        admin2026ScrollScreenOnboardingToTop()
        {
            if (typeof window === 'undefined')
            {
                return;
            }

            try
            {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'auto',
                });
            }
            catch (e)
            {
                window.scrollTo(0, 0);
            }
        },

        admin2026RefreshScreenOnboardingTarget()
        {
            if (!this.admin2026OnboardingActive || typeof window === 'undefined')
            {
                return;
            }

            const step = this.admin2026OnboardingCurrentStep();
            const targets = this.admin2026FindScreenOnboardingTargets(step);
            const target = targets[0] || null;

            if (!target)
            {
                this.admin2026AdvanceScreenOnboarding();
                return;
            }

            window.setTimeout(() => {
                if (!this.admin2026OnboardingActive)
                {
                    return;
                }

                const rects = targets.map((candidate) => candidate.getBoundingClientRect()).filter((candidateRect) => {
                    return candidateRect && candidateRect.width >= 1 && candidateRect.height >= 1;
                });

                if (!rects.length)
                {
                    this.admin2026AdvanceScreenOnboarding();
                    return;
                }

                const rect = {
                    top: Math.min(...rects.map((candidateRect) => candidateRect.top)),
                    left: Math.min(...rects.map((candidateRect) => candidateRect.left)),
                    right: Math.max(...rects.map((candidateRect) => candidateRect.right)),
                    bottom: Math.max(...rects.map((candidateRect) => candidateRect.bottom)),
                };
                rect.width = rect.right - rect.left;
                rect.height = rect.bottom - rect.top;

                const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
                const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
                const margin = 10;
                const topInset = this.admin2026ScreenOnboardingViewportTopInset();
                const header = document.querySelector('.admin2026-header-shell');
                const targetInsideStickyHeader = header
                    && typeof header.contains === 'function'
                    && (header.contains(target) || targets.slice(1).some((candidate) => header.contains(candidate)));
                const targetTopInset = targetInsideStickyHeader ? 8 : topInset;
                const left = Math.max(8, Math.floor(rect.left - margin));
                const top = Math.max(targetTopInset, Math.floor(rect.top - margin));
                const right = Math.min(viewportWidth - 8, Math.ceil(rect.right + margin));
                const bottom = Math.min(viewportHeight - 8, Math.ceil(rect.bottom + margin));
                const width = Math.max(0, right - left);
                const height = Math.max(0, bottom - top);

                if (width < 1 || height < 1)
                {
                    this.admin2026AdvanceScreenOnboarding();
                    return;
                }

                this.admin2026OnboardingBounds = {
                    top,
                    left,
                    width,
                    height,
                    visible: true,
                };

                const edge = 16;
                const topEdge = Math.max(edge, targetTopInset);
                const gap = 16;
                const preferredCardWidth = 360;
                const availableViewportWidth = Math.max(160, viewportWidth - (edge * 2));
                const minimumCardWidth = Math.min(280, availableViewportWidth);
                const leftSideWidth = Math.max(0, left - gap - edge);
                const rightSideWidth = Math.max(0, viewportWidth - right - gap - edge);
                const sideWidth = Math.max(leftSideWidth, rightSideWidth);
                const cardWidth = sideWidth >= minimumCardWidth
                    ? Math.min(preferredCardWidth, sideWidth)
                    : Math.min(preferredCardWidth, Math.max(minimumCardWidth, availableViewportWidth));
                const callout = this.$refs && this.$refs.admin2026ScreenOnboardingCallout
                    ? this.$refs.admin2026ScreenOnboardingCallout
                    : null;
                const measuredCardHeight = callout && callout.offsetHeight
                    ? Math.ceil(callout.offsetHeight)
                    : 230;
                const cardHeightEstimate = Math.min(
                    Math.max(180, measuredCardHeight),
                    Math.max(180, viewportHeight - topEdge - edge)
                );
                const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));
                const overlapArea = (candidate) => {
                    const candidateRight = candidate.left + cardWidth;
                    const candidateBottom = candidate.top + cardHeightEstimate;
                    const overlapWidth = Math.max(0, Math.min(candidateRight, right) - Math.max(candidate.left, left));
                    const overlapHeight = Math.max(0, Math.min(candidateBottom, bottom) - Math.max(candidate.top, top));

                    return overlapWidth * overlapHeight;
                };
                let calloutTop = edge;
                let calloutLeft = edge;
                let selectedCandidate = null;
                const verticalTop = clamp(top, topEdge, viewportHeight - cardHeightEstimate - edge);
                const horizontalLeft = clamp(left, edge, viewportWidth - cardWidth - edge);
                const sideCandidates = [];

                if (right + gap + cardWidth <= viewportWidth - edge)
                {
                    sideCandidates.push({
                        left: right + gap,
                        top: verticalTop,
                        space: viewportWidth - right,
                    });
                }

                if (left - gap - cardWidth >= edge)
                {
                    sideCandidates.push({
                        left: left - gap - cardWidth,
                        top: verticalTop,
                        space: left,
                    });
                }

                const verticalCandidates = [];

                if (bottom + gap + cardHeightEstimate <= viewportHeight - edge)
                {
                    verticalCandidates.push({
                        left: horizontalLeft,
                        top: bottom + gap,
                        space: viewportHeight - bottom,
                    });
                }

                if (top - gap - cardHeightEstimate >= topEdge)
                {
                    verticalCandidates.push({
                        left: horizontalLeft,
                        top: top - gap - cardHeightEstimate,
                        space: top,
                    });
                }

                const usableSideCandidates = sideCandidates.filter((candidate) => overlapArea(candidate) === 0);
                const usableVerticalCandidates = verticalCandidates.filter((candidate) => overlapArea(candidate) === 0);

                if (step.placement === 'top-center')
                {
                    selectedCandidate = {
                        left: clamp((viewportWidth - cardWidth) / 2, edge, viewportWidth - cardWidth - edge),
                        top: topEdge,
                    };
                }
                else if (usableSideCandidates.length)
                {
                    selectedCandidate = usableSideCandidates.sort((a, b) => b.space - a.space)[0];
                }
                else if (usableVerticalCandidates.length)
                {
                    selectedCandidate = usableVerticalCandidates.sort((a, b) => b.space - a.space)[0];
                }
                else
                {
                    const fallbackCandidates = [
                        {
                            left: edge,
                            top: topEdge,
                        },
                        {
                            left: viewportWidth - cardWidth - edge,
                            top: topEdge,
                        },
                        {
                            left: edge,
                            top: clamp(viewportHeight - cardHeightEstimate - edge, topEdge, viewportHeight - cardHeightEstimate - edge),
                        },
                        {
                            left: viewportWidth - cardWidth - edge,
                            top: clamp(viewportHeight - cardHeightEstimate - edge, topEdge, viewportHeight - cardHeightEstimate - edge),
                        },
                        {
                            left: clamp(right + gap, edge, viewportWidth - cardWidth - edge),
                            top: verticalTop,
                        },
                        {
                            left: clamp(left - gap - cardWidth, edge, viewportWidth - cardWidth - edge),
                            top: verticalTop,
                        },
                        {
                            left: horizontalLeft,
                            top: clamp(bottom + gap, topEdge, viewportHeight - cardHeightEstimate - edge),
                        },
                        {
                            left: horizontalLeft,
                            top: clamp(top - gap - cardHeightEstimate, topEdge, viewportHeight - cardHeightEstimate - edge),
                        },
                    ];

                    selectedCandidate = fallbackCandidates.sort((a, b) => overlapArea(a) - overlapArea(b))[0];
                }

                calloutTop = selectedCandidate ? selectedCandidate.top : edge;
                calloutLeft = selectedCandidate ? selectedCandidate.left : edge;

                this.admin2026OnboardingCalloutStyle = {
                    top: calloutTop + 'px',
                    left: calloutLeft + 'px',
                    width: cardWidth + 'px',
                };
            }, 0);
        },

        admin2026OnboardingHighlightStyle()
        {
            const bounds = this.admin2026OnboardingBounds || {};

            return {
                display: bounds.visible ? 'block' : 'none',
                top: (bounds.top || 0) + 'px',
                left: (bounds.left || 0) + 'px',
                width: (bounds.width || 0) + 'px',
                height: (bounds.height || 0) + 'px',
                boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.78), 0 24px 80px rgba(15, 23, 42, 0.35)',
            };
        },

        admin2026FocusScreenOnboardingButton()
        {
            if (typeof document === 'undefined')
            {
                return;
            }

            window.setTimeout(() => {
                const button = document.querySelector([
                    '[data-admin2026-screen-onboarding-primary="true"]',
                    '[data-admin2026-screen-onboarding-next="true"]:not([disabled])',
                    '[data-admin2026-screen-onboarding-back="true"]:not([disabled])',
                ].join(', '));

                if (button && typeof button.focus === 'function')
                {
                    button.focus();
                }
            }, 50);
        },

        admin2026PreviousScreenOnboarding()
        {
            if (!this.admin2026OnboardingActive || !this.admin2026OnboardingCanGoBack())
            {
                return;
            }

            this.admin2026OnboardingStepIndex -= 1;
            const boot = this.admin2026ScreenOnboardingBoot() || {};
            const step = this.admin2026OnboardingCurrentStep();
            this.admin2026RecordScreenOnboardingEvent('tour_step_back', {
                step_key: step.key || '',
                control_key: boot.key || '',
                outcome: 'viewed',
            });

            this.$nextTick(() => {
                this.admin2026RefreshScreenOnboardingTarget();
                this.admin2026FocusScreenOnboardingButton();
            });
        },

        admin2026AdvanceScreenOnboarding()
        {
            const steps = this.admin2026ScreenOnboardingSteps();

            if (!this.admin2026OnboardingActive || !steps.length)
            {
                return;
            }

            if (this.admin2026OnboardingStepIndex >= steps.length - 1)
            {
                this.admin2026CompleteScreenOnboarding(false);
                return;
            }

            this.admin2026OnboardingStepIndex += 1;
            const boot = this.admin2026ScreenOnboardingBoot() || {};
            const step = this.admin2026OnboardingCurrentStep();
            this.admin2026RecordScreenOnboardingEvent('tour_step_viewed', {
                step_key: step.key || '',
                control_key: boot.key || '',
                outcome: 'viewed',
            });

            this.$nextTick(() => {
                this.admin2026RefreshScreenOnboardingTarget();
                this.admin2026FocusScreenOnboardingButton();
            });
        },

        async admin2026CompleteScreenOnboarding(skipped=false)
        {
            const boot = this.admin2026ScreenOnboardingBoot();

            if (this.admin2026OnboardingCompleting)
            {
                return;
            }

            this.admin2026OnboardingCompleting = true;
            this.admin2026OnboardingActive = false;
            this.admin2026DetachScreenOnboardingListeners();

            if (typeof window !== 'undefined' && window.vAdmin2026ScreenOnboarding)
            {
                window.vAdmin2026ScreenOnboarding.completed = true;
            }

            if (!boot || !boot.key || !boot.complete_endpoint)
            {
                this.admin2026OnboardingCompleting = false;
                this.admin2026FlushScreenOnboardingDeferredCallbacks();
                return;
            }

            try
            {
                const response = await this.axios.post(boot.complete_endpoint, {
                    key: boot.key,
                    skipped: !!skipped,
                }, {
                    headers: {
                        Accept: 'application/json',
                    },
                });

                if (!(response && response.data && response.data.success))
                {
                    throw new Error('Screen onboarding completion response was not successful.');
                }
            }
            catch (error)
            {
                if (typeof this.openModalWarning === 'function')
                {
                    this.openModalWarning('ERROR', 'There was a connection problem and this tour could not be saved as completed. Please try again.');
                }
            }
            finally
            {
                this.admin2026OnboardingCompleting = false;
                this.admin2026FlushScreenOnboardingDeferredCallbacks();
            }
        },

        admin2026AttachScreenOnboardingListeners()
        {
            if (typeof window === 'undefined' || this._admin2026ScreenOnboardingRefreshHandler)
            {
                return;
            }

            this._admin2026ScreenOnboardingRefreshHandler = () => {
                this.admin2026RefreshScreenOnboardingTarget();
            };

            window.addEventListener('resize', this._admin2026ScreenOnboardingRefreshHandler);
            window.addEventListener('scroll', this._admin2026ScreenOnboardingRefreshHandler, true);
        },

        admin2026DetachScreenOnboardingListeners()
        {
            if (typeof window !== 'undefined' && this._admin2026ScreenOnboardingRefreshHandler)
            {
                window.removeEventListener('resize', this._admin2026ScreenOnboardingRefreshHandler);
                window.removeEventListener('scroll', this._admin2026ScreenOnboardingRefreshHandler, true);
            }

            this._admin2026ScreenOnboardingRefreshHandler = null;
        },

        admin2026ConfirmShowOnboardingHelp()
        {
            if (typeof this.help_closeDrawer === 'function')
            {
                this.help_closeDrawer('show-onboarding-help');
            }

            window.setTimeout(() => {
                this.openModalConfirm(
                    'Show onboarding help?',
                    'Would you like to show the onboarding help screens again? If you say yes, then all of the help popups and dashboard widgets that originally appeared will appear again. You can of course dismiss them after you have seen them.',
                    'Yes, show help',
                    'Cancel',
                    'admin2026ResetScreenOnboardingHelp'
                );
            }, 250);
        },

        async admin2026ResetScreenOnboardingHelp()
        {
            if (this.admin2026OnboardingResetting)
            {
                return;
            }

            const endpoint = typeof window !== 'undefined'
                ? (window.vAdmin2026ScreenOnboardingResetEndpoint || '').toString().trim()
                : '';

            if (!endpoint)
            {
                this.openModalWarning('ERROR', 'Onboarding help could not be reset because the reset endpoint is unavailable.');
                return;
            }

            const path = typeof window !== 'undefined' && window.location
                ? window.location.pathname + window.location.search
                : '';

            this.admin2026OnboardingResetting = true;

            try
            {
                const response = await this.axios.post(endpoint, {
                    path,
                }, {
                    headers: {
                        Accept: 'application/json',
                    },
                });

                if (!(response && response.data && response.data.success))
                {
                    throw new Error('Screen onboarding reset response was not successful.');
                }

                this.admin2026StarterContentDismissedKeys = Array.isArray(response.data.starter_content_dismissed_cards)
                    ? response.data.starter_content_dismissed_cards
                    : [];

                if (response.data.has_current_tour && typeof window !== 'undefined')
                {
                    window.location.reload();
                    return;
                }

                this.openModalOk('Onboarding help reset', 'Onboarding help has been reset. You will see the help popups again as you navigate through the admin.', 'OK');
            }
            catch (error)
            {
                this.openModalWarning('ERROR', 'There was a connection problem and onboarding help could not be reset. Please try again.');
            }
            finally
            {
                this.admin2026OnboardingResetting = false;
            }
        },

        closeAdminProfileMenu(refocusTrigger=false)
        {
            const adminProfileMenu = this.getAdminProfileMenu();

            if (!adminProfileMenu) {
                this.isAdminPopoverOpen = false;
                return;
            }

            if (adminProfileMenu.open) {
                adminProfileMenu.open = false;
            }

            this.isAdminPopoverOpen = false;

            if (!refocusTrigger) {
                return;
            }

            const trigger = adminProfileMenu.querySelector('summary');

            if (trigger && typeof trigger.focus === 'function') {
                trigger.focus();
            }
        },

        handleAdminProfileMenuDocumentClick(event)
        {
            if (!this.isAdminPopoverOpen) {
                return;
            }

            const adminProfileMenu = this.getAdminProfileMenu();

            if (!adminProfileMenu || !adminProfileMenu.open) {
                this.isAdminPopoverOpen = false;
                return;
            }

            if (event && adminProfileMenu.contains(event.target)) {
                return;
            }

            this.closeAdminProfileMenu();
        },

        getAdmin2026ProfilePhotoInput()
        {
            const inputRef = this.$refs ? this.$refs.admin2026ProfilePhotoInput : null;

            if (Array.isArray(inputRef)) {
                return inputRef[0] || null;
            }

            return inputRef || null;
        },

        resetAdmin2026ProfilePhotoForm()
        {
            this.admin2026ProfilePhotoFileName = '';

            const input = this.getAdmin2026ProfilePhotoInput();

            if (input) {
                input.value = '';
            }
        },

        openAdmin2026ProfilePhotoModal()
        {
            this.closeAdminProfileMenu();
            this.resetAdmin2026ProfilePhotoForm();
            this.openModal('admin2026-profile-photo');
        },

        closeAdmin2026ProfilePhotoModal()
        {
            this.closeModal('admin2026-profile-photo');
            this.resetAdmin2026ProfilePhotoForm();
        },

        handleAdmin2026ProfilePhotoSelected(event)
        {
            const files = event && event.target && event.target.files ? event.target.files : [];

            this.admin2026ProfilePhotoFileName = files && files[0] ? files[0].name : '';
        },

        submitAdmin2026ProfilePhotoForm()
        {
            const input = this.getAdmin2026ProfilePhotoInput();

            if (!(input && input.files && input.files.length)) {
                this.openModalWarning('Photo required', 'Choose a photo before saving your profile photo.');
                return;
            }

            this.submitForm('admin2026-profile-photo-form');
        },

        // click on an item in the admin menu
        clickAdminMenuItem(e, item)
        {
            // show overlay when opening in same window
            try
            {
                if (!e.shiftKey && !e.ctrlKey && !e.metaKey)
                {
                    this.showOverlay();
                }
            }
            catch(ee) {}
        },

        // show admin filter
        showAdminFilter()
        {
            this.adminListFilterVisible = true;
        },

        // hide admin filter
        hideAdminFilter(url)
        {
            this.adminListFilterVisible = false;

            this.showOverlay();

            location.href = url;
        },

        toggleAdmin2026CategoryNames()
        {
            this.admin2026CategoryNamesVisible = !this.admin2026CategoryNamesVisible;

            try
            {
                if (window.localStorage)
                {
                    window.localStorage.setItem(
                        'ponoAdmin2026CategoryNamesVisible',
                        this.admin2026CategoryNamesVisible ? '1' : '0'
                    );
                }
            }
            catch (e) {}
        },

        // choose new avatar
        chooseAvatar(inputId='avatar-upload')
        {
            const input = document.getElementById(inputId);

            if (input)
            {
                input.click();
            }
        },

        // blur focus from all inputs
        blurFocus()
        {
            setTimeout(() => {
                let el = document.querySelector(':focus');
                if (el)
                {
                    const isAdmin2026DrawerSurface = typeof window !== 'undefined'
                        && !!window.vAdmin2026Chrome
                        && (
                            (
                                el.classList
                                && el.classList.contains('admin2026-drawer-panel')
                            )
                            || (
                                typeof el.getAttribute === 'function'
                                && el.getAttribute('role') === 'dialog'
                                && el.getAttribute('aria-modal') === 'true'
                                && typeof this.admin2026ElementHasDrawerMarker === 'function'
                                && this.admin2026ElementHasDrawerMarker(el)
                            )
                        );

                    if (isAdmin2026DrawerSurface)
                    {
                        return;
                    }

                    el.blur();
                }
            }, 200);
        },

        blurFocusAndStopPropagation(e)
        {
            e.stopPropagation();
            this.blurFocus();
        },

        resolveQuillRef(refOrName)
        {
            let quillRef = refOrName;
            if (typeof refOrName === 'string' && this.$refs)
            {
                quillRef = this.$refs[refOrName];
            }

            if (Array.isArray(quillRef))
            {
                quillRef = quillRef[0];
            }

            return quillRef || null;
        },

        getQuillInstance(refOrName)
        {
            const quillRef = this.resolveQuillRef(refOrName);
            if (!this.isQuillEditorReady(quillRef) || typeof quillRef.getQuill !== 'function')
            {
                return null;
            }

            try
            {
                return quillRef.getQuill() || null;
            }
            catch (error)
            {
                return null;
            }
        },

        getQuillEmbed(refOrName, blotName, domNode=null)
        {
            const quill = this.getQuillInstance(refOrName);
            if (!quill || !blotName)
            {
                return null;
            }

            let blot = null;
            let resolvedNode = domNode || null;

            if (resolvedNode && typeof Quill.find === 'function')
            {
                blot = Quill.find(resolvedNode, true);
            }
            else
            {
                const range = quill.getSelection();
                if (!range)
                {
                    return null;
                }

                const candidateIndexes = range.length > 0 ? [range.index] : [range.index, range.index - 1];
                for (const candidateIndex of candidateIndexes)
                {
                    if (candidateIndex < 0)
                    {
                        continue;
                    }

                    const [leaf] = quill.getLeaf(candidateIndex);
                    if (leaf && leaf.statics && leaf.statics.blotName === blotName)
                    {
                        blot = leaf;
                        break;
                    }
                }
            }

            if (!blot || !blot.statics || blot.statics.blotName !== blotName)
            {
                return null;
            }

            resolvedNode = resolvedNode || blot.domNode || null;

            let index = null;
            let length = 1;
            let value = null;

            try
            {
                index = quill.getIndex(blot);
            }
            catch (error) {}

            try
            {
                length = typeof blot.length === 'function' ? blot.length() : 1;
            }
            catch (error) {}

            try
            {
                value = typeof blot.statics.value === 'function' ? blot.statics.value(resolvedNode) : null;
            }
            catch (error) {}

            return {
                quill,
                blot,
                domNode: resolvedNode,
                index,
                length,
                value,
            };
        },

        isQuillEditorReady(quillRef)
        {
            if (!quillRef || typeof quillRef.setHTML !== 'function')
            {
                return false;
            }

            if (quillRef.$el && !document.body.contains(quillRef.$el))
            {
                return false;
            }

            if (typeof quillRef.getQuill === 'function')
            {
                try
                {
                    const quill = quillRef.getQuill();
                    if (!quill || !quill.root || !quill.emitter)
                    {
                        return false;
                    }
                }
                catch (error)
                {
                    return false;
                }
            }

            return true;
        },

        setQuillHtmlSafely(refOrName, html, runInNextTick=false, retryCount=4)
        {
            const content = typeof html === 'string' ? html : '';
            const applyHtml = (remainingRetries) => {
                const quillRef = this.resolveQuillRef(refOrName);
                if (!this.isQuillEditorReady(quillRef))
                {
                    if (remainingRetries > 0)
                    {
                        setTimeout(() => {
                            applyHtml(remainingRetries - 1);
                        }, 50);
                    }

                    return false;
                }

                try
                {
                    quillRef.setHTML(content);
                    return true;
                }
                catch (error)
                {
                    return false;
                }
            };

            if (runInNextTick)
            {
                this.$nextTick(() => {
                    applyHtml(retryCount);
                });

                return true;
            }

            return applyHtml(retryCount);
        },

        // normalize legacy Quill HTML spacing artifacts without touching live editor DOM
        normalizeQuillHtml(html)
        {
            if (typeof html !== 'string')
            {
                return html;
            }

            let normalized = html
                // Normalize empty Quill paragraphs to a single canonical blank line marker.
                .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '<p><br></p>')
                .replace(/<p>(?:\s|&nbsp;|\u00a0)*<\/p>/gi, '<p><br></p>');

            return normalized;
        },
        
        // Find all Quill editor elements and modify content, to avoid a bug with "<p> <br> </p>"
        cleanQuill()
        {
            try
            {
                setTimeout(() => {
                    const tEditors = document.querySelectorAll('.ql-editor');
                    tEditors.forEach((tEditor) => {
                        // Never mutate Quill-managed DOM directly; it can desync selection state.
                        const quillInstance = tEditor.closest('.ql-container')?.__quill;
                        if (quillInstance)
                        {
                            return;
                        }

                        const cleaned = this.normalizeQuillHtml(tEditor.innerHTML || '');
                        if (cleaned !== tEditor.innerHTML)
                        {
                            tEditor.innerHTML = cleaned;
                        }
                    });
                }, 100);
            }
            catch (error) {}
        },

        // toggle dark mode
        toggleDarkMode(value)
        {
            this.darkMode = value;

            if (this.darkMode == 1)
            {
                localStorage.ponoTheme = 'verylight';
                document.documentElement.classList.remove('dark');
            }
            else if (this.darkMode == 2)
            {
                localStorage.ponoTheme = 'light';
                document.documentElement.classList.remove('dark');
            }
            else if (this.darkMode == 3)
            {
                localStorage.ponoTheme = 'blue';
                document.documentElement.classList.remove('dark');
            }
            else if (this.darkMode == 4)
            {
                localStorage.ponoTheme = 'dark';
                document.documentElement.classList.add('dark');
            }
            else
            {
                localStorage.ponoTheme = 'auto';
                document.documentElement.classList.remove('dark');

                if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
                {
                    document.documentElement.classList.add('dark');
                }
            }

            if (this.account2026AppearanceBoot())
            {
                this.account2026ApplyAppearance(this.account2026ResolvedAppearance(this.account2026AppearancePreference));
            }
        },

        // toggle admin full edit more
        toggleFullEditMode()
        {
            this.fullEditMode = !this.fullEditMode;

            this.axios.post('/pono-admin/pono-ai/full-edit-mode', {'full_edit_mode': this.fullEditMode});
        },

        // handle scroll events
        scrollListener()
        {
            // show/hide powered by Pono on scroll
            if (window.scrollY > 100)
            {
                this.poweredByPonoFixedOn = false;
            }
            else
            {
                this.poweredByPonoFixedOn = true;
            }

            // show/hide bottom nav links for scroll to top and contact
            if (window.scrollY > 400)
            {
                this.bottomNavOn = true;
                this.scrollDownOn = false;
            }
            else
            {
                this.bottomNavOn = false;
                this.scrollDownOn = true;
            }
        },

        // scroll down
        scrollDown()
        {
            window.scrollTo(0, window.innerHeight);
        },

        // scroll to bottom
        scrollToBottom()
        {
            window.scrollTo(0, 9999999);
        },

        // scroll to top
        scrollToTop()
        {
            window.scrollTo(0, 0);
        },

        // reload page
        reloadPage()
        {
            this.showOverlay();
            window.location.reload();
        },

        // reload page without triggering any POST to repear
        reloadPageViaRedirect()
        {
            this.showOverlay();
            window.location.href = window.location.href;
        },

        // viewing on mobile device
        isMobile()
        {
            return window.innerWidth < 768 ? true : false;
        },

        // check if is numeric
        isNumeric(str) {
            return !isNaN(str) && !isNaN(parseFloat(str));
        },

        isProductFiltersDrawerOpen(drawerId)
        {
            return this.productFiltersDrawerOpenId === drawerId;
        },

        openProductFiltersDrawer(drawerId, event=null)
        {
            if (!drawerId)
            {
                return;
            }

            if (event && typeof event.preventDefault === 'function')
            {
                event.preventDefault();
            }

            if (this.productFiltersDrawerOpenId && this.productFiltersDrawerOpenId !== drawerId)
            {
                this.closeProductFiltersDrawer(this.productFiltersDrawerOpenId, false);
            }

            this.productFiltersDrawerPreviousFocusEl = document.activeElement;
            this.productFiltersDrawerOpenId = drawerId;
            document.documentElement.classList.add('overflow-hidden');

            this.$nextTick(() => {
                let closeBtn = document.querySelector('[data-mobile-filters-close="' + drawerId + '"]');
                if (closeBtn && typeof closeBtn.focus === 'function')
                {
                    closeBtn.focus();
                }
            });
        },

        closeProductFiltersDrawer(drawerId=null, restoreFocus=true)
        {
            if (drawerId && this.productFiltersDrawerOpenId !== drawerId)
            {
                return;
            }

            if (!this.productFiltersDrawerOpenId)
            {
                return;
            }

            let openDrawerId = this.productFiltersDrawerOpenId;
            let focusTarget = this.productFiltersDrawerPreviousFocusEl;

            this.productFiltersDrawerOpenId = null;
            this.productFiltersDrawerPreviousFocusEl = null;
            document.documentElement.classList.remove('overflow-hidden');

            if (!restoreFocus)
            {
                return;
            }

            this.$nextTick(() => {
                if (
                    !focusTarget ||
                    typeof focusTarget.focus !== 'function' ||
                    !document.contains(focusTarget)
                )
                {
                    focusTarget = document.querySelector('[data-mobile-filters-open="' + openDrawerId + '"]');
                }

                if (focusTarget && typeof focusTarget.focus === 'function')
                {
                    focusTarget.focus();
                }
            });
        },

        productLeadCaptureShell()
        {
            return document.querySelector('[data-product-lead-capture-shell="true"]');
        },

        productLeadCapturePrefill()
        {
            let shell = this.productLeadCaptureShell();

            return {
                name: shell ? (shell.getAttribute('data-product-lead-capture-name') || '') : '',
                email: shell ? (shell.getAttribute('data-product-lead-capture-email') || '') : '',
            };
        },

        normalizeProductLeadCapturePayload(payload={})
        {
            let normalized = payload && typeof payload === 'object' ? payload : {};

            return {
                id: normalized.id ? parseInt(normalized.id, 10) : 0,
                submit_url: normalized.submit_url || '',
                title: normalized.title || 'Offer',
                summary_html: normalized.summary_html || '',
                image_url: normalized.image_url || '',
                submit_label: normalized.submit_label || 'Get access',
            };
        },

        productLeadCaptureFormState(key, payload=null)
        {
            if (!key)
            {
                return null;
            }

            let current = this.productLeadCaptureForms[key];
            let normalizedPayload = payload ? this.normalizeProductLeadCapturePayload(payload) : null;

            if (!current)
            {
                let prefill = this.productLeadCapturePrefill();
                current = {
                    payload: normalizedPayload || this.normalizeProductLeadCapturePayload(),
                    name: prefill.name,
                    email: prefill.email,
                    errors: {},
                    submitting: false,
                };

                this.productLeadCaptureForms[key] = current;
            }
            else if (normalizedPayload)
            {
                current.payload = normalizedPayload;
            }

            return current;
        },

        productLeadCaptureActiveState()
        {
            return this.productLeadCaptureModalKey
                ? this.productLeadCaptureFormState(this.productLeadCaptureModalKey)
                : null;
        },

        productLeadCaptureInitTurnstile(root = null)
        {
            if (typeof window === 'undefined'
                || !window.PonoTurnstile
                || typeof window.PonoTurnstile.init !== 'function')
            {
                return;
            }

            window.PonoTurnstile.init(root || document);
        },

        openProductLeadCaptureModal(payload, event=null)
        {
            if (event && typeof event.preventDefault === 'function')
            {
                event.preventDefault();
            }

            if (event && typeof event.stopPropagation === 'function')
            {
                event.stopPropagation();
            }

            let normalizedPayload = this.normalizeProductLeadCapturePayload(payload);
            let key = 'product-' + (normalizedPayload.id || 'modal');
            let state = this.productLeadCaptureFormState(key, normalizedPayload);

            if (state)
            {
                state.errors = {};
            }

            this.productLeadCaptureModalKey = key;
            this.productLeadCaptureModalOpen = true;
            document.documentElement.classList.add('overflow-hidden');

            this.$nextTick(() => {
                let formEl = this.$refs.productLeadCaptureModalForm;
                if (Array.isArray(formEl))
                {
                    formEl = formEl[0];
                }

                this.productLeadCaptureInitTurnstile(formEl || document);

                let input = formEl && typeof formEl.querySelector === 'function'
                    ? formEl.querySelector('[data-product-lead-capture-primary-input="true"]')
                    : document.querySelector('[data-product-lead-capture-primary-input="true"]');
                if (input && typeof input.focus === 'function')
                {
                    input.focus();
                }
            });
        },

        handleProductLeadCaptureTriggerClick(event)
        {
            if (!event || !event.target || typeof event.target.closest !== 'function')
            {
                return false;
            }

            let trigger = event.target.closest('[data-product-lead-capture-payload]');
            if (!trigger)
            {
                return false;
            }

            let payload = {};
            try
            {
                payload = JSON.parse(trigger.getAttribute('data-product-lead-capture-payload') || '{}');
            }
            catch (e)
            {
                payload = {};
            }

            let finderRoot = trigger.closest('[data-product-finder-root][data-product-finder-mode="1"]');
            if (finderRoot)
            {
                this.dispatchProductFinderEvent(finderRoot, 'lead-capture-cta', {
                    productId: payload && payload.id ? payload.id : 0,
                });
            }

            this.openProductLeadCaptureModal(payload, event);
            return true;
        },

        productFinderRoots()
        {
            if (typeof document === 'undefined')
            {
                return [];
            }

            return Array.from(document.querySelectorAll('[data-product-finder-root][data-product-finder-mode="1"]'));
        },

        productFinderTrainingRoots()
        {
            return this.productFinderRoots().filter((root) => {
                return root.getAttribute('data-product-finder-layout') === 'training_plan_cards';
            });
        },

        normalizeProductFinderPathKey(value)
        {
            return String(value || '')
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
        },

        normalizeProductFinderFilterLabel(value)
        {
            return String(value || '')
                .trim()
                .toLowerCase()
                .replace(/\s+/g, ' ');
        },

        productFinderPathFilters(root)
        {
            if (!root)
            {
                return [];
            }

            if (Array.isArray(root._productFinderPathFilters))
            {
                return root._productFinderPathFilters;
            }

            let filters = [];
            let configJson = root.getAttribute('data-product-finder-path-filters-json') || '';
            let config = configJson === '' ? root.querySelector('[data-product-finder-path-filters]') : null;
            if (configJson !== '' || config)
            {
                try
                {
                    let parsed = JSON.parse(configJson !== '' ? configJson : (config.textContent || '[]'));
                    if (Array.isArray(parsed))
                    {
                        filters = parsed.map((filter) => {
                            let categoryIds = Array.isArray(filter.category_ids) ? filter.category_ids : [];
                            let tagIds = Array.isArray(filter.tag_ids) ? filter.tag_ids : [];

                            return {
                                key: this.normalizeProductFinderPathKey(filter.key),
                                label: filter.label || filter.key || '',
                                category_ids: categoryIds.map((id) => String(id)).filter((id) => id !== ''),
                                tag_ids: tagIds.map((id) => String(id)).filter((id) => id !== ''),
                            };
                        }).filter((filter) => {
                            return filter.key && (filter.category_ids.length || filter.tag_ids.length);
                        });
                    }
                }
                catch (e)
                {
                    filters = [];
                }
            }

            root._productFinderPathFilters = filters;
            return filters;
        },

        productFinderPathFilterForKey(root, key)
        {
            let normalizedKey = this.normalizeProductFinderPathKey(key);
            if (!normalizedKey)
            {
                return null;
            }

            return this.productFinderPathFilters(root).find((filter) => {
                return filter.key === normalizedKey;
            }) || null;
        },

        productFinderRootForExternalControl(control)
        {
            if (!control || typeof document === 'undefined')
            {
                return null;
            }

            let href = control.getAttribute('href') || '';
            if (href && typeof window !== 'undefined')
            {
                try
                {
                    let targetUrl = new URL(href, window.location.href);
                    if (targetUrl.hash)
                    {
                        let target = document.getElementById(decodeURIComponent(targetUrl.hash.slice(1)));
                        if (target)
                        {
                            let root = target.matches && target.matches('[data-product-finder-root][data-product-finder-mode="1"]')
                                ? target
                                : target.closest('[data-product-finder-root][data-product-finder-mode="1"]');
                            if (root && root.getAttribute('data-product-finder-layout') === 'training_plan_cards')
                            {
                                return root;
                            }
                        }
                    }
                }
                catch (e) {}
            }

            return this.productFinderTrainingRoots()[0] || null;
        },

        productFinderCategoryForFilterLink(root, value)
        {
            if (!root)
            {
                return null;
            }

            let requestedValue = String(value || '').trim();
            let requestedLabel = this.normalizeProductFinderFilterLabel(requestedValue);

            if (!requestedValue)
            {
                return null;
            }

            let controls = Array.from(root.querySelectorAll('[data-product-finder-filter]'));
            for (let i = 0; i < controls.length; i++)
            {
                let control = controls[i];
                let categoryId = this.normalizeProductFinderCategoryId(control.getAttribute('data-category-id') || control.getAttribute('data-product-finder-category-option'));
                let controlLabel = this.normalizeProductFinderFilterLabel(control.getAttribute('data-product-finder-filter-label') || control.textContent || '');

                if (requestedValue === categoryId || requestedLabel === controlLabel)
                {
                    return {
                        categoryId: categoryId,
                        href: control.getAttribute('href') || '',
                    };
                }
            }

            return null;
        },

        productFinderScrollFocus(root)
        {
            if (!root)
            {
                return;
            }

            if (!root.hasAttribute('tabindex'))
            {
                root.setAttribute('tabindex', '-1');
            }

            if (typeof root.scrollIntoView === 'function')
            {
                root.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }

            window.setTimeout(() => {
                if (typeof root.focus === 'function')
                {
                    root.focus({ preventScroll: true });
                }
            }, 250);
        },

        dispatchProductFinderEvent(root, name, detail={})
        {
            if (!root || typeof CustomEvent === 'undefined')
            {
                return;
            }

            root.dispatchEvent(new CustomEvent('pono:product-finder:' + name, {
                bubbles: true,
                detail: Object.assign({
                    rootId: root.id || '',
                }, detail),
            }));
        },

        initProductFinderRoot(root)
        {
            if (!root || root.getAttribute('data-product-finder-initialized') === '1')
            {
                return;
            }

            root.setAttribute('data-product-finder-initialized', '1');

            if (!root.id)
            {
                root.id = 'product-finder-' + Math.random().toString(36).slice(2);
            }

            let categoryId = root.getAttribute('data-product-finder-active-category-id') || 'all';
            let pathKey = root.getAttribute('data-product-finder-active-path-key') || '';

            if (root.getAttribute('data-product-finder-client-side') === '1')
            {
                let pathFilter = this.productFinderPathFilterForKey(root, pathKey);
                if (pathFilter)
                {
                    this.applyProductFinderClientFilter(root, 'all', {
                        pathFilter: pathFilter,
                    });
                }
                else
                {
                    this.applyProductFinderClientFilter(root, categoryId);
                }
                return;
            }

            this.updateProductFinderActiveControls(root, categoryId);
            this.updateProductFinderCount(root);
        },

        normalizeProductFinderCategoryId(categoryId)
        {
            if (categoryId === null || categoryId === undefined || categoryId === '')
            {
                return 'all';
            }

            return String(categoryId);
        },

        productFinderCanHandleLinkClick(event, link)
        {
            if (!event || !link)
            {
                return false;
            }

            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
            {
                return false;
            }

            let target = link.getAttribute('target');
            if (target && target !== '_self')
            {
                return false;
            }

            if (link.hasAttribute('download'))
            {
                return false;
            }

            return true;
        },

        productFinderCardHasInteractiveTarget(event, card)
        {
            if (!event || !event.target || typeof event.target.closest !== 'function' || !card)
            {
                return false;
            }

            let interactive = event.target.closest('a, button, input, select, textarea, label, summary, [data-product-lead-capture-payload], [data-product-finder-filter], [data-path-filter], [data-filter-link]');

            return interactive && interactive !== card;
        },

        activateProductFinderCard(root, card, event)
        {
            if (!root || !card || this.productFinderCardHasInteractiveTarget(event, card))
            {
                return false;
            }

            if (event && event.type === 'click' && !this.productFinderCanHandleLinkClick(event, card))
            {
                return false;
            }

            let leadCapturePayload = card.getAttribute('data-product-finder-card-lead-capture-payload');
            this.dispatchProductFinderEvent(root, 'product-card-click', {
                productId: card.getAttribute('data-product-id') || 0,
                leadCapture: !!leadCapturePayload,
            });

            if (leadCapturePayload)
            {
                let payload = {};
                try
                {
                    payload = JSON.parse(leadCapturePayload || '{}');
                }
                catch (e)
                {
                    payload = {};
                }

                this.dispatchProductFinderEvent(root, 'lead-capture-cta', {
                    productId: payload && payload.id ? payload.id : (card.getAttribute('data-product-id') || 0),
                });
                this.openProductLeadCaptureModal(payload, event);
                return true;
            }

            let href = card.getAttribute('data-product-finder-card-href') || '';
            if (!href || typeof window === 'undefined')
            {
                return false;
            }

            if (event && typeof event.preventDefault === 'function')
            {
                event.preventDefault();
            }
            if (event && typeof event.stopPropagation === 'function')
            {
                event.stopPropagation();
            }

            window.location.href = href;
            return true;
        },

        handleProductFinderExternalControlClick(event)
        {
            if (!event || !event.target || typeof event.target.closest !== 'function')
            {
                return false;
            }

            let pathControl = event.target.closest('[data-path-filter]');
            if (pathControl)
            {
                let root = this.productFinderRootForExternalControl(pathControl);
                let pathKey = pathControl.getAttribute('data-path-filter') || '';
                let pathFilter = this.productFinderPathFilterForKey(root, pathKey);
                if (!root || !pathFilter || !this.productFinderCanHandleLinkClick(event, pathControl))
                {
                    return false;
                }

                event.preventDefault();
                this.applyProductFinderPath(root, pathFilter.key, {
                    href: pathControl.getAttribute('href') || '',
                    pushHistory: true,
                    scrollFocus: true,
                });
                return true;
            }

            let filterLink = event.target.closest('[data-filter-link]');
            if (filterLink)
            {
                let root = this.productFinderRootForExternalControl(filterLink);
                let requestedFilter = filterLink.getAttribute('data-filter-link') || filterLink.textContent || '';
                let categoryMatch = this.productFinderCategoryForFilterLink(root, requestedFilter);
                if (!root || !categoryMatch || !this.productFinderCanHandleLinkClick(event, filterLink))
                {
                    return false;
                }

                event.preventDefault();
                this.applyProductFinderCategory(root, categoryMatch.categoryId, {
                    href: categoryMatch.href || filterLink.getAttribute('href') || '',
                    pushHistory: true,
                    scrollFocus: true,
                });
                return true;
            }

            return false;
        },

        handleProductFinderDocumentClick(event)
        {
            if (!event || !event.target || typeof event.target.closest !== 'function')
            {
                return false;
            }

            let toggle = event.target.closest('[data-product-finder-toggle]');
            if (toggle)
            {
                let toggleRoot = toggle.closest('[data-product-finder-root][data-product-finder-mode="1"]');
                if (!toggleRoot)
                {
                    return false;
                }

                event.preventDefault();
                this.toggleProductFinderPanel(toggleRoot);
                return true;
            }

            if (this.handleProductFinderExternalControlClick(event))
            {
                return true;
            }

            let card = event.target.closest('[data-product-finder-card]');
            if (card)
            {
                let cardRoot = card.closest('[data-product-finder-root][data-product-finder-mode="1"]');
                if (cardRoot && this.activateProductFinderCard(cardRoot, card, event))
                {
                    return true;
                }
            }

            let filter = event.target.closest('[data-product-finder-filter]');
            if (!filter)
            {
                return false;
            }

            let root = filter.closest('[data-product-finder-root][data-product-finder-mode="1"]');
            if (!root || !this.productFinderCanHandleLinkClick(event, filter))
            {
                return false;
            }

            let isClientSide = root.getAttribute('data-product-finder-client-side') === '1';
            let isRemote = root.getAttribute('data-product-finder-remote') === '1';
            if (!isClientSide && !isRemote)
            {
                return false;
            }

            let categoryId = filter.getAttribute('data-category-id') || filter.getAttribute('data-product-finder-category-option') || 'all';
            let href = filter.getAttribute('href') || '';

            event.preventDefault();
            this.applyProductFinderCategory(root, categoryId, {
                href: href,
                pushHistory: true,
            });
            return true;
        },

        toggleProductFinderPanel(root, open=null)
        {
            if (!root)
            {
                return false;
            }

            let disclosure = root.querySelector('[data-product-finder-disclosure]');
            if (!disclosure)
            {
                return false;
            }

            let nextOpen = open === null ? !disclosure.open : !!open;
            disclosure.open = nextOpen;

            let toggle = root.querySelector('[data-product-finder-toggle]');
            if (toggle)
            {
                toggle.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
            }

            if (!nextOpen)
            {
                let panel = root.querySelector('[data-product-finder-panel]');
                if (panel && panel.contains(document.activeElement) && toggle && typeof toggle.focus === 'function')
                {
                    toggle.focus();
                }
            }

            return true;
        },

        applyProductFinderCategory(root, categoryId, options={})
        {
            if (!root)
            {
                return;
            }

            let normalizedCategoryId = this.normalizeProductFinderCategoryId(categoryId);
            root.setAttribute('data-product-finder-active-path-key', '');
            root.setAttribute('data-product-finder-active-path-label', '');

            if (root.getAttribute('data-product-finder-remote') === '1')
            {
                this.loadProductFinderRemoteResults(root, normalizedCategoryId, options.href || '', options);
                return;
            }

            if (root.getAttribute('data-product-finder-client-side') === '1')
            {
                this.applyProductFinderClientFilter(root, normalizedCategoryId);
                this.dispatchProductFinderEvent(root, 'category-filter', {
                    categoryId: normalizedCategoryId,
                });

                if (options.scrollFocus)
                {
                    this.productFinderScrollFocus(root);
                }

                if (options.pushHistory)
                {
                    this.productFinderPushHistory(root, normalizedCategoryId, options.href || '');
                }
            }
        },

        applyProductFinderPath(root, pathKey, options={})
        {
            if (!root)
            {
                return false;
            }

            let pathFilter = options.pathFilter || this.productFinderPathFilterForKey(root, pathKey);
            if (!pathFilter)
            {
                return false;
            }

            if (root.getAttribute('data-product-finder-remote') === '1')
            {
                this.loadProductFinderRemoteResults(root, 'all', options.href || '', Object.assign({}, options, {
                    pathKey: pathFilter.key,
                    pathFilter: pathFilter,
                }));
                return true;
            }

            if (root.getAttribute('data-product-finder-client-side') === '1')
            {
                this.applyProductFinderClientFilter(root, 'all', {
                    pathFilter: pathFilter,
                });
                this.dispatchProductFinderEvent(root, 'path-filter', {
                    pathKey: pathFilter.key,
                    pathLabel: pathFilter.label || '',
                });

                if (options.scrollFocus)
                {
                    this.productFinderScrollFocus(root);
                }

                if (options.pushHistory)
                {
                    this.productFinderPushHistory(root, 'all', options.href || '', pathFilter.key);
                }

                return true;
            }

            return false;
        },

        applyProductFinderClientFilter(root, categoryId, options={})
        {
            if (!root)
            {
                return 0;
            }

            let normalizedCategoryId = this.normalizeProductFinderCategoryId(categoryId);
            let pathFilter = options.pathFilter || null;
            let visibleCount = 0;

            Array.from(root.querySelectorAll('[data-product-finder-card]')).forEach((card) => {
                let categoryIds = (card.getAttribute('data-category-ids') || '')
                    .split(',')
                    .map((id) => id.trim())
                    .filter((id) => id !== '');
                let tagIds = (card.getAttribute('data-tag-ids') || '')
                    .split(',')
                    .map((id) => id.trim())
                    .filter((id) => id !== '');
                let visible = false;

                if (pathFilter)
                {
                    let pathCategoryIds = Array.isArray(pathFilter.category_ids) ? pathFilter.category_ids.map((id) => String(id)) : [];
                    let pathTagIds = Array.isArray(pathFilter.tag_ids) ? pathFilter.tag_ids.map((id) => String(id)) : [];
                    visible = categoryIds.some((id) => pathCategoryIds.indexOf(id) !== -1)
                        || tagIds.some((id) => pathTagIds.indexOf(id) !== -1);
                }
                else
                {
                    visible = normalizedCategoryId === 'all' || categoryIds.indexOf(normalizedCategoryId) !== -1;
                }

                card.classList.toggle('hidden', !visible);
                if (visible)
                {
                    card.removeAttribute('hidden');
                    visibleCount++;
                }
                else
                {
                    card.setAttribute('hidden', 'hidden');
                }
            });

            root.setAttribute('data-product-finder-active-category-id', pathFilter || normalizedCategoryId === 'all' ? '' : normalizedCategoryId);
            root.setAttribute('data-product-finder-active-path-key', pathFilter ? pathFilter.key : '');
            root.setAttribute('data-product-finder-active-path-label', pathFilter ? (pathFilter.label || '') : '');
            this.updateProductFinderActiveControls(root, normalizedCategoryId);
            this.updateProductFinderCount(root, visibleCount);

            return visibleCount;
        },

        handleProductFinderCardKeydown(event)
        {
            if (!event || (event.key !== 'Enter' && event.key !== ' '))
            {
                return false;
            }

            if (!event.target || typeof event.target.closest !== 'function')
            {
                return false;
            }

            let card = event.target.closest('[data-product-finder-card]');
            if (!card)
            {
                return false;
            }

            let root = card.closest('[data-product-finder-root][data-product-finder-mode="1"]');

            return this.activateProductFinderCard(root, card, event);
        },

        loadProductFinderRemoteResults(root, categoryId, href, options={})
        {
            if (!root)
            {
                return;
            }

            let resultsUrl = root.getAttribute('data-product-finder-results-url') || '';
            if (!resultsUrl || typeof window === 'undefined' || typeof window.fetch !== 'function')
            {
                this.productFinderFallbackToHref(href);
                return;
            }

            let normalizedCategoryId = this.normalizeProductFinderCategoryId(categoryId);
            let pathKey = this.normalizeProductFinderPathKey(options.pathKey || '');
            let requestUrl;

            try
            {
                requestUrl = new URL(resultsUrl, window.location.origin);
                requestUrl.searchParams.set('category_id', normalizedCategoryId);
                if (pathKey)
                {
                    requestUrl.searchParams.set('path_key', pathKey);
                }
                else
                {
                    requestUrl.searchParams.delete('path_key');
                }
            }
            catch (e)
            {
                this.productFinderFallbackToHref(href);
                return;
            }

            if (root._productFinderAbortController)
            {
                root._productFinderAbortController.abort();
                root._productFinderAbortController = null;
            }

            let fetchOptions = {
                credentials: 'same-origin',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            };

            if (typeof AbortController !== 'undefined')
            {
                root._productFinderAbortController = new AbortController();
                fetchOptions.signal = root._productFinderAbortController.signal;
            }

            this.setProductFinderLoading(root, true);

            window.fetch(requestUrl.toString(), fetchOptions)
                .then((response) => {
                    if (!response.ok)
                    {
                        throw new Error('Product finder request failed.');
                    }

                    return response.json();
                })
                .then((data) => {
                    if (!data || !data.success || typeof data.html !== 'string')
                    {
                        throw new Error('Product finder response failed.');
                    }

                    let currentResults = root.querySelector('[data-product-finder-results]');
                    let template = document.createElement('template');
                    template.innerHTML = data.html.trim();
                    let nextResults = template.content.querySelector('[data-product-finder-results]');

                    if (currentResults && nextResults)
                    {
                        currentResults.replaceWith(nextResults);
                    }
                    else if (currentResults)
                    {
                        currentResults.innerHTML = data.html;
                    }

                    let activeCategoryId = this.normalizeProductFinderCategoryId(data.active_category_id || normalizedCategoryId);
                    let activePathKey = this.normalizeProductFinderPathKey(data.active_path_key || pathKey);
                    root.setAttribute('data-product-finder-active-category-id', activePathKey || activeCategoryId === 'all' ? '' : activeCategoryId);
                    root.setAttribute('data-product-finder-active-path-key', activePathKey);
                    root.setAttribute('data-product-finder-active-path-label', activePathKey ? (data.active_path_label || '') : '');
                    this.updateProductFinderActiveControls(root, activeCategoryId);
                    this.updateProductFinderCount(root, parseInt(data.count, 10));
                    this.setProductFinderLoading(root, false);

                    if (activePathKey)
                    {
                        this.dispatchProductFinderEvent(root, 'path-filter', {
                            pathKey: activePathKey,
                            pathLabel: data.active_path_label || '',
                        });
                    }
                    else
                    {
                        this.dispatchProductFinderEvent(root, 'category-filter', {
                            categoryId: activeCategoryId,
                        });
                    }

                    if (options.scrollFocus)
                    {
                        this.productFinderScrollFocus(root);
                    }

                    if (options.pushHistory)
                    {
                        this.productFinderPushHistory(root, activeCategoryId, href, activePathKey);
                    }
                })
                .catch((error) => {
                    if (error && error.name === 'AbortError')
                    {
                        return;
                    }

                    if (href)
                    {
                        this.productFinderFallbackToHref(href);
                        return;
                    }

                    this.setProductFinderLoading(root, false);
                });
        },

        setProductFinderLoading(root, isLoading)
        {
            if (!root)
            {
                return;
            }

            root.setAttribute('aria-busy', isLoading ? 'true' : 'false');

            Array.from(root.querySelectorAll('[data-product-finder-loading]')).forEach((loadingEl) => {
                loadingEl.classList.toggle('hidden', !isLoading);
            });
        },

        updateProductFinderCount(root, forcedCount=null)
        {
            if (!root)
            {
                return 0;
            }

            let count = Number.isFinite(forcedCount) ? forcedCount : null;
            if (count === null)
            {
                count = Array.from(root.querySelectorAll('[data-product-finder-card]')).filter((card) => !card.hasAttribute('hidden')).length;
            }

            let countEl = root.querySelector('[data-product-finder-count]');
            if (countEl)
            {
                countEl.setAttribute('data-product-finder-count', count);
            }

            let valueEl = root.querySelector('[data-product-finder-count-value]');
            if (valueEl)
            {
                valueEl.textContent = count;
            }

            let labelEl = root.querySelector('[data-product-finder-count-label]');
            if (labelEl)
            {
                labelEl.textContent = count === 1 ? 'result' : 'results';
            }

            let emptyState = root.querySelector('[data-product-finder-empty-state]');
            if (emptyState)
            {
                emptyState.classList.toggle('hidden', count > 0);
            }

            return count;
        },

        updateProductFinderActiveControls(root, categoryId)
        {
            if (!root)
            {
                return;
            }

            let normalizedCategoryId = this.normalizeProductFinderCategoryId(categoryId);
            let activePathKey = this.normalizeProductFinderPathKey(root.getAttribute('data-product-finder-active-path-key') || '');
            let hasActiveFilter = !!activePathKey || normalizedCategoryId !== 'all';
            let activeClasses = ['border-slate-950', 'bg-slate-950', 'text-white', 'hover:bg-slate-900'];
            let inactiveClasses = ['border-slate-200', 'text-slate-900', 'hover:bg-slate-50'];

            Array.from(root.querySelectorAll('[data-product-finder-filter]')).forEach((control) => {
                let controlCategoryId = this.normalizeProductFinderCategoryId(control.getAttribute('data-category-id') || control.getAttribute('data-product-finder-category-option'));
                let active = !activePathKey && controlCategoryId === normalizedCategoryId;

                if (active)
                {
                    control.setAttribute('aria-current', 'true');
                }
                else
                {
                    control.removeAttribute('aria-current');
                }

                if (control.hasAttribute('data-product-finder-option-control'))
                {
                    activeClasses.forEach((className) => control.classList.toggle(className, active));
                    inactiveClasses.forEach((className) => control.classList.toggle(className, !active));

                    Array.from(control.querySelectorAll('[data-product-finder-option-muted]')).forEach((mutedEl) => {
                        mutedEl.classList.toggle('text-slate-200', active);
                        mutedEl.classList.toggle('text-slate-500', !active);
                    });
                }
            });

            Array.from(root.querySelectorAll('[data-product-finder-filter-clear]')).forEach((control) => {
                control.classList.toggle('hidden', !hasActiveFilter);
                if (hasActiveFilter)
                {
                    control.removeAttribute('hidden');
                }
                else
                {
                    control.setAttribute('hidden', 'hidden');
                    control.removeAttribute('aria-current');
                }
            });

            if (typeof document !== 'undefined')
            {
                Array.from(document.querySelectorAll('[data-path-filter]')).forEach((control) => {
                    let controlRoot = this.productFinderRootForExternalControl(control);
                    if (controlRoot !== root)
                    {
                        return;
                    }

                    let controlPathKey = this.normalizeProductFinderPathKey(control.getAttribute('data-path-filter') || '');
                    if (activePathKey && controlPathKey === activePathKey)
                    {
                        control.setAttribute('aria-current', 'true');
                    }
                    else
                    {
                        control.removeAttribute('aria-current');
                    }
                });
            }
        },

        handleProductFinderKeydown(event)
        {
            if (!event || event.key !== 'Escape')
            {
                return false;
            }

            let openRoot = this.productFinderRoots().find((root) => {
                let disclosure = root.querySelector('[data-product-finder-disclosure]');
                return disclosure && disclosure.open;
            });

            if (!openRoot)
            {
                return false;
            }

            event.preventDefault();
            this.toggleProductFinderPanel(openRoot, false);
            return true;
        },

        handleProductFinderPopState(event)
        {
            this.productFinderRoots().forEach((root) => {
                let categoryId = null;
                let pathKey = null;

                if (
                    event &&
                    event.state &&
                    event.state.productFinder &&
                    (!event.state.productFinderRootId || event.state.productFinderRootId === root.id)
                )
                {
                    categoryId = event.state.productFinderCategoryId;
                    pathKey = event.state.productFinderPathKey || '';
                }

                if (pathKey)
                {
                    this.applyProductFinderPath(root, pathKey, {
                        href: window.location.href,
                        pushHistory: false,
                    });
                    return;
                }

                if (!categoryId)
                {
                    categoryId = this.productFinderCategoryIdForCurrentUrl(root);
                }

                this.applyProductFinderCategory(root, categoryId || 'all', {
                    href: window.location.href,
                    pushHistory: false,
                });
            });
        },

        productFinderCategoryIdForCurrentUrl(root)
        {
            if (!root || typeof window === 'undefined')
            {
                return 'all';
            }

            let currentUrl;
            try
            {
                currentUrl = new URL(window.location.href);
            }
            catch (e)
            {
                return root.getAttribute('data-product-finder-active-category-id') || 'all';
            }

            let controls = Array.from(root.querySelectorAll('[data-product-finder-filter][href]'));
            for (let i = 0; i < controls.length; i++)
            {
                try
                {
                    let controlUrl = new URL(controls[i].getAttribute('href'), window.location.href);
                    if (controlUrl.origin === currentUrl.origin && controlUrl.pathname === currentUrl.pathname)
                    {
                        return controls[i].getAttribute('data-category-id') || controls[i].getAttribute('data-product-finder-category-option') || 'all';
                    }
                }
                catch (e) {}
            }

            return root.getAttribute('data-product-finder-active-category-id') || 'all';
        },

        productFinderPushHistory(root, categoryId, href, pathKey='')
        {
            if (typeof window === 'undefined' || !window.history || typeof window.history.pushState !== 'function' || !href)
            {
                return;
            }

            try
            {
                let targetUrl = new URL(href, window.location.href);
                if (targetUrl.origin !== window.location.origin || targetUrl.href === window.location.href)
                {
                    return;
                }

                window.history.pushState({
                    productFinder: true,
                    productFinderRootId: root && root.id ? root.id : '',
                    productFinderCategoryId: this.normalizeProductFinderCategoryId(categoryId),
                    productFinderPathKey: this.normalizeProductFinderPathKey(pathKey),
                }, '', targetUrl.href);
            }
            catch (e) {}
        },

        productFinderFallbackToHref(href)
        {
            if (!href || typeof window === 'undefined')
            {
                return;
            }

            window.setTimeout(() => {
                window.location.href = href;
            }, 150);
        },

        closeProductLeadCaptureModal()
        {
            this.productLeadCaptureModalOpen = false;
            // Keep the active form available until the closing transition unmounts it.
            document.documentElement.classList.remove('overflow-hidden');
        },

        productLeadCaptureFirstError(key, field)
        {
            let state = this.productLeadCaptureFormState(key);
            if (!state || !state.errors || !state.errors[field] || !state.errors[field].length)
            {
                return '';
            }

            return state.errors[field][0];
        },

        productLeadCaptureTrimmedValue(value)
        {
            if (typeof value === 'string')
            {
                return value.trim();
            }

            if (value === null || typeof value === 'undefined')
            {
                return '';
            }

            return String(value).trim();
        },

        productLeadCaptureHasRequiredFields(key)
        {
            let state = this.productLeadCaptureFormState(key);
            if (!state)
            {
                return false;
            }

            return this.productLeadCaptureTrimmedValue(state.name) !== ''
                && this.productLeadCaptureTrimmedValue(state.email) !== '';
        },

        productLeadCaptureCanSubmit(key)
        {
            let state = this.productLeadCaptureFormState(key);
            if (!state || state.submitting)
            {
                return false;
            }

            return this.productLeadCaptureHasRequiredFields(key);
        },

        productLeadCaptureHandleDownload(url, filename = '')
        {
            if (!url || typeof document === 'undefined')
            {
                window.location.assign(url);
                return;
            }

            let link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener';

            if (filename)
            {
                link.setAttribute('download', filename);
            }

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        async submitProductLeadCapture(key, refName)
        {
            let state = this.productLeadCaptureFormState(key);
            if (!state || !state.payload || !state.payload.submit_url)
            {
                return;
            }

            if (!this.productLeadCaptureCanSubmit(key))
            {
                return;
            }

            let formEl = this.$refs[refName];
            if (Array.isArray(formEl))
            {
                formEl = formEl[0];
            }

            if (!formEl || typeof FormData === 'undefined')
            {
                this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                return;
            }

            state.submitting = true;
            state.errors = {};

            let formData = new FormData(formEl);
            formData.set('name', state.name || '');
            formData.set('email', state.email || '');

            try
            {
                let res = await this.axios.post(state.payload.submit_url, formData, {
                    headers: {
                        'Accept': 'application/json',
                    },
                });

                state.submitting = false;

                if (res && res.data && res.data.redirect_url)
                {
                    window.location.assign(res.data.redirect_url);
                    return;
                }

                if (res && res.data && res.data.download_url)
                {
                    if (this.productLeadCaptureModalOpen)
                    {
                        this.closeProductLeadCaptureModal();
                    }

                    this.productLeadCaptureHandleDownload(res.data.download_url, res.data.download_name || '');
                    return;
                }

                if (this.productLeadCaptureModalOpen)
                {
                    this.closeProductLeadCaptureModal();
                }

                this.openModalOk('Success', (res && res.data && res.data.message) ? res.data.message : 'Thank you, your request has been received.', 'Close');
            }
            catch (error)
            {
                state.submitting = false;

                if (error && error.response && error.response.status === 422 && error.response.data && error.response.data.errors)
                {
                    state.errors = error.response.data.errors;
                    if (state.errors['cf-turnstile-response'] && window.PonoTurnstileWidgets)
                    {
                        window.PonoTurnstileWidgets.reject(formEl, () => {
                            delete state.errors['cf-turnstile-response'];
                        });
                    }
                    return;
                }

                let message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'There was a connection problem and the process could not be completed. Please try again.';

                this.openModalWarning('ERROR', message);
            }
        },

        ...websiteLeadCapturePresentationMethods,

        websiteLeadCaptureShell()
        {
            return document.querySelector('[data-website-lead-capture-shell="true"]');
        },

        websiteLeadCaptureInit()
        {
            let shell = this.websiteLeadCaptureShell();
            if (!shell)
            {
                return;
            }

            let raw = shell.getAttribute('data-website-lead-capture-payload') || '';
            if (!raw)
            {
                return;
            }

            try
            {
                this.websiteLeadCaptureBoot = JSON.parse(raw);
            }
            catch (e)
            {
                this.websiteLeadCaptureBoot = null;
            }

            this.websiteLeadCaptureWatchViewport();
            this.websiteLeadCaptureScheduleNext();
        },

        websiteLeadCaptureNormalizePayload(payload={})
        {
            let normalized = payload && typeof payload === 'object' ? payload : {};

            return {
                ...normalizeLeadCapturePresentation(normalized),
                action_type: normalized.action_type === 'open_link' ? 'open_link' : 'signup',
                action_url: normalized.action_url || '',
                secondary_link_url: normalized.secondary_link_url || '',
                secondary_link_label: normalized.secondary_link_label || '',
                once_per_session: normalized.once_per_session !== false && normalized.once_per_session !== 0 && normalized.once_per_session !== '0',
                dismiss_days: normalized.dismiss_days == null ? 7 : parseInt(normalized.dismiss_days, 10),
                click_days: normalized.click_days == null ? 7 : parseInt(normalized.click_days, 10),
                id: normalized.id ? parseInt(normalized.id, 10) : 0,
                version: normalized.version || '',
                submit_url: normalized.submit_url || '',
                event_url: normalized.event_url || '',
                path: normalized.path || '',
                page_id: normalized.page_id || '',
                context_token: normalized.context_token || '',
                delay_seconds: normalized.delay_seconds === null || typeof normalized.delay_seconds === 'undefined' ? null : parseInt(normalized.delay_seconds, 10),
                scroll_percent: normalized.scroll_percent === null || typeof normalized.scroll_percent === 'undefined' ? null : parseInt(normalized.scroll_percent, 10),
                collect_last_name: !!normalized.collect_last_name,
                show_captcha: !!normalized.show_captcha,
                first_name_label: normalized.first_name_label || 'First name',
                last_name_label: normalized.last_name_label || 'Last name',
                email_label: normalized.email_label || 'Email address',
                eyebrow: normalized.eyebrow || '',
                title: normalized.title || 'Join the list',
                body: normalized.body || '',
                bullets: Array.isArray(normalized.bullets) ? normalized.bullets : [],
                button_label: normalized.button_label || 'Submit',
                fine_print: normalized.fine_print || '',
                dismiss_text: normalized.dismiss_text || 'No thanks, I will keep browsing.',
                success_message: normalized.success_message || 'Thank you. Your request has been received.',
                success_popup_content: '',
                image_url: normalized.image_url || '',
                dismissal_reset_at: normalized.dismissal_reset_at ? parseInt(normalized.dismissal_reset_at, 10) : 0,
                submission_reset_at: normalized.submission_reset_at ? parseInt(normalized.submission_reset_at, 10) : 0,
                submission_reset_tokens: normalized.submission_reset_tokens && typeof normalized.submission_reset_tokens === 'object' ? normalized.submission_reset_tokens : {},
                preview: !!normalized.preview,
                colors: normalized.colors && typeof normalized.colors === 'object' ? normalized.colors : {},
            };
        },

        websiteLeadCaptureFormState(payload=null)
        {
            let normalizedPayload = payload ? this.websiteLeadCaptureNormalizePayload(payload) : null;
            let key = normalizedPayload
                ? 'website-' + (normalizedPayload.id || 'popup')
                : this.websiteLeadCaptureActiveKey;

            if (!key)
            {
                return null;
            }

            let current = this.websiteLeadCaptureForms[key];

            if (!current)
            {
                current = {
                    payload: normalizedPayload || this.websiteLeadCaptureNormalizePayload(),
                    first_name: '',
                    last_name: '',
                    email: '',
                    errors: {},
                    submitting: false,
                };

                this.websiteLeadCaptureForms[key] = current;
            }
            else if (normalizedPayload)
            {
                current.payload = normalizedPayload;
            }

            return current;
        },

        websiteLeadCaptureActiveState()
        {
            return this.websiteLeadCaptureActiveKey
                ? this.websiteLeadCaptureFormState()
                : null;
        },

        websiteLeadCaptureActiveColors()
        {
            let state = this.websiteLeadCaptureActiveState();
            let colors = state && state.payload && state.payload.colors ? state.payload.colors : {};

            return Object.assign({
                overlay_background: 'rgba(15, 23, 42, 0.72)',
                background: '#0f172a',
                panel_background: '#0b0f19',
                image_background: '#111827',
                eyebrow: '#facc15',
                title: '#f8fafc',
                body: '#d1d5db',
                bullet: '#f8fafc',
                bullet_icon: '#fde047',
                input_background: '#111827',
                input_text: '#f8fafc',
                input_border: '#475569',
                button_background: '#facc15',
                button_text: '#111827',
                fine_print: '#94a3b8',
                dismiss_text: '#d6bc86',
            }, colors);
        },

        websiteLeadCaptureStorageKey(payload)
        {
            let normalized = this.websiteLeadCaptureNormalizePayload(payload);

            return normalized.action_type === 'open_link'
                ? 'ponoWebsiteLinkPopup:' + normalized.id
                : 'ponoWebsiteLeadCapture:' + normalized.id + ':' + normalized.version;
        },

        websiteLeadCaptureReadSuppression(payload)
        {
            try
            {
                let raw = localStorage.getItem(this.websiteLeadCaptureStorageKey(payload));
                return this.websiteLeadCaptureApplySubmissionReset(
                    payload,
                    this.websiteLeadCaptureApplyDismissalReset(payload, raw ? JSON.parse(raw) : {})
                );
            }
            catch (e)
            {
                return {};
            }
        },

        websiteLeadCaptureApplyDismissalReset(payload, data={})
        {
            if (!data || typeof data !== 'object')
            {
                return {};
            }

            if (data.submitted)
            {
                return data;
            }

            let normalized = this.websiteLeadCaptureNormalizePayload(payload);
            let resetAt = parseInt(normalized.dismissal_reset_at || 0, 10);
            let dismissedAt = parseInt(data.dismissed_at || 0, 10);

            if (resetAt > 0 && dismissedAt > 0 && dismissedAt <= resetAt)
            {
                try
                {
                    localStorage.removeItem(this.websiteLeadCaptureStorageKey(payload));
                }
                catch (e) {}

                return {};
            }

            return data;
        },

        websiteLeadCaptureApplySubmissionReset(payload, data={})
        {
            if (!data || typeof data !== 'object')
            {
                return {};
            }

            if (!data.submitted)
            {
                return data;
            }

            let normalized = this.websiteLeadCaptureNormalizePayload(payload);
            let resetAt = parseInt(normalized.submission_reset_at || 0, 10);
            let submissionResetToken = data.submission_reset_token || '';
            if (submissionResetToken && normalized.submission_reset_tokens)
            {
                resetAt = Math.max(resetAt, parseInt(normalized.submission_reset_tokens[submissionResetToken] || 0, 10));
            }
            let submittedAt = parseInt(data.submitted_at || 0, 10);

            if (resetAt > 0 && submittedAt > 0 && submittedAt <= resetAt)
            {
                try
                {
                    localStorage.removeItem(this.websiteLeadCaptureStorageKey(payload));
                }
                catch (e) {}

                return {};
            }

            return data;
        },

        websiteLeadCaptureWriteSuppression(payload, data)
        {
            try
            {
                localStorage.setItem(this.websiteLeadCaptureStorageKey(payload), JSON.stringify(data || {}));
            }
            catch (e) {}
        },

        websiteLeadCaptureIsSuppressed(payload)
        {
            if (this.websiteLeadCaptureNormalizePayload(payload).preview)
            {
                return false;
            }

            const normalized = this.websiteLeadCaptureNormalizePayload(payload);
            if (normalized.action_type === 'open_link' && normalized.once_per_session && this.websiteLeadCaptureSessionSeen(normalized)) return true;
            let data = this.websiteLeadCaptureReadSuppression(payload);
            if (normalized.action_type === 'open_link' && data.clicked_until > Date.now()) return true;
            if (data && data.submitted)
            {
                return true;
            }

            if (data && data.dismissed_until && parseInt(data.dismissed_until, 10) > Date.now())
            {
                return true;
            }

            return false;
        },

        websiteLeadCaptureMarkDismissed(payload)
        {
            let days = this.websiteLeadCaptureBoot && this.websiteLeadCaptureBoot.dismiss_days
                ? parseInt(this.websiteLeadCaptureBoot.dismiss_days, 10)
                : 30;
            const normalized = this.websiteLeadCaptureNormalizePayload(payload);
            if (normalized.action_type === 'open_link') days = normalized.dismiss_days;
            let now = Date.now();
            let data = {
                dismissed_at: now,
                dismissed_until: now + (Math.max(0, days) * 24 * 60 * 60 * 1000),
                popup_version: this.websiteLeadCaptureNormalizePayload(payload).version || '',
            };

            this.websiteLeadCaptureWriteSuppression(payload, data);

            return data;
        },

        websiteLeadCaptureMarkSubmitted(payload, responseData={})
        {
            let data = responseData && typeof responseData === 'object' ? responseData : {};
            this.websiteLeadCaptureWriteSuppression(payload, {
                submitted: true,
                submitted_at: Date.now(),
                submission_reset_token: data.submission_reset_token || '',
            });
        },

        websiteLeadCaptureNextPayload()
        {
            if (!this.websiteLeadCaptureBoot || !Array.isArray(this.websiteLeadCaptureBoot.popups))
            {
                return null;
            }

            return this.websiteLeadCaptureBoot.popups.find((payload) => {
                return payload
                    && !this.websiteLeadCaptureIsManualPayload(payload)
                    && !this.websiteLeadCaptureHasInlineForm(payload)
                    && !this.websiteLeadCaptureIsSuppressed(payload);
            }) || null;
        },

        websiteLeadCaptureManualTriggerId(trigger)
        {
            if (!trigger || typeof trigger.getAttribute !== 'function')
            {
                return 0;
            }

            let id = parseInt(trigger.getAttribute('data-website-lead-capture-trigger') || '', 10);

            return Number.isFinite(id) && id > 0 ? id : 0;
        },

        websiteLeadCapturePayloadById(id)
        {
            id = parseInt(id || 0, 10);
            if (!id || !this.websiteLeadCaptureBoot || !Array.isArray(this.websiteLeadCaptureBoot.popups))
            {
                return null;
            }

            return this.websiteLeadCaptureBoot.popups.find((payload) => {
                return payload && parseInt(payload.id || 0, 10) === id;
            }) || null;
        },

        websiteLeadCaptureIsManualPayload(payload)
        {
            if (typeof document === 'undefined')
            {
                return false;
            }

            let id = parseInt(payload && payload.id ? payload.id : 0, 10);
            if (!id)
            {
                return false;
            }

            return Array.from(document.querySelectorAll('[data-website-lead-capture-trigger]')).some((trigger) => {
                return this.websiteLeadCaptureManualTriggerId(trigger) === id;
            });
        },

        handleWebsiteLeadCaptureTriggerClick(event)
        {
            let target = event && event.target ? event.target : null;
            let trigger = target && typeof target.closest === 'function'
                ? target.closest('[data-website-lead-capture-trigger]')
                : null;
            let payload = this.websiteLeadCapturePayloadById(this.websiteLeadCaptureManualTriggerId(trigger));

            if (!trigger || !payload)
            {
                return false;
            }

            event.preventDefault();
            event.stopPropagation();
            this.openWebsiteLeadCapture(payload, true);

            return true;
        },

        websiteLeadCaptureScheduleNext()
        {
            if (this.websiteLeadCapturePageViewDone || this.websiteLeadCapturePopupOpen || this.websiteLeadCaptureSuccessPopupOpen)
            {
                return;
            }

            let payload = this.websiteLeadCaptureNextPayload();
            if (!payload)
            {
                return;
            }

            this.websiteLeadCaptureCleanupTriggers();

            let normalized = this.websiteLeadCaptureNormalizePayload(payload);
            let delay = Number.isFinite(normalized.delay_seconds) && normalized.delay_seconds !== null
                ? Math.max(0, normalized.delay_seconds)
                : null;
            let scroll = Number.isFinite(normalized.scroll_percent) && normalized.scroll_percent !== null
                ? Math.max(0, Math.min(100, normalized.scroll_percent))
                : null;

            if (normalized.preview)
            {
                delay = 0;
                scroll = null;
            }

            if (delay === null && scroll === null)
            {
                delay = 0;
            }

            if (delay !== null)
            {
                this.websiteLeadCaptureDelayTimer = window.setTimeout(() => {
                    this.openWebsiteLeadCapture(payload);
                }, delay * 1000);
            }

            if (scroll !== null)
            {
                this.websiteLeadCaptureScrollHandler = () => {
                    let doc = document.documentElement;
                    let body = document.body;
                    let scrollTop = window.pageYOffset || doc.scrollTop || body.scrollTop || 0;
                    let scrollHeight = Math.max(
                        body ? body.scrollHeight : 0,
                        doc ? doc.scrollHeight : 0,
                        body ? body.offsetHeight : 0,
                        doc ? doc.offsetHeight : 0,
                        body ? body.clientHeight : 0,
                        doc ? doc.clientHeight : 0
                    );
                    let viewport = window.innerHeight || (doc ? doc.clientHeight : 0) || 0;
                    let denominator = Math.max(1, scrollHeight - viewport);
                    let percent = Math.round((scrollTop / denominator) * 100);

                    if (percent >= scroll)
                    {
                        this.openWebsiteLeadCapture(payload);
                    }
                };

                window.addEventListener('scroll', this.websiteLeadCaptureScrollHandler, { passive: true });
                this.websiteLeadCaptureScrollHandler();
            }
        },

        websiteLeadCaptureCleanupTriggers()
        {
            if (this.websiteLeadCaptureDelayTimer)
            {
                window.clearTimeout(this.websiteLeadCaptureDelayTimer);
                this.websiteLeadCaptureDelayTimer = null;
            }

            if (this.websiteLeadCaptureScrollHandler)
            {
                window.removeEventListener('scroll', this.websiteLeadCaptureScrollHandler);
                this.websiteLeadCaptureScrollHandler = null;
            }
        },

        openWebsiteLeadCapture(payload, manual=false)
        {
            if (this.websiteLeadCapturePopupOpen
                || this.websiteLeadCaptureSuccessPopupOpen
                || (!manual && (this.websiteLeadCapturePageViewDone || this.websiteLeadCaptureIsSuppressed(payload))))
            {
                return;
            }

            let normalizedPayload = this.websiteLeadCaptureNormalizePayload(payload);
            if (!manual && normalizedPayload.action_type === 'open_link' && this.websiteLeadCaptureBlockedByInteraction()) {
                this.websiteLeadCaptureDelayTimer = window.setTimeout(() => this.openWebsiteLeadCapture(payload), 1000);
                return;
            }
            let key = 'website-' + (normalizedPayload.id || 'popup');
            let state = this.websiteLeadCaptureFormState(normalizedPayload);

            if (state)
            {
                state.errors = {};
            }

            this.websiteLeadCaptureCleanupTriggers();
            this.websiteLeadCaptureActiveKey = key;
            this._websiteLeadCapturePreviousFocus = document.activeElement;
            this.websiteLeadCaptureExpanded = !!manual;
            this.websiteLeadCapturePopupOpen = true;
            if (normalizedPayload.action_type === 'open_link' && !normalizedPayload.preview) this.websiteLeadCaptureMarkSessionSeen(normalizedPayload);
            this.websiteLeadCapturePostEvent('impression');
            this.websiteLeadCaptureSyncPresentation(true);
        },

        websiteLeadCaptureInitPublicForm(root = null)
        {
            if (typeof window !== 'undefined' && window.PonoHoneypot && typeof window.PonoHoneypot.init === 'function')
            {
                window.PonoHoneypot.init(root || document);
            }

            if (typeof window !== 'undefined' && window.PonoTurnstile && typeof window.PonoTurnstile.init === 'function')
            {
                window.PonoTurnstile.init(root || document);
            }
        },

        closeWebsiteLeadCapture()
        {
            const restoreFocus = this.$refs.websiteLeadCapturePresentation?.contains(document.activeElement);
            this.websiteLeadCapturePopupOpen = false;
            this.websiteLeadCaptureActiveKey = null;
            this.websiteLeadCaptureExpanded = false;
            this.websiteLeadCaptureSetScrollLock(this.websiteLeadCaptureSuccessPopupOpen);
            if (restoreFocus) this.$nextTick(() => {
                if (!this.websiteLeadCaptureSuccessPopupOpen && this._websiteLeadCapturePreviousFocus?.isConnected) this._websiteLeadCapturePreviousFocus.focus({preventScroll: true});
            });
        },

        websiteLeadCaptureShouldShowSuccessPopup(payload, responseData={})
        {
            let normalizedPayload = this.websiteLeadCaptureNormalizePayload(payload);

            return !!normalizedPayload.success_message;
        },

        openWebsiteLeadCaptureSuccessPopup(payload, responseData={})
        {
            let normalizedPayload = this.websiteLeadCaptureNormalizePayload(payload);
            let data = responseData && typeof responseData === 'object' ? responseData : {};

            this.websiteLeadCaptureSuccessState = {
                payload: normalizedPayload,
                content: data.message || normalizedPayload.success_message || 'Thank you. Your request has been received.',
                message: data.message || normalizedPayload.success_message || 'Thank you. Your request has been received.',
                download_url: data.download_url || '',
                download_name: data.download_name || '',
            };
            this.websiteLeadCaptureSuccessPopupOpen = true;
            this.websiteLeadCaptureSetScrollLock(true);
        },

        closeWebsiteLeadCaptureSuccessPopup()
        {
            this.websiteLeadCaptureSuccessPopupOpen = false;
            this.websiteLeadCaptureSuccessState = null;

            if (!this.websiteLeadCapturePopupOpen)
            {
                this.websiteLeadCaptureSetScrollLock(false);
            }
        },

        websiteLeadCaptureDownloadSuccessFile()
        {
            if (!this.websiteLeadCaptureSuccessState || !this.websiteLeadCaptureSuccessState.download_url)
            {
                return;
            }

            this.productLeadCaptureHandleDownload(
                this.websiteLeadCaptureSuccessState.download_url,
                this.websiteLeadCaptureSuccessState.download_name || ''
            );
        },

        dismissWebsiteLeadCapture()
        {
            let state = this.websiteLeadCaptureActiveState();
            if (state && state.payload)
            {
                let suppression = this.websiteLeadCaptureMarkDismissed(state.payload);
                this.websiteLeadCapturePostEvent('dismiss', {
                    dismissed_at: suppression.dismissed_at,
                    dismissed_until: suppression.dismissed_until,
                    popup_version: suppression.popup_version,
                });
            }

            this.websiteLeadCapturePageViewDone = true;
            this.closeWebsiteLeadCapture();
        },

        websiteLeadCaptureFirstError(field)
        {
            let state = this.websiteLeadCaptureActiveState();
            if (!state || !state.errors || !state.errors[field] || !state.errors[field].length)
            {
                return '';
            }

            return state.errors[field][0];
        },

        websiteLeadCaptureCanSubmit()
        {
            let state = this.websiteLeadCaptureActiveState();
            if (!state || state.submitting)
            {
                return false;
            }

            return this.productLeadCaptureTrimmedValue(state.first_name) !== ''
                && this.productLeadCaptureTrimmedValue(state.email) !== '';
        },

        websiteLeadCapturePostEvent(eventType, metadata={})
        {
            let state = this.websiteLeadCaptureActiveState();
            if (!state || !state.payload || !state.payload.event_url || state.payload.preview)
            {
                return;
            }

            try
            {
                const data = {
                    event_type: eventType,
                    path: state.payload.path || window.location.pathname.replace(/^\/+|\/+$/g, ''),
                    page_id: state.payload.page_id || null,
                    context_token: state.payload.context_token || '',
                    metadata: metadata || {},
                };
                if ((eventType === 'link_click' || eventType === 'secondary_link_click') && typeof window.fetch === 'function') {
                    window.fetch(state.payload.event_url, {
                        method: 'POST', credentials: 'same-origin', keepalive: true,
                        headers: {'Accept': 'application/json', 'Content-Type': 'application/json', 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || ''},
                        body: JSON.stringify(data),
                    }).catch(() => {});
                    return;
                }
                this.axios.post(state.payload.event_url, data, {
                    headers: {
                        'Accept': 'application/json',
                    },
                }).catch(() => {});
            }
            catch (e) {}
        },

        async submitWebsiteLeadCapture()
        {
            let state = this.websiteLeadCaptureActiveState();
            if (!state || !state.payload || !state.payload.submit_url || !this.websiteLeadCaptureCanSubmit())
            {
                return;
            }

            let formEl = this.$refs.websiteLeadCaptureForm;
            if (Array.isArray(formEl))
            {
                formEl = formEl[0];
            }

            if (!formEl || typeof FormData === 'undefined')
            {
                this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                return;
            }

            state.submitting = true;
            state.errors = {};

            let formData = new FormData(formEl);
            formData.set('first_name', state.first_name || '');
            formData.set('last_name', state.last_name || '');
            formData.set('email', state.email || '');
            formData.set('path', state.payload.path || window.location.pathname.replace(/^\/+|\/+$/g, ''));
            formData.set('context_token', state.payload.context_token || '');
            if (state.payload.page_id)
            {
                formData.set('page_id', state.payload.page_id);
            }

            try
            {
                let res = await this.axios.post(state.payload.submit_url, formData, {
                    headers: {
                        'Accept': 'application/json',
                    },
                });

                let responseData = res && res.data ? res.data : {};
                let showSuccessPopup = this.websiteLeadCaptureShouldShowSuccessPopup(state.payload, responseData);
                let shouldDownloadImmediately = !!responseData.download_url
                    && this.websiteLeadCaptureIsManualPayload(state.payload);

                state.submitting = false;
                window.PonoWebsiteLeadCapture.trackSuccess(responseData);
                this.websiteLeadCaptureMarkSubmitted(state.payload, responseData);
                this.websiteLeadCapturePageViewDone = true;
                this.closeWebsiteLeadCapture();

                if (shouldDownloadImmediately)
                {
                    this.productLeadCaptureHandleDownload(
                        responseData.download_url,
                        responseData.download_name || ''
                    );
                }

                if (showSuccessPopup)
                {
                    this.openWebsiteLeadCaptureSuccessPopup(state.payload, responseData);
                    return;
                }

                if (responseData.message)
                {
                    this.openModalOk('Success', responseData.message, 'Close');
                }
            }
            catch (error)
            {
                state.submitting = false;

                if (error && error.response && error.response.status === 422 && error.response.data && error.response.data.errors)
                {
                    state.errors = error.response.data.errors;
                    if (state.errors['cf-turnstile-response'] && window.PonoTurnstileWidgets)
                    {
                        window.PonoTurnstileWidgets.reject(formEl, () => {
                            delete state.errors['cf-turnstile-response'];
                        });
                    }
                    return;
                }

                this.openModalWarning('ERROR', this.websiteLeadCaptureSubmissionErrorMessage(error));
            }
        },

        websiteLeadCaptureSubmissionErrorMessage(error)
        {
            if (error && error.response && error.response.status === 429)
            {
                return 'Please wait a moment and try again.';
            }

            return error && error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : 'There was a connection problem and the process could not be completed. Please try again.';
        },

        websiteLeadCaptureInlineFormState(key, payload=null)
        {
            key = String(key || '');
            if (!key)
            {
                return null;
            }

            let normalizedPayload = payload ? this.websiteLeadCaptureNormalizePayload(payload) : null;
            let current = this.websiteLeadCaptureForms[key];

            if (!current)
            {
                current = {
                    payload: normalizedPayload || this.websiteLeadCaptureNormalizePayload(),
                    first_name: '',
                    last_name: '',
                    email: '',
                    errors: {},
                    submitting: false,
                    completed: false,
                    success_message: normalizedPayload ? normalizedPayload.success_message : '',
                    download_url: '',
                    download_name: '',
                };

                this.websiteLeadCaptureForms[key] = current;
            }
            else if (normalizedPayload)
            {
                current.payload = normalizedPayload;
                if (!current.success_message)
                {
                    current.success_message = normalizedPayload.success_message;
                }
            }

            return current;
        },

        websiteLeadCaptureInlineFirstError(key, field)
        {
            let state = this.websiteLeadCaptureInlineFormState(key);
            if (!state || !state.errors || !state.errors[field] || !state.errors[field].length)
            {
                return '';
            }

            return state.errors[field][0];
        },

        websiteLeadCaptureInlineCanSubmit(key)
        {
            let state = this.websiteLeadCaptureInlineFormState(key);
            if (!state || state.submitting || state.completed)
            {
                return false;
            }

            return this.productLeadCaptureTrimmedValue(state.first_name) !== ''
                && this.productLeadCaptureTrimmedValue(state.email) !== '';
        },

        websiteLeadCaptureInitInlineForms(root=null)
        {
            this.$nextTick(() => {
                let scope = root || document;
                this.websiteLeadCaptureInitPublicForm(scope);
            });
        },

        async submitWebsiteLeadCaptureInline(key, refName)
        {
            let state = this.websiteLeadCaptureInlineFormState(key);
            if (!state || !state.payload || !state.payload.submit_url || !this.websiteLeadCaptureInlineCanSubmit(key))
            {
                return;
            }

            let formEl = this.$refs[refName];
            if (Array.isArray(formEl))
            {
                formEl = formEl[0];
            }

            if (!formEl || typeof FormData === 'undefined')
            {
                this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                return;
            }

            state.submitting = true;
            state.errors = {};

            let formData = new FormData(formEl);
            formData.set('first_name', state.first_name || '');
            formData.set('last_name', state.last_name || '');
            formData.set('email', state.email || '');
            formData.set('path', state.payload.path || window.location.pathname.replace(/^\/+|\/+$/g, ''));
            formData.set('context_token', state.payload.context_token || '');
            if (state.payload.page_id)
            {
                formData.set('page_id', state.payload.page_id);
            }

            try
            {
                let res = await this.axios.post(state.payload.submit_url, formData, {
                    headers: {
                        'Accept': 'application/json',
                    },
                });

                let responseData = res && res.data ? res.data : {};

                state.submitting = false;
                state.completed = true;
                window.PonoWebsiteLeadCapture.trackSuccess(responseData);
                state.success_message = responseData.message || state.payload.success_message || 'Thank you. Your request has been received.';
                state.download_url = responseData.download_url || '';
                state.download_name = responseData.download_name || '';
                state.errors = {};
            }
            catch (error)
            {
                state.submitting = false;

                if (error && error.response && error.response.status === 422 && error.response.data && error.response.data.errors)
                {
                    state.errors = error.response.data.errors;
                    if (state.errors['cf-turnstile-response'] && window.PonoTurnstileWidgets)
                    {
                        window.PonoTurnstileWidgets.reject(formEl, () => {
                            delete state.errors['cf-turnstile-response'];
                        });
                    }
                    this.websiteLeadCaptureInitInlineForms(formEl);
                    return;
                }

                this.openModalWarning('ERROR', this.websiteLeadCaptureSubmissionErrorMessage(error));
            }
        },

        // show page overlay
        showOverlay(event=null)
        {
            // Direct template calls often omit $event; use only the event currently being dispatched.
            const trigger = event || (typeof window !== 'undefined' ? window.event : null);

            if (
                trigger
                && (trigger.type === 'click' || trigger.type === 'auxclick')
                && this.account2026ModifiedNavigationEvent(trigger)
                && this.admin2026ChromeRoot(trigger.target)
            )
            {
                return;
            }

            this.overlayOn = true;
        },

        showNavigationOverlay(event=null)
        {
            if (
                this.account2026NavigationClickShouldShowOverlay(event)
                || this.admin2026NavigationClickShouldShowOverlay(event)
            )
            {
                this.showOverlay();
            }
        },

        // hide page overlay
        hideOverlay()
        {
            this.overlayOn = false;
        },

        account2026AppearanceBoot()
        {
            if (typeof window === 'undefined' || !window.vAccount2026Appearance || !window.vAccount2026Appearance.enabled)
            {
                return null;
            }

            return window.vAccount2026Appearance;
        },

        account2026NormalizeAppearance(value)
        {
            return value === 'dark' || value === 'light' ? value : '';
        },

        account2026AppearanceStorageKey()
        {
            const boot = this.account2026AppearanceBoot();

            return boot && boot.storageKey ? boot.storageKey : 'ponoAccount2026Appearance';
        },

        account2026StoredAppearance()
        {
            try
            {
                if (!localStorage)
                {
                    return '';
                }

                return this.account2026NormalizeAppearance(localStorage.getItem(this.account2026AppearanceStorageKey()));
            }
            catch (e) {}

            return '';
        },

        account2026RememberAppearance(appearance)
        {
            const nextAppearance = this.account2026NormalizeAppearance(appearance);

            if (!nextAppearance)
            {
                return;
            }

            try
            {
                if (localStorage)
                {
                    localStorage.setItem(this.account2026AppearanceStorageKey(), nextAppearance);
                }
            }
            catch (e) {}
        },

        account2026DefaultAppearance()
        {
            return 'light';
        },

        account2026SystemAppearance()
        {
            return this.account2026DefaultAppearance();
        },

        account2026ResolvedAppearance(preference=null)
        {
            const storedAppearance = this.account2026StoredAppearance();
            if (storedAppearance)
            {
                return storedAppearance;
            }

            const storedPreference = preference === null ? this.account2026AppearancePreference : preference;
            const normalized = this.account2026NormalizeAppearance(storedPreference);

            return normalized || this.account2026DefaultAppearance();
        },

        account2026SetAppearance(appearance)
        {
            const nextAppearance = this.account2026NormalizeAppearance(appearance) || this.account2026DefaultAppearance();
            this.account2026AppearancePreference = nextAppearance;
            this.account2026RememberAppearance(nextAppearance);
            this.account2026ApplyAppearance(nextAppearance);
        },

        account2026ApplyAppearance(appearance)
        {
            const nextAppearance = this.account2026NormalizeAppearance(appearance) || 'light';
            this.account2026Appearance = nextAppearance;

            try
            {
                document.documentElement.classList.toggle('dark', nextAppearance === 'dark');
                document.documentElement.setAttribute('data-account2026-appearance', nextAppearance);

                if (document.body)
                {
                    document.body.setAttribute('data-account2026-appearance', nextAppearance);
                }
            }
            catch (e) {}

            this.account2026SyncFrameDrawerAppearance(nextAppearance);
        },

        account2026SyncFrameDrawerAppearance(appearance)
        {
            const nextAppearance = this.account2026NormalizeAppearance(appearance) || 'light';

            try
            {
                const frames = document.querySelectorAll('[data-account2026-frame-drawer-iframe="true"]');

                frames.forEach((frame) => {
                    try
                    {
                        const frameDocument = frame.contentDocument || (frame.contentWindow ? frame.contentWindow.document : null);

                        if (!frameDocument || !frameDocument.documentElement)
                        {
                            return;
                        }

                        frameDocument.documentElement.classList.toggle('dark', nextAppearance === 'dark');
                        frameDocument.documentElement.setAttribute('data-account2026-appearance', nextAppearance);

                        if (frameDocument.body)
                        {
                            frameDocument.body.setAttribute('data-account2026-appearance', nextAppearance);
                        }
                    }
                    catch (e) {}
                });
            }
            catch (e) {}
        },

        account2026HandleSystemAppearanceChange()
        {
            this.account2026ApplyAppearance(this.account2026ResolvedAppearance(''));
        },

        account2026InstallSystemAppearanceListener()
        {
            return;
        },

        account2026RemoveSystemAppearanceListener()
        {
            const mediaQuery = this._account2026AppearanceMediaQuery || null;
            const handler = this._account2026AppearanceMediaQueryHandler || null;

            if (!mediaQuery || !handler)
            {
                return;
            }

            try
            {
                if (typeof mediaQuery.removeEventListener === 'function')
                {
                    mediaQuery.removeEventListener('change', handler);
                }
                else if (typeof mediaQuery.removeListener === 'function')
                {
                    mediaQuery.removeListener(handler);
                }
            }
            catch (e) {}

            this._account2026AppearanceMediaQuery = null;
            this._account2026AppearanceMediaQueryHandler = null;
        },

        account2026InitializeAppearance()
        {
            const boot = this.account2026AppearanceBoot();

            if (!boot)
            {
                return;
            }

            this.account2026AppearancePreference = this.account2026StoredAppearance() || this.account2026NormalizeAppearance(boot.preference);
            this.account2026ApplyAppearance(this.account2026ResolvedAppearance(this.account2026AppearancePreference));
            this.account2026InstallSystemAppearanceListener();
        },

        account2026EventElement(target)
        {
            if (!target)
            {
                return null;
            }

            if (target.nodeType === 1)
            {
                return target;
            }

            return target.parentElement || null;
        },

        account2026ClientDashboardRoot(target=null)
        {
            try
            {
                const selector = '.account2026-dashboard, .account2026-hybrid';
                const element = this.account2026EventElement(target);

                if (element && typeof element.closest === 'function')
                {
                    return element.closest(selector);
                }

                if (typeof document !== 'undefined')
                {
                    return document.querySelector(selector);
                }
            }
            catch (e) {}

            return null;
        },

        account2026ModifiedNavigationEvent(event)
        {
            return !!(
                event
                && (
                    event.metaKey
                    || event.ctrlKey
                    || event.shiftKey
                    || event.altKey
                    || (typeof event.button === 'number' && event.button !== 0)
                )
            );
        },

        account2026ElementOpensInSameTab(element)
        {
            if (!element || typeof element.getAttribute !== 'function')
            {
                return true;
            }

            const target = (element.getAttribute('target') || '').trim().toLowerCase();

            if (target && target !== '_self')
            {
                return false;
            }

            if (typeof element.hasAttribute === 'function' && element.hasAttribute('download'))
            {
                return false;
            }

            return true;
        },

        account2026UrlLoadsNewDocument(rawUrl)
        {
            if (!rawUrl)
            {
                return false;
            }

            const value = rawUrl.toString().trim();

            if (!value || value === '#' || value.startsWith('#'))
            {
                return false;
            }

            const lowered = value.toLowerCase();

            if (
                lowered.startsWith('javascript:')
                || lowered.startsWith('mailto:')
                || lowered.startsWith('tel:')
            )
            {
                return false;
            }

            try
            {
                const nextUrl = new URL(value, window.location.href);
                const currentUrl = new URL(window.location.href);

                if (
                    nextUrl.origin === currentUrl.origin
                    && nextUrl.pathname === currentUrl.pathname
                    && nextUrl.search === currentUrl.search
                    && nextUrl.hash
                    && nextUrl.hash !== currentUrl.hash
                )
                {
                    return false;
                }

                return nextUrl.href !== currentUrl.href;
            }
            catch (e)
            {
                return true;
            }
        },

        account2026NavigationClickShouldShowOverlay(event)
        {
            if (!event || event.defaultPrevented || this.account2026ModifiedNavigationEvent(event))
            {
                return false;
            }

            const target = this.account2026EventElement(event.target);

            if (!this.account2026ClientDashboardRoot(target))
            {
                return false;
            }

            const link = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;

            if (!link || !this.account2026ClientDashboardRoot(link))
            {
                return false;
            }

            if (typeof link.hasAttribute === 'function' && link.hasAttribute('data-account2026-no-overlay'))
            {
                return false;
            }

            if (!this.account2026ElementOpensInSameTab(link))
            {
                return false;
            }

            return this.account2026UrlLoadsNewDocument(link.getAttribute('href'));
        },

        account2026NavigationSubmitShouldShowOverlay(event)
        {
            if (!event || event.defaultPrevented)
            {
                return false;
            }

            const form = event.target && event.target.matches && event.target.matches('form') ? event.target : null;

            if (!form || !this.account2026ClientDashboardRoot(form))
            {
                return false;
            }

            if (typeof form.hasAttribute === 'function' && form.hasAttribute('data-account2026-no-overlay'))
            {
                return false;
            }

            if (!this.account2026ElementOpensInSameTab(form))
            {
                return false;
            }

            const submitter = event.submitter || null;

            if (submitter && !this.account2026ElementOpensInSameTab(submitter))
            {
                return false;
            }

            if (typeof form.checkValidity === 'function' && !form.checkValidity())
            {
                return false;
            }

            return true;
        },

        account2026ManualNavigationShouldShowOverlay(event, link, action=0)
        {
            if (
                !link
                || !event
                || this.account2026ModifiedNavigationEvent(event)
                || !this.account2026ClientDashboardRoot(event.target)
                || action === 2
                || action === 3
            )
            {
                return false;
            }

            try
            {
                const nextUrl = new URL(link, window.location.href);

                if (nextUrl.origin !== window.location.origin && action !== 1)
                {
                    return false;
                }
            }
            catch (e) {}

            return this.account2026UrlLoadsNewDocument(link);
        },

        admin2026ChromeRoot(target=null)
        {
            try
            {
                if (typeof window === 'undefined' || !window.vAdmin2026Chrome)
                {
                    return null;
                }

                const selector = '.admin2026-shell';
                const element = this.account2026EventElement(target);

                if (element && typeof element.closest === 'function')
                {
                    return element.closest(selector);
                }

                if (typeof document !== 'undefined')
                {
                    return document.querySelector(selector);
                }
            }
            catch (e) {}

            return null;
        },

        admin2026NavigationClickShouldShowOverlay(event)
        {
            if (!event || event.defaultPrevented || this.account2026ModifiedNavigationEvent(event))
            {
                return false;
            }

            const target = this.account2026EventElement(event.target);

            if (!this.admin2026ChromeRoot(target))
            {
                return false;
            }

            const link = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;

            if (!link || !this.admin2026ChromeRoot(link))
            {
                return false;
            }

            if (
                typeof link.closest === 'function'
                && link.closest('[data-admin2026-no-overlay]')
            )
            {
                return false;
            }

            if (!this.account2026ElementOpensInSameTab(link))
            {
                return false;
            }

            return this.account2026UrlLoadsNewDocument(link.getAttribute('href'));
        },

        admin2026ManualNavigationShouldShowOverlay(event, link, action=0)
        {
            if (
                !link
                || !event
                || this.account2026ModifiedNavigationEvent(event)
                || !this.admin2026ChromeRoot(event.target)
                || action === 2
                || action === 3
            )
            {
                return false;
            }

            try
            {
                const nextUrl = new URL(link, window.location.href);

                if (nextUrl.origin !== window.location.origin && action !== 1)
                {
                    return false;
                }
            }
            catch (e) {}

            return this.account2026UrlLoadsNewDocument(link);
        },

        browserHistoryRestored(event=null)
        {
            try
            {
                if (event && event.persisted === true)
                {
                    return true;
                }
            }
            catch (e) {}

            try
            {
                if (
                    typeof performance !== 'undefined'
                    && typeof performance.getEntriesByType === 'function'
                )
                {
                    const navigationEntries = performance.getEntriesByType('navigation');
                    const latestNavigation = navigationEntries && navigationEntries.length ? navigationEntries[0] : null;

                    if (latestNavigation && latestNavigation.type === 'back_forward')
                    {
                        return true;
                    }
                }
            }
            catch (e) {}

            try
            {
                if (
                    typeof performance !== 'undefined'
                    && performance.navigation
                    && performance.navigation.type === 2
                )
                {
                    return true;
                }
            }
            catch (e) {}

            return false;
        },

        hideOverlayForBrowserHistoryRestore(event=null)
        {
            if (!this.browserHistoryRestored(event))
            {
                return;
            }

            this.hideOverlay();
        },

        // hide page overlay when broswer has finished loading
        hideOverlayWhenLoadingComplete(timeoutMs=null) {
            this.overlayOn = true;
            let timeout = timeoutMs && timeoutMs > 0 ? timeoutMs : (this.isAdminRoute() ? 10000 : 5000);
            setTimeout(() => {
                this.overlayOn = false;
            }, timeout);
        },

        // show top overlay
        showBottomOverlay()
        {
            this.overlayBottomOn = true;
        },

        // hide top overlay
        hideBottomOverlay()
        {
            this.overlayBottomOn = false;
        },

        // auto-dismissing alert
        alertCountdownChanged(alertCountdown)
        {
            this.alertCountdown = alertCountdown;
        },

        // open link
        openLink(e, link, action=0, stopPropagation=false)
        {
            // stop propagation if pressing shift key
            if (e.shiftKey)
            {
                e.stopPropagation();
                return;
            }

            // stop propagation
            if (stopPropagation)
            {
                e.stopPropagation();
            }

            if (!link)
            {
                return;
            }

            if (link.toString().substr(0,4) == 'www.')
            {
                link = 'http://' + link;
            }

            const showAccount2026Overlay = this.account2026ManualNavigationShouldShowOverlay(e, link, action);
            const showAdmin2026Overlay = this.admin2026ManualNavigationShouldShowOverlay(e, link, action);
            const showNavigationOverlay = showAccount2026Overlay || showAdmin2026Overlay;

            if (action == 3)
            {
                if (link.toString().substr(0,4) == 'http' || link.toString().substr(0,1) == '/')
                {
                    window.open(link);
                }
                else
                {
                    window.open('/'+link);
                }
            }
            else if (action == 2 || (link.toString().substr(0,4) == 'http' && action !== 1 && !link.includes(window.location.origin)))
            {
                window.open(link);
            }
            else
            {
                if (e.ctrlKey || e.metaKey)
                {
                    window.open(link);
                }
                else
                {
                    if (showNavigationOverlay)
                    {
                        this.showOverlay();
                    }

                    location.href = link;
                }
            }
        },

        sameDocumentHashLink(link)
        {
            if (typeof window === 'undefined' || !link)
            {
                return false;
            }

            try
            {
                const target = new URL(link.toString(), window.location.href);

                return target.origin === window.location.origin
                    && target.pathname === window.location.pathname
                    && target.search === window.location.search
                    && target.hash !== '';
            }
            catch (e)
            {
                return false;
            }
        },

        // open link with overlay
        openLinkWithOverlay(e, link, action=0, stopPropagation=false)
        {
            const navigationEvent = e || (typeof window !== 'undefined' ? window.event : null) || {};

            if (this.sameDocumentHashLink(link))
            {
                this.openLink(navigationEvent, link, action, stopPropagation);
                return;
            }

            if (!navigationEvent.ctrlKey && !navigationEvent.shiftKey && !navigationEvent.metaKey)
            {
                this.showOverlay();
            }

            setTimeout(() => { this.openLink(navigationEvent, link, action, stopPropagation); }, 100);
            this.hideOverlayWhenLoadingComplete();
        },

        openUrl(e, url)
        {
            e.stopPropagation();
            window.open(url);
        },

        account2026ResolveElement(target)
        {
            if (typeof document === 'undefined' || !target)
            {
                return null;
            }

            if (typeof target === 'string')
            {
                return document.querySelector(target);
            }

            if (Array.isArray(target))
            {
                return target[0] || null;
            }

            if (target && target.$el)
            {
                return target.$el;
            }

            return target;
        },

        account2026FocusElement(element)
        {
            if (!element || typeof element.focus !== 'function')
            {
                return false;
            }

            try
            {
                element.focus({ preventScroll: true });
            }
            catch (error)
            {
                element.focus();
            }

            return true;
        },

        account2026VisibleDrawerDialogs()
        {
            if (typeof document === 'undefined')
            {
                return [];
            }

            return Array.from(document.querySelectorAll('.account2026-frame-drawer-shell[role="dialog"][aria-modal="true"]')).filter((drawer) => {
                return this.admin2026ElementIsVisible(drawer);
            });
        },

        account2026FocusDrawerSurface(target, options = {})
        {
            const delay = Number.isFinite(Number(options.delay)) ? Number(options.delay) : 50;

            this.$nextTick(() => {
                setTimeout(() => {
                    const drawer = this.account2026ResolveElement(target);

                    if (!drawer || !this.admin2026ElementIsVisible(drawer))
                    {
                        return;
                    }

                    const activeElement = document.activeElement || null;

                    if (
                        options.onlyIfFocusOutside !== false
                        && activeElement
                        && activeElement !== document.body
                        && activeElement !== document.documentElement
                        && typeof drawer.contains === 'function'
                        && drawer.contains(activeElement)
                    )
                    {
                        return;
                    }

                    if (typeof drawer.hasAttribute === 'function' && !drawer.hasAttribute('tabindex'))
                    {
                        drawer.setAttribute('tabindex', '-1');
                    }

                    this.account2026FocusElement(drawer);
                }, Math.max(0, delay));
            });
        },

        account2026FocusLatestDrawer(options = {})
        {
            const delay = Number.isFinite(Number(options.delay)) ? Number(options.delay) : 50;

            this.$nextTick(() => {
                setTimeout(() => {
                    const drawers = this.account2026VisibleDrawerDialogs();
                    const drawer = drawers.length ? drawers[drawers.length - 1] : null;

                    if (!drawer)
                    {
                        return;
                    }

                    if (options.markFocused !== false && drawer.dataset)
                    {
                        drawer.dataset.account2026InitialFocusApplied = 'true';
                    }

                    this.account2026FocusDrawerSurface(drawer, {
                        delay: 0,
                        onlyIfFocusOutside: options.onlyIfFocusOutside !== false,
                    });
                }, Math.max(0, delay));
            });
        },

        account2026FocusDrawerBySelector(selector, options = {})
        {
            this.account2026FocusDrawerSurface(selector, options);
        },

        account2026ScheduleInitialDrawerFocus()
        {
            [60, 240].forEach((delay) => {
                this.account2026FocusLatestDrawer({
                    delay: delay,
                    onlyIfFocusOutside: true,
                    markFocused: true,
                });
            });
        },

        account2026CloseStaticDrawer(closeUrl)
        {
            if (!closeUrl)
            {
                return;
            }

            this.showOverlay();
            window.location.href = closeUrl;
        },

        account2026FrameDrawerUrl(link)
        {
            if (!link)
            {
                return '';
            }

            try
            {
                const url = new URL(link, window.location.origin);

                if (url.origin === window.location.origin && !url.searchParams.has('account_drawer'))
                {
                    url.searchParams.set('account_drawer', '1');
                }

                return url.toString();
            }
            catch (error)
            {
                return link;
            }
        },

        account2026BindFrameDrawerEscape(frame)
        {
            if (!frame)
            {
                return;
            }

            try
            {
                const frameWindow = frame.contentWindow;

                if (!frameWindow || typeof frameWindow.addEventListener !== 'function')
                {
                    return;
                }

                frameWindow.addEventListener('keydown', (event) => {
                    if (!event || (event.key !== 'Escape' && event.key !== 'Esc'))
                    {
                        return;
                    }

                    event.preventDefault();
                    this.closeAccount2026FrameDrawer();
                });
            }
            catch (error)
            {
                // Cross-origin frames cannot be bound; parent-window Escape still works when the parent has focus.
            }
        },

        markIncomingMessageListRead(id)
        {
            markIncomingMessageListRead(id);
        },

        setIncomingMessageListReadState(id, read)
        {
            setIncomingMessageListReadState(id, read);
        },

        account2026FrameDrawerSyncMessageReads(frame)
        {
            try
            {
                syncConfirmedMessageListReads(frame && frame.contentDocument);
            }
            catch (error)
            {
                // A cross-origin or unavailable frame cannot confirm a read.
            }
        },

        account2026FrameDrawerHasSaved(frame)
        {
            if (!frame)
            {
                return false;
            }

            try
            {
                const frameWindow = frame.contentWindow;

                if (!frameWindow || !frameWindow.location)
                {
                    return false;
                }

                const params = new URLSearchParams(frameWindow.location.search || '');

                return params.get('account_drawer') === '1' && params.get('saved') === '1';
            }
            catch (error)
            {
                return false;
            }
        },

        openAccount2026FrameDrawer(e, link, title='Details')
        {
            if (e)
            {
                if (this.account2026ModifiedNavigationEvent(e))
                {
                    e.stopPropagation();
                    return;
                }

                e.preventDefault();
                e.stopPropagation();
            }

            if (!link)
            {
                return;
            }

            const drawerUrl = this.account2026FrameDrawerUrl(link);
            this.account2026FrameDrawerNeedsRefresh = false;

            this.openModal('account2026-frame-drawer');
            this.$nextTick(() => {
                const drawer = document.querySelector('[data-account2026-frame-drawer="true"]');

                if (!drawer)
                {
                    window.location.href = link;
                    return;
                }

                const frame = drawer.querySelector('[data-account2026-frame-drawer-iframe="true"]');
                const titleElement = drawer.querySelector('[data-account2026-frame-drawer-title="true"]');
                const fullPageLink = drawer.querySelector('[data-account2026-frame-drawer-open="true"]');

                if (titleElement)
                {
                    titleElement.textContent = title || 'Details';
                }

                if (fullPageLink)
                {
                    fullPageLink.setAttribute('href', link);
                }

                if (frame)
                {
                    frame.onload = () => {
                        this.account2026BindFrameDrawerEscape(frame);
                        this.account2026FrameDrawerSyncMessageReads(frame);
                        this.account2026SyncFrameDrawerAppearance(this.account2026Appearance);
                        if (this.account2026FrameDrawerHasSaved(frame))
                        {
                            this.account2026FrameDrawerNeedsRefresh = true;
                        }
                    };
                    frame.setAttribute('title', title || 'Details');
                    frame.setAttribute('src', drawerUrl);
                }

                if (drawer)
                {
                    this.account2026FocusDrawerSurface(drawer, {
                        delay: 20,
                        onlyIfFocusOutside: true,
                    });
                    this.account2026FocusDrawerSurface(drawer, {
                        delay: 240,
                        onlyIfFocusOutside: true,
                    });
                }
            });
        },

        closeAccount2026FrameDrawer()
        {
            if (this.account2026FrameDrawerNeedsRefresh)
            {
                this.showOverlay();
                window.location.reload();
                return;
            }

            this.closeModal('account2026-frame-drawer');

            window.setTimeout(() => {
                const frame = document.querySelector('[data-account2026-frame-drawer-iframe="true"]');

                if (frame)
                {
                    frame.onload = null;
                    frame.setAttribute('src', 'about:blank');
                }
            }, 180);
        },

        // copy to clipboard
        copyToClipboard(content, message, toastTimeout=5000, options=null)
        {
            let textArea = document.createElement('textarea');
            textArea.value = content;
            document.body.appendChild(textArea);
            textArea.select();
            if (options && options.verifyCopy === true)
            {
                let copied = false;
                try
                {
                    copied = document.execCommand('copy');
                }
                catch (error) {}
                document.body.removeChild(textArea);
                if (!copied)
                {
                    this.openModalWarning('Unable to copy link', options.failureMessage || 'The link could not be copied. Please try again.');
                    return false;
                }
            }
            else
            {
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }

            this.showToastApp(message ? message : 'Copied to the clipboard', toastTimeout);
            this.showToastAdmin(message ? message : 'Copied to the clipboard');

            // remove selection
            if (window.getSelection)
            {
                window.getSelection().removeAllRanges();
            }
            else if (document.selection)
            {
                document.selection.empty();
            }
        },

        // clean text in textarea
        textareaCleanText(data)
        {
            this[data] = this[data].replace(/[\r\n]+/g, " ");
        },

        // clicked on admin user note to expand/collapse it
        clickOnNote(id) {
            let index = this.userNotesClicked.indexOf(id);
            if (index > -1)
            {
                this.userNotesClicked = [];
            }
            else
            {
                this.userNotesClicked = [];
                this.userNotesClicked.push(id);
            }
        },

        // set active index when scrolling in carousel
        carouselUpdateActiveIndex(slide) {
            this.carouselActiveIndex = slide.slidingToIndex;
        },

        // set datepicker to today
        dateRangeToPair(value)
        {
            if (Array.isArray(value))
            {
                const start = value[0] || '';
                const end = value[1] || start;
                return [start, end];
            }

            if (value && typeof value === 'object')
            {
                const parts = Object.values(value);
                const start = parts[0] || '';
                const end = parts[1] || start;
                return [start, end];
            }

            if (typeof value === 'string')
            {
                const parts = value.split('~').map(v => (v || '').trim()).filter(Boolean);
                const start = parts[0] || '';
                const end = parts[1] || start;
                return [start, end];
            }

            return ['', ''];
        },

        dateRangeValueToString(value)
        {
            const [start, end] = this.dateRangeToPair(value);
            if (!start && !end)
            {
                return '';
            }

            return `${start} ~ ${end}`;
        },

        setDateRangeValue(vmodel, start, end)
        {
            if (Array.isArray(this[vmodel]))
            {
                this[vmodel] = [start, end];
                return;
            }

            if (this[vmodel] && typeof this[vmodel] === 'object')
            {
                const keys = Object.keys(this[vmodel]);
                if (keys.length >= 2)
                {
                    this[vmodel] = {
                        [keys[0]]: start,
                        [keys[1]]: end,
                    };
                    return;
                }
            }

            this[vmodel] = `${start} ~ ${end}`;
        },

        dateSetToday(vmodel, range)
        {
            const today = this.moment().format('DD MMM YYYY');

            if (range)
            {
                this.setDateRangeValue(vmodel, today, today);
                if (this.$refs[vmodel])
                {
                    this.$refs[vmodel].pickerValue = `${today} ~ ${today}`;
                }
            }
            else
            {
                this[vmodel] = today;
                if (this.$refs[vmodel])
                {
                    this.$refs[vmodel].pickerValue = today;
                }
            }

            this.touchForm();
        },

        // open notification from the admin notification center
        openNotification(e, notification)
        {
            this.showOverlay();

            // automatically mark as read?
            if (this.autoMarkNotificationsAsRead)
            {
                this.axios.post('/pono-admin/notifications/'+notification.id+'/mark-as-read').then(res => {
                    if (e.ctrlKey || e.metaKey)
                    {
                        this.hideOverlay();
                        window.open(notification.url);
                    }
                    else
                    {
                        location.href = notification.url;
                    }
                });
            }
            else
            {
                if (e.ctrlKey || e.metaKey)
                {
                    this.hideOverlay();
                    window.open(notification.url);
                }
                else
                {
                    location.href = notification.url;
                }
            }
        },

        // mark admin notification as read
        markNotificationAsRead(notification)
        {
            this.axios.post('/pono-admin/notifications/'+notification.id+'/mark-as-read').then(res => {
                if (res && res.data && res.data.success)
                {
                    notification.read_at = new Date();
                    this.getNotificationCount();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // mark admin notification as unread
        markNotificationAsUnread(notification)
        {
            this.axios.post('/pono-admin/notifications/'+notification.id+'/mark-as-unread').then(res => {
                if (res && res.data && res.data.success)
                {
                    notification.read_at = null;
                    this.getNotificationCount();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // mark all admin notification as read
        markAllNotificationAsRead(id)
        {
            this.openModalConfirm('Are you sure?',
                                    'Would you like to mark all notifications as read?',
                                    'Yes, mark all as read now',
                                    'No, do not mark as read',
                                    'markAllNotificationAsReadConfirmed',
                                    id);
        },

        markAllNotificationAsReadConfirmed(id)
        {
            this.showOverlay();
            window.location.href = '/pono-admin/users/' + id + '/notifications/mark-all-as-read';
        },

        // view admin notification detail popup
        viewNotificationDetails(notification)
        {
            this.modalNotificationDetailsData = notification;
            this.modalNotificationDetailsOpen = true;
        },

        // update unread notification count
        getNotificationCount()
        {
            this.axios.post(this.notificationRequestUrl()).then(res => {
                if (res && res.data && res.data.success)
                {
                    this.unreadNotificationCount = res.data.unreadNotificationCount;
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // called (mostly from admin) when any form element is touched
        touchForm(event=null)
        {
            this.formTouched = true;
            this.admin2026SetDirtyFormState(true);

            const target = event && event.target ? event.target : null;

            if (target && typeof target.closest === 'function') {
                const dirtyForm = target.closest('[data-admin2026-dirty-form="true"]');

                if (dirtyForm) {
                    this.admin2026MarkDirtyFormElement(dirtyForm);
                }
            }
        },

        admin2026SetDirtyFormState(dirty=true)
        {
            try
            {
                if (typeof window === 'undefined' || !window.vAdmin2026Chrome || typeof document === 'undefined') {
                    return;
                }

                window.vAdmin2026HasDirtyForm = !!dirty;

                if (document.documentElement && document.documentElement.dataset) {
                    document.documentElement.dataset.admin2026HasDirtyForm = dirty ? 'true' : 'false';
                }
            }
            catch (e) {}
        },

        admin2026MarkDirtyFormElement(element)
        {
            if (!element) {
                return;
            }

            if (element.dataset) {
                element.dataset.admin2026FormDirty = 'true';
            }

            this.admin2026SetDirtyFormState(true);

            const formKey = typeof element.getAttribute === 'function'
                ? element.getAttribute('data-admin2026-dirty-form-key')
                : null;

            if (formKey)
            {
                this.admin2026DirtyForms = Object.assign({}, this.admin2026DirtyForms, {
                    [formKey]: true,
                });
            }
        },

        admin2026ClearDirtyFormState(scope=null)
        {
            try
            {
                this.formTouched = false;
                this.admin2026DirtyForms = {};
                this.admin2026SetDirtyFormState(false);

                const surface = this.admin2026ResolveElement(scope) || (typeof document !== 'undefined' ? document : null);

                if (surface && typeof surface.querySelectorAll === 'function') {
                    surface.querySelectorAll('[data-admin2026-form-dirty="true"]').forEach((element) => {
                        if (element.dataset) {
                            delete element.dataset.admin2026FormDirty;
                        }
                    });
                }
            }
            catch (e) {}
        },

        admin2026TouchDirtyForm(formKey=null)
        {
            this.touchForm();

            if (formKey)
            {
                this.admin2026DirtyForms = Object.assign({}, this.admin2026DirtyForms, {
                    [formKey]: true,
                });
            }
        },

        admin2026IsDirtyForm(formKey=null)
        {
            return formKey ? !!this.admin2026DirtyForms[formKey] : this.formTouched;
        },

        admin2026MarkDirtyForm(event)
        {
            try
            {
                const isAdmin2026Chrome = typeof window !== 'undefined' && !!window.vAdmin2026Chrome;

                if (!isAdmin2026Chrome || !event || !event.target || typeof event.target.closest !== 'function')
                {
                    return;
                }

                if (event.target.closest('[data-admin2026-dirty-ignore="true"]'))
                {
                    return;
                }

                const dirtyForm = event.target.closest('[data-admin2026-dirty-form="true"]');

                if (dirtyForm)
                {
                    this.admin2026MarkDirtyFormElement(dirtyForm);
                    this.admin2026TouchDirtyForm(dirtyForm.getAttribute('data-admin2026-dirty-form-key') || null);
                }
            }
            catch (e) {}
        },

        admin2026DocumentHasDirtyForm(doc=null)
        {
            try
            {
                const targetDocument = doc || (typeof document !== 'undefined' ? document : null);

                if (!targetDocument) {
                    return false;
                }

                const targetWindow = targetDocument.defaultView || null;
                const root = targetDocument.documentElement || null;

                if (targetWindow && targetWindow.vAdmin2026HasDirtyForm) {
                    return true;
                }

                if (root && root.dataset && root.dataset.admin2026HasDirtyForm === 'true') {
                    return true;
                }

                return !!targetDocument.querySelector('[data-admin2026-form-dirty="true"]');
            }
            catch (e) {
                return false;
            }
        },

        admin2026FrameHasDirtyForm(frame)
        {
            try
            {
                const iframe = this.admin2026ResolveElement(frame);

                if (!iframe) {
                    return false;
                }

                const frameDocument = iframe.contentDocument
                    || (iframe.contentWindow ? iframe.contentWindow.document : null);

                return this.admin2026DocumentHasDirtyForm(frameDocument);
            }
            catch (e) {
                return false;
            }
        },

        admin2026ScopeHasDirtyForm(scope=null)
        {
            try
            {
                const surface = this.admin2026ResolveElement(scope);

                if (!surface) {
                    return this.formTouched || this.admin2026DocumentHasDirtyForm();
                }

                if (
                    !surface.querySelector
                    || !surface.querySelector('form, [data-admin2026-dirty-form="true"]')
                ) {
                    return false;
                }

                if (surface.dataset && surface.dataset.admin2026FormDirty === 'true') {
                    return true;
                }

                if (surface.querySelector('[data-admin2026-form-dirty="true"]')) {
                    return true;
                }

                return !!this.formTouched;
            }
            catch (e) {
                return false;
            }
        },

        admin2026ConfirmUnsavedDrawerClose(confirmedMethod, scope=null)
        {
            if (!confirmedMethod || typeof this[confirmedMethod] !== 'function') {
                return true;
            }

            if (!this.admin2026ScopeHasDirtyForm(scope)) {
                return true;
            }

            this.admin2026PendingUnsavedDrawerCloseMethod = confirmedMethod;
            this.openModalConfirm(
                'Unsaved changes',
                'This drawer has unsaved changes. If you close it now, those changes will be lost. Save first if you want to keep them.',
                'Close without saving',
                'Keep editing',
                'admin2026ConfirmUnsavedDrawerCloseConfirmed'
            );

            return false;
        },

        admin2026ConfirmUnsavedDrawerCloseConfirmed()
        {
            const confirmedMethod = this.admin2026PendingUnsavedDrawerCloseMethod;

            this.admin2026PendingUnsavedDrawerCloseMethod = '';

            if (confirmedMethod && typeof this[confirmedMethod] === 'function') {
                this[confirmedMethod]();
            }
        },

        admin2026ConfirmDeleteUrl(
            url,
            title='Are you sure?',
            content='Are you sure that you would like to delete and PERMANENTLY remove this? This can NOT be undone.',
            confirmLabel='Yes, delete now',
            cancelLabel='No, do not delete'
        )
        {
            if (!url) {
                return;
            }

            this.openModalConfirm(
                title,
                content,
                confirmLabel,
                cancelLabel,
                'admin2026DeleteUrlConfirmed',
                url
            );
        },

        admin2026DeleteUrlConfirmed(url)
        {
            if (!url) {
                return;
            }

            this.showOverlay();
            window.location.href = url;
        },

        admin2026ConfirmDeleteForm(
            form,
            title='Are you sure?',
            content='Are you sure that you would like to delete and PERMANENTLY remove this? This can NOT be undone.',
            confirmLabel='Yes, delete now',
            cancelLabel='No, do not delete'
        )
        {
            if (!form) {
                return;
            }

            this.openModalConfirm(
                title,
                content,
                confirmLabel,
                cancelLabel,
                'admin2026DeleteFormConfirmed',
                form
            );
        },

        admin2026DeleteFormConfirmed(form)
        {
            const resolvedForm = this.admin2026ResolveElement(form);

            if (!resolvedForm || typeof resolvedForm.submit !== 'function') {
                return;
            }

            this.showOverlay();
            resolvedForm.submit();
        },

        admin2026CancelDirtyForm()
        {
            this.openModalConfirm(
                'Cancel changes?',
                'Are you sure you want to cancel and lose your changes?',
                'Yes, cancel changes',
                'No, keep editing',
                'admin2026CancelDirtyFormConfirmed'
            );
        },

        admin2026CancelDirtyFormConfirmed()
        {
            this.showOverlay();

            setTimeout(() => {
                window.location.reload();
            }, 100);
        },

        // submit form
        submitForm(ref='form')
        {
            if (typeof window !== 'undefined' && window.PonoTurnstileWidgets
                && !window.PonoTurnstileWidgets.canSubmit(this.$refs[ref]))
            {
                return;
            }

            // copy quill content
            try
            {
                this.submitFormAdditional();
            }
            catch(e) {}

            this.showOverlay();

            setTimeout(() => { this.$refs[ref].submit(); }, 100);
        },

        // submit form as draft
        submitFormAsDraft()
        {
            this.showOverlay();
            setTimeout(() => {
                document.getElementById('draft_save_for_later').value = 1;
                this.$refs.form.submit();
            }, 100);
        },

        // show/hide admin send message screen
        sendMessage()
        {
            this.sendMessageVisible = true;
            this.adminStatusSummaryOpen = false;

            setTimeout(() => {
                let messageContent = document.getElementById('message-content');
                if (messageContent && messageContent.firstChild) {
                    messageContent.firstChild.focus();
                }
            }, 500);
        },

        // update analytics report visibility
        toggleAnalyticsWidgetVisibility(id)
        {
            if (this.hiddenWidgets.includes(id))
            {
                let index = this.hiddenWidgets.indexOf(id);
                this.hiddenWidgets.splice(index, 1);
            }
            else
            {
                this.hiddenWidgets.push(id);
            }

            this.axios.post('/pono-admin/reports/analytics/store-widget-visibility', {'widget': id, 'hidden': this.hiddenWidgets.includes(id)});
            this.hiddenWidgetsChanged = true;
        },

        // expand/collapse tree
        expandCollapseTree()
        {
            this.treeExpanded = !this.treeExpanded;
        },

        // handle hover
        handleHover(hovered, id)
        {
            this.isHovered[id] = hovered ? 1 : 0;
            this.$forceUpdate();
        },

        // open OK modal
        openModalOk(title, content, btn='', fn='', id1=null, id2=null, id3=null, options={})
        {
            this.modalOkTitle = title;
            this.modalOkContent = content;
            this.modalOkContentSlot = !!(options && options.contentSlot);
            this.modalOkHeaderLink = options && options.headerLink && typeof options.headerLink.url === 'string' && options.headerLink.url.trim()
                ? {url: options.headerLink.url, label: options.headerLink.label || 'Open in a new tab'}
                : null;
            this.modalOkBtn = btn;
            this.modalOkFn = fn;
            this.modalOkId1 = id1;
            this.modalOkId2 = id2;
            this.modalOkId3 = id3;
            this.modalOkOpen = true;
            this.$forceUpdate();
            this.focusModalActionButton('modalOkButton', 'modalOkOpen');
        },

        // close OK modal
        closeModalOk(confirmed=false)
        {
            this.modalOkOpen = false;
            this.modalOkContentSlot = false;
            this.modalOkHeaderLink = null;

            if (confirmed && this.modalOkFn)
            {
                if (this.modalOkId1 !== null && this.modalOkId2 !== null && this.modalOkId3 !== null)
                {
                    this[this.modalOkFn](this.modalOkId1, this.modalOkId2, this.modalOkId3);
                }
                else if (this.modalOkId1 !== null && this.modalOkId2 !== null)
                {
                    this[this.modalOkFn](this.modalOkId1, this.modalOkId2);
                }
                else if (this.modalOkId1 !== null)
                {
                    this[this.modalOkFn](this.modalOkId1);
                }
                else
                {
                    this[this.modalOkFn]();
                }
            }

            this.modalOkFn = '';
            this.modalOkId1 = null;
            this.modalOkId2 = null;
            this.modalOkId3 = null;
            this.$forceUpdate();
        },

        // open warning modal
        openModalWarning(title, content, btn='')
        {
            this.modalWarningTitle = title;
            this.modalWarningContent = content;
            this.modalWarningBtn = btn;
            this.modalWarningOpen = true;
            this.$forceUpdate();
            this.focusModalActionButton('modalWarningButton', 'modalWarningOpen');
        },

        // close warning modal
        closeModalWarning()
        {
            this.modalWarningOpen = false;
            this.$forceUpdate();
        },

        // open confirm modal
        openModalConfirm(title, content, btn1='', btn2='', fn='', id1=null, id2=null, id3=null, options=null)
        {
            this.modalConfirmTitle = title;
            this.modalConfirmContent = content;
            this.modalConfirmBtn1 = btn1;
            this.modalConfirmBtn2 = btn2;
            this.modalConfirmFn = fn;
            this.modalConfirmId1 = id1;
            this.modalConfirmId2 = id2;
            this.modalConfirmId3 = id3;
            this.modalConfirmSecondaryAction = null;
            this.modalConfirmContentSlot = !!(options && typeof options === 'object' && !Array.isArray(options) && options.contentSlot);
            this.modalConfirmActionsVisible = !(options && typeof options === 'object' && options.actionsVisible === false);
            this.modalConfirmKeepOpenOnConfirm = !!(options && typeof options === 'object' && options.keepOpenOnConfirm);
            this.modalConfirmShowCloseButton = !!(options && typeof options === 'object' && options.showCloseButton);
            this.modalConfirmBusy = false;
            const secondary = options && typeof options === 'object' && !Array.isArray(options)
                ? options.secondaryAction : null;

            if (secondary && typeof secondary.label === 'string' && secondary.label.trim()
                && typeof secondary.method === 'string' && typeof this[secondary.method] === 'function'
                && (secondary.args === undefined || Array.isArray(secondary.args))) {
                this.modalConfirmSecondaryAction = {
                    label: secondary.label,
                    method: secondary.method,
                    args: secondary.args ? [...secondary.args] : [],
                    tone: ['primary', 'success', 'danger'].includes(secondary.tone) ? secondary.tone : 'primary',
                };
            }

            this.modalConfirmOpen = true;
            this.$forceUpdate();
            this.focusModalActionButton(this.modalConfirmActionsVisible ? 'modalConfirmPrimaryButton' : 'modalConfirmCloseButton', 'modalConfirmOpen');
        },

        focusModalActionButton(refName, modalOpenKey)
        {
            if (typeof window === 'undefined') {
                return;
            }

            this.$nextTick(() => {
                const focusButton = (allowRefocusInsideDialog = true) => {
                    if (modalOpenKey && !this[modalOpenKey]) {
                        return;
                    }

                    let button = this.$refs ? this.$refs[refName] : null;

                    if (Array.isArray(button)) {
                        button = button[0];
                    }

                    if (!button || typeof button.focus !== 'function') {
                        return;
                    }

                    if (!allowRefocusInsideDialog && typeof document !== 'undefined') {
                        const activeElement = document.activeElement;
                        const dialog = typeof button.closest === 'function' ? button.closest('[role="dialog"]') : null;
                        const focusableTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
                        const activeElementTag = activeElement && activeElement.tagName ? activeElement.tagName.toUpperCase() : '';

                        if (
                            dialog
                            && activeElement
                            && activeElement !== button
                            && dialog.contains(activeElement)
                            && (focusableTags.includes(activeElementTag) || activeElement.tabIndex >= 0)
                        ) {
                            return;
                        }
                    }

                    try {
                        button.focus({ preventScroll: true });
                    }
                    catch (e) {
                        button.focus();
                    }
                };

                if (typeof window.requestAnimationFrame === 'function') {
                    window.requestAnimationFrame(focusButton);
                }
                else {
                    setTimeout(focusButton, 0);
                }

                [0, 50, 150, 300].forEach((delay) => {
                    setTimeout(() => focusButton(false), delay);
                });
            });
        },

        handleModalConfirmKeydown(event)
        {
            if (!event || !this.modalConfirmOpen || this.modalConfirmBusy || this.modalConfirmActionsVisible === false || event.key !== 'Enter' || event.isComposing) {
                return false;
            }

            let primaryButton = this.$refs ? this.$refs.modalConfirmPrimaryButton : null;

            if (Array.isArray(primaryButton)) {
                primaryButton = primaryButton[0];
            }

            if (primaryButton && event.target && event.target !== primaryButton) {
                const dialog = typeof primaryButton.closest === 'function' ? primaryButton.closest('[role="dialog"]') : null;

                if (dialog && dialog.contains(event.target)) {
                    const eventTargetTag = event.target.tagName ? event.target.tagName.toUpperCase() : '';
                    const focusableTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];

                    if (focusableTags.includes(eventTargetTag) || event.target.tabIndex >= 0 || event.target.isContentEditable) {
                        return false;
                    }
                }
            }

            if (typeof event.preventDefault === 'function') {
                event.preventDefault();
            }
            if (typeof event.stopPropagation === 'function') {
                event.stopPropagation();
            }
            if (typeof event.stopImmediatePropagation === 'function') {
                event.stopImmediatePropagation();
            }

            this.closeModalConfirm(true);

            return true;
        },

        // close OK modal
        closeModalConfirm(confirmed=false)
        {
            if (this.modalConfirmBusy || (confirmed && this.modalConfirmActionsVisible === false)) {
                return;
            }
            if (!confirmed || !this.modalConfirmKeepOpenOnConfirm) {
                this.modalConfirmOpen = false;
            }

            if (confirmed && this.modalConfirmFn)
            {
                if (this.modalConfirmId1 !== null && this.modalConfirmId2 !== null && this.modalConfirmId3 !== null)
                {
                    this[this.modalConfirmFn](this.modalConfirmId1, this.modalConfirmId2, this.modalConfirmId3);
                }
                else if (this.modalConfirmId1 !== null && this.modalConfirmId2 !== null)
                {
                    this[this.modalConfirmFn](this.modalConfirmId1, this.modalConfirmId2);
                }
                else if (this.modalConfirmId1 !== null)
                {
                    this[this.modalConfirmFn](this.modalConfirmId1);
                }
                else
                {
                    this[this.modalConfirmFn]();
                }
            }
        },

        closeModalConfirmSecondary()
        {
            const action = this.modalConfirmSecondaryAction;
            if (!this.modalConfirmOpen || !action || this.modalConfirmBusy || this.modalConfirmActionsVisible === false) {
                return;
            }

            // Close before dispatch; the selected action may open another confirmation.
            this.modalConfirmOpen = false;
            this[action.method](...action.args);
        },

        // open notifications modal
        notificationRequestUrl(filter=null, removeFilter=false, from=null)
        {
            const params = new URLSearchParams();
            const requestedFilter = filter === null ? this.notificationTypeFilter : filter;

            if (requestedFilter) params.set('notification', requestedFilter);
            if (removeFilter) params.set('clearfilter', '1');
            if (this.notificationGroupLabelFilter) params.set('group_label', this.notificationGroupLabelFilter);
            if (from !== null) params.set('from', from);

            const query = params.toString();

            return '/pono-admin/load-unread-notifications' + (query ? '?' + query : '');
        },

        notificationGroupLabelFilterName()
        {
            const selected = (this.notificationGroupLabels || []).find((label) => Number(label.id) === Number(this.notificationGroupLabelFilter));

            return selected ? selected.name : 'Group label';
        },

        notificationTypeFilterName()
        {
            const selected = (this.notificationTypes || []).find((type) => type.key === this.notificationTypeFilter);

            return selected ? selected.value : 'notification type';
        },

        selectNotificationGroupLabel(labelId)
        {
            this.openModalNotifications(null, false, labelId);
        },

        clearNotificationFilters()
        {
            this.notificationGroupLabelFilter = 0;
            this.openModalNotifications('', true, 0);
        },

        openModalNotifications(filter=null, removeFilter=false, groupLabelFilter=null)
        {
            if (filter !== null) this.notificationTypeFilter = filter;
            if (groupLabelFilter !== null) this.notificationGroupLabelFilter = Number(groupLabelFilter) || 0;
            this.visibleNotificationCount = 50;

            this.axios.post(this.notificationRequestUrl(filter, removeFilter)).then(res => {
                if (res && res.data && res.data.success)
                {
                    this.notifications = res.data.notifications;
                    this.notificationsRecent = res.data.recents;
                    this.notificationTypes = res.data.notificationTypes;
                    this.unreadNotificationCount = res.data.unreadNotificationCount;
                    this.recentNotificationCount = res.data.recentNotificationCount;
                    this.notificationTypeFilter = res.data.notificationFilter;
                    this.notificationGroupLabels = Array.isArray(res.data.groupLabels) ? res.data.groupLabels : [];
                    this.notificationGroupLabelFilter = Number(res.data.groupLabelFilter) || 0;

                    this.modalNotificationsOpen = true;
                    this.attachModalNotificationsDrawerListeners();
                    this.$forceUpdate();
                    this.blurFocus();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // load previous or next page of notifications
        loadPreviousNextModalNotifications(previous=false)
        {
            this.showOverlay();
            this.detachModalNotificationsDrawerListeners();
            this.modalNotificationsOpen = false;
            if (previous)
            {
                this.visibleNotificationCount -= 100;
            }

            this.axios.post(this.notificationRequestUrl(null, false, this.visibleNotificationCount)).then(res => {
                this.hideOverlay();

                if (res && res.data && res.data.success)
                {
                    this.visibleNotificationCount += 50;

                    this.notifications = res.data.notifications;
                    this.notificationsRecent = res.data.recents;
                    this.notificationTypes = res.data.notificationTypes;
                    this.unreadNotificationCount = res.data.unreadNotificationCount;
                    this.recentNotificationCount = res.data.recentNotificationCount;
                    this.notificationGroupLabels = Array.isArray(res.data.groupLabels) ? res.data.groupLabels : [];
                    this.notificationGroupLabelFilter = Number(res.data.groupLabelFilter) || 0;
                    this.modalNotificationsOpen = true;
                    this.attachModalNotificationsDrawerListeners();
                    this.$forceUpdate();
                    this.blurFocus();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // close notifications modal
        closeModalNotifications()
        {
            this.detachModalNotificationsDrawerListeners();
            this.modalNotificationDetailsOpen = false;
            this.modalNotificationsOpen = false;
            this.$forceUpdate();
        },

        // close notifications modal
        closeModalNotificationDetails()
        {
            this.modalNotificationDetailsOpen = false;
            this.$forceUpdate();
        },

        attachModalNotificationsDrawerListeners()
        {
            if (typeof document === 'undefined' || this._modalNotificationsDrawerKeydownHandler) {
                return;
            }

            this._modalNotificationsDrawerKeydownHandler = (event) => {
                this.handleModalNotificationsDrawerKeydown(event);
            };

            document.addEventListener('keydown', this._modalNotificationsDrawerKeydownHandler, true);
        },

        detachModalNotificationsDrawerListeners()
        {
            if (typeof document !== 'undefined' && this._modalNotificationsDrawerKeydownHandler) {
                document.removeEventListener('keydown', this._modalNotificationsDrawerKeydownHandler, true);
            }

            this._modalNotificationsDrawerKeydownHandler = null;
            this.resetModalNotificationsBackdropPointer();
        },

        handleModalNotificationsDrawerKeydown(event)
        {
            if (!event || event.key !== 'Escape' || (!this.modalNotificationsOpen && !this.modalNotificationDetailsOpen)) {
                return;
            }

            if (typeof event.preventDefault === 'function') {
                event.preventDefault();
            }
            if (typeof event.stopPropagation === 'function') {
                event.stopPropagation();
            }
            if (typeof event.stopImmediatePropagation === 'function') {
                event.stopImmediatePropagation();
            }

            if (this.modalNotificationDetailsOpen) {
                this.closeModalNotificationDetails();
                return;
            }

            this.closeModalNotifications();
        },

        handleModalNotificationsDialogClose()
        {
            // Ignore generic Headless UI close requests for this drawer.
            // Explicit Escape, backdrop, and button handlers own closing.
        },

        handleModalNotificationsBackdropPointerDown(event)
        {
            this.guardedDrawerBackdropPointerDown('_modalNotificationsBackdropPointer', event);
        },

        handleModalNotificationsBackdropPointerUp(event)
        {
            if (!this.modalNotificationsOpen) {
                this.resetModalNotificationsBackdropPointer();
                return;
            }

            this.guardedDrawerBackdropPointerUp(
                '_modalNotificationsBackdropPointer',
                event,
                () => this.closeModalNotifications()
            );
        },

        resetModalNotificationsBackdropPointer()
        {
            this.resetGuardedDrawerBackdropPointer('_modalNotificationsBackdropPointer');
        },

        // open PONO placeholders modal
        openModalPlaceholders(aboveDrawer=false)
        {
            this.modalPlaceholdersAboveDrawer = aboveDrawer === true;
            this.modalPlaceholdersOpen = true;
            this.$forceUpdate();
        },

        // close PONO placeholders modal
        closeModalPlaceholders()
        {
            this.modalPlaceholdersOpen = false;
            this.modalPlaceholdersAboveDrawer = false;
            this.$forceUpdate();
        },

        // open Pono upgrade modal
        openModalPonoUpgrade()
        {
            this.modalPonoupgradeOpen = true;
            this.$forceUpdate();
        },

        // close Pono upgrade modal
        closeModalPonoUpgrade()
        {
            this.modalPonoupgradeOpen = false;
            this.$forceUpdate();
        },

        // show modal for assets type 1, images
        openModalAsset1(src, name='', rotation=0, heicSource='', options={})
        {
            this.modalAsset1Src = src;
            this.modalAsset1Name = name;
            this.modalAsset1Rotation = rotation;
            this.modalAsset1HeicSource = heicSource || '';
            this.modalAsset1Frame = options && ['phone', 'phone-capture', 'laptop'].includes(options.frame) ? options.frame : '';
            this.modalAsset1Open = true;
            this.$forceUpdate();
        },

        // close asset 1 modal
        closeModalAsset1()
        {
            this.modalAsset1Open = false;
            this.modalAsset1HeicSource = '';
            this.$forceUpdate();
        },

        // show modal for assets type 11, youtube videos
        openModalAsset11(src)
        {
            this.modalAsset11Src = src;
            this.modalAsset11Open = true;
            this.$forceUpdate();
        },

        // close asset 11 modal
        closeModalAsset11()
        {
            this.modalAsset11Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 13, website videos
        openModalAsset13(src, converting, options = {})
        {
            this.modalAsset13Options = options || {};
            this.modalAsset13Converting = converting;
            this.modalAsset13Src = src;
            this.modalAsset13Open = true;
            this.$forceUpdate();
            if (this.modalAsset13Options.helpPreview) {
                this.$nextTick(() => {
                    if (!this.modalAsset13Open || this.modalAsset13Src !== src || !this.modalAsset13Options.helpPreview) return;
                    const video = this.$refs.modalAsset13Video;
                    if (video) window.PonoFeaturePlayer?.attach(video, { buttonClass: 'tbtn tbtn-outline-primary cursor-pointer' });
                });
            }
        },

        // close asset 13 modal
        closeModalAsset13()
        {
            if (this.modalAsset13Options.helpPreview) {
                const video = this.$refs.modalAsset13Video;
                if (video) {
                    window.PonoFeaturePlayer?.destroy(video);
                    video.pause();
                }
            }
            this.modalAsset13Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 14, vimeo videos
        openModalAsset14(src)
        {
            this.modalAsset14Src = src;
            this.modalAsset14Open = true;
            this.$forceUpdate();
        },

        // close asset 14 modal
        closeModalAsset14()
        {
            this.modalAsset14Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 15, wistia videos
        openModalAsset15(src)
        {
            this.modalAsset15Src = src;
            this.modalAsset15Open = true;
            this.$forceUpdate();
        },

        // close asset 15 modal
        closeModalAsset15()
        {
            this.modalAsset15Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 16, loom videos
        openModalAsset16(src)
        {
            this.modalAsset16Src = src;
            this.modalAsset16Open = true;
            this.$forceUpdate();
        },

        // close asset 16 modal
        closeModalAsset16()
        {
            this.modalAsset16Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 17, pdf/doc previews
        openModalAsset17(src, name='', mime='')
        {
            this.modalAsset17Src = src;
            this.modalAsset17Name = name;
            this.modalAsset17IsPdf = false;
            this.modalAsset17Sandbox = '';
            this.modalAsset17SupportsEmbed = true;
            this.modalAsset17Message = '';
            const lowerMime = (mime || '').toLowerCase();
            const isHtml = lowerMime.includes('text/html') || /\.(html?|xhtml)($|\?)/i.test(src) || /\.(html?|xhtml)$/i.test(name || '');
            const isPdf = lowerMime.includes('pdf')
                || /\.pdf($|\?)/i.test(src)
                || /\.pdf$/i.test(name || '');
            const isOffice = ['msword','wordprocessingml','ms-excel','spreadsheetml','ms-powerpoint','presentationml','vnd.ms-'].some(t => lowerMime.includes(t))
                || /\.(docx?|xlsx?|xlsm|csv|pptx?|ppsx?)($|\?)/i.test(src);
            const isApple = lowerMime.includes('vnd.apple.pages') || lowerMime.includes('vnd.apple.numbers') || /\.(pages|numbers)($|\?)/i.test(src);

            if (isHtml) {
                this.modalAsset17EmbedSrc = src;
                this.modalAsset17Sandbox = 'allow-scripts allow-forms allow-popups';
            } else if (isPdf) {
                this.modalAsset17IsPdf = true;
                this.modalAsset17EmbedSrc = src;
            } else if (isOffice) {
                this.modalAsset17EmbedSrc = 'https://view.officeapps.live.com/op/embed.aspx?src=' + encodeURIComponent(src);
            } else if (isApple) {
                // Google viewer often blocks iframe for Pages/Numbers; fall back to download/open.
                this.modalAsset17SupportsEmbed = false;
                this.modalAsset17EmbedSrc = '';
                this.modalAsset17Message = 'Preview not available for this file type. Use Open in new tab or Download to view.';
            } else {
                this.modalAsset17EmbedSrc = src;
            }

            this.modalAsset17Open = true;
            this.$forceUpdate();
        },

        // close asset 17 modal
        closeModalAsset17()
        {
            this.modalAsset17Open = false;
            this.modalAsset17IsPdf = false;
            this.$forceUpdate();
        },

        handlePonoUpdateAssetClick(event) {
            if (!event || !(event.target instanceof Element)) return;

            const btn = event.target.closest('.pono-update-asset[data-asset]');
            if (!btn) return;

            const src = btn.getAttribute('data-asset');
            if (!src) return;

            const name = btn.getAttribute('data-name') || '';
            const rotation = parseInt(btn.getAttribute('data-rotation') || '0', 10);

            this.openModalAsset1(src, name, rotation);
        },

        // open view specific modal
        openModal(name, options={})
        {
            this.modalImagePreviewParents[name] = !!(options && options.preserveForImagePreview === true);
            this.modalOpen[name] = true;
            this.$forceUpdate();
            this.account2026ScheduleInitialDrawerFocus();
            this.admin2026FocusLatestDrawer({
                delay: 60,
                onlyIfFocusOutside: true,
                markFocused: true,
            });
            this.admin2026FocusLatestDrawer({
                delay: 260,
                onlyIfFocusOutside: true,
                markFocused: true,
            });
            this.admin2026FocusLatestDrawer({
                delay: 420,
                onlyIfFocusOutside: true,
                markFocused: true,
            });
        },

        // close view specific modal
        closeModal(name)
        {
            if (this.modalImagePreviewParents[name] && this.modalAsset1Open)
            {
                this.closeModalAsset1();
                return;
            }

            this.modalOpen[name] = false;
            delete this.modalImagePreviewParents[name];
            this.$forceUpdate();
        },

        // fix to open Litepie datepicker in modal
        openDatepicker(name)
        {
            this.$refs[name].isShow = !this.$refs[name].isShow;
        },

        // show toast app
        showToastApp(str, timeout=5000)
        {
            this.toastApp = str;

            setTimeout(() => { this.toastApp = ''; }, timeout);
        },

        // show toast admin
        showToastAdmin(str, icon='check', timeout=5000)
        {
            if (this.toastAdminTimeoutId) {
                clearTimeout(this.toastAdminTimeoutId);
                this.toastAdminTimeoutId = null;
            }

            this.toastAdmin = str;
            this.toastAdminIcon = icon ? icon : 'check';

            if (timeout > 0) {
                this.toastAdminTimeoutId = setTimeout(() => {
                    this.closeToastAdmin();
                }, timeout);
            }
        },

        prepareAdmin2026SentSound()
        {
            admin2026SentSound.prepare();
        },

        playAdmin2026SentSound()
        {
            admin2026SentSound.play();
        },

        closeToastAdmin()
        {
            if (this.toastAdminTimeoutId) {
                clearTimeout(this.toastAdminTimeoutId);
                this.toastAdminTimeoutId = null;
            }

            this.toastAdmin = '';
            this.toastAdminIcon = 'check';
        },

        openAdmin2026SupportDrawer()
        {
            if (!this.admin2026SupportBoot || !this.admin2026SupportBoot.enabled)
            {
                return;
            }

            this.admin2026SupportDrawerPreviousFocusEl = typeof document !== 'undefined' ? document.activeElement : null;
            this.admin2026SupportResetForm();
            this.admin2026SupportDrawerOpen = true;
            this.admin2026FocusDrawerBySelector('[data-admin2026-support-drawer="true"]', {
                delay: 50,
                preferField: true,
                focusSelector: '[name="subject"]',
                markFocused: true,
            });
        },

        closeAdmin2026SupportDrawer()
        {
            if (this.admin2026SupportSubmitting)
            {
                return;
            }

            this.admin2026SupportDrawerOpen = false;
            this.admin2026SupportResetForm();

            const previousFocusEl = this.admin2026SupportDrawerPreviousFocusEl;
            this.admin2026SupportDrawerPreviousFocusEl = null;
            this.$nextTick(() => {
                if (previousFocusEl && typeof previousFocusEl.focus === 'function' && document.contains(previousFocusEl))
                {
                    previousFocusEl.focus();
                }
            });
        },

        openAdmin2026SupportHistory()
        {
            if (
                !this.admin2026SupportBoot
                || !this.admin2026SupportBoot.has_history
                || !this.admin2026SupportBoot.history_url
            )
            {
                return;
            }

            this.showOverlay();
            window.location.assign(this.admin2026SupportBoot.history_url);
        },

        admin2026SupportResetForm()
        {
            this.admin2026SupportError = '';
            this.admin2026SupportForm = {
                support_ticket_type_id: '',
                subject: '',
                body: '',
                regarding_current_screen: true,
            };
            this.admin2026SupportClearUploads();
        },

        admin2026SupportClearUploads()
        {
            const upload = 9901;
            const uploadGroups = Array.isArray(this.uploadAssets[upload]) ? this.uploadAssets[upload].length : 0;

            for (let index = 1; index <= 20; index++)
            {
                this['file_upload'+upload+index] = null;

                const input = this.$refs['file_upload'+upload+index];
                const resolvedInput = Array.isArray(input) ? input[0] : input;
                if (resolvedInput)
                {
                    resolvedInput.value = '';
                }
            }

            this.uploadAssets[upload] = [];
            this.uploadAssetsCount = Math.max(0, Number(this.uploadAssetsCount || 0) - uploadGroups);
            this.fileUploadSetDragActive(upload, false);
        },

        admin2026SupportAppendUploads(formData)
        {
            const upload = 9901;
            const uploadGroups = Array.isArray(this.uploadAssets[upload]) ? this.uploadAssets[upload] : [];

            uploadGroups.slice(0, 20).forEach((files, groupIndex) => {
                Array.from(files || []).forEach((file) => {
                    if (file)
                    {
                        formData.append('file_upload'+upload+(groupIndex + 1)+'[]', file);
                    }
                });
            });
        },

        async submitAdmin2026SupportTicket()
        {
            if (this.admin2026SupportSubmitting)
            {
                return;
            }

            const typeId = String(this.admin2026SupportForm.support_ticket_type_id || '').trim();
            const subject = String(this.admin2026SupportForm.subject || '').trim();
            const body = String(this.admin2026SupportForm.body || '').trim();
            if (!typeId || !subject || !body)
            {
                this.admin2026SupportError = 'Choose a request type and enter a subject and message.';
                return;
            }

            this.admin2026SupportSubmitting = true;
            this.admin2026SupportError = '';

            const formData = new FormData();
            formData.append('support_ticket_type_id', typeId);
            formData.append('subject', subject);
            formData.append('body', body);
            formData.append('regarding_current_screen', this.admin2026SupportForm.regarding_current_screen ? '1' : '0');
            formData.append('screen_context_payload', String(this.admin2026SupportBoot.context_payload || ''));
            formData.append('screen_context_signature', String(this.admin2026SupportBoot.context_signature || ''));
            this.admin2026SupportAppendUploads(formData);

            try
            {
                const response = await this.axios.post(this.admin2026SupportBoot.store_url, formData, {
                    headers: { Accept: 'application/json' },
                });
                if (!response || !response.data || !response.data.success)
                {
                    throw new Error('The support ticket could not be submitted.');
                }

                this.admin2026SupportBoot.has_history = true;
                this.admin2026SupportSubmitting = false;
                this.closeAdmin2026SupportDrawer();
                this.showToastAdmin('Your support ticket was submitted.');
            }
            catch (error)
            {
                const errors = error && error.response && error.response.data ? error.response.data.errors : null;
                const firstError = errors && typeof errors === 'object'
                    ? Object.values(errors).flat().find((message) => !!message)
                    : null;
                this.admin2026SupportError = firstError
                    ? String(firstError)
                    : 'The support ticket could not be submitted. Please try again.';
                this.admin2026SupportSubmitting = false;
            }
        },

        // make preview asset
        previewAsset(id)
        {
            this.preview_asset = id;
            this.touchForm();
        },

        // remove assets
        removeAsset(id)
        {
            this.remove_assets = this.remove_assets + id + ',';
            this.touchForm();
        },

        // capture a thumbnail of a video
        captureThumbnail(video, index) {
            let canvas = document.getElementById(`video-thumbnail-${index}`);
            if (!canvas) return;

            let ctx = canvas.getContext("2d");

            // Ensure video seeks to a valid frame before capturing
            let videoDuration = video.duration;
            let captureTime = 0.5;

            video.currentTime = captureTime;

            video.addEventListener("seeked", function onSeeked() {
                requestAnimationFrame(() => {
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;

                    // Draw video frame
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                    // Draw semi-transparent dark overlay
                    ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    // Center position for the icon
                    let centerX = canvas.width / 2;
                    let centerY = canvas.height / 2;
                    let radius = Math.min(canvas.width, canvas.height) * 0.15; // 15% of the smallest dimension

                    // Draw semi-transparent black circle behind the icon
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI, false);
                    ctx.fillStyle = "rgba(0, 0, 0, 0.3)"; // Black with 30% opacity
                    ctx.fill();

                    // Adjustments for icon centering
                    let textOffsetX = radius * 0.1; // Shift slightly right
                    let textOffsetY = radius * 0.15; // Shift slightly down

                    // Draw Play Icon
                    ctx.fillStyle = "#ffffff"; // White color
                    ctx.font = `bold ${radius * 1.2}px sans-serif`; // Font size scaled to radius
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";

                    ctx.fillText("▶", centerX + textOffsetX, centerY + textOffsetY);
                });

                // Reset the video to start AFTER the frame is captured
                setTimeout(() => {
                    video.currentTime = 0;
                }, 50); // Small delay to ensure seek is processed

                video.removeEventListener("seeked", onSeeked);
            });
        },

        // play a video by clicking on the thumbnail
        playVideo(index) {
            let thumbnail = document.getElementById(`video-thumbnail-${index}`);
            let video = document.getElementById(`video-player-${index}`);

            if (thumbnail && video) {
                thumbnail.classList.add('hidden');
                video.classList.remove('hidden');
            }

            video.play();
        },

        isHeicUrl(url = '')
        {
            if (!url || typeof url !== 'string')
            {
                return false;
            }

            let decoded = url;
            try
            {
                decoded = decodeURIComponent(url);
            }
            catch (e)
            {
            }

            return /\.(heic|heif)($|\?)/i.test(decoded);
        },

        heicFallbackOnError(event, url, id, overlay=false, sourceUrl='')
        {
            if (!url)
            {
                return;
            }

            const target = event && event.target ? event.target : null;
            if (target && target.dataset && target.dataset.heicConverting === '1')
            {
                return;
            }

            if (target && target.dataset && target.dataset.heicFallbackTried === '1')
            {
                return;
            }

            if (target && target.dataset)
            {
                target.dataset.heicFallbackTried = '1';
            }

            this.heicToJpg(url,id,overlay,sourceUrl);
        },

        heicToJpg(url,id,overlay=false,sourceUrl='')
        {
            const target = document.getElementById(id);
            if (target && target.dataset && target.dataset.heicConverting === '1')
            {
                return url;
            }
            if (target && target.dataset)
            {
                target.dataset.heicConverting = '1';
            }

            if (overlay)
            {
                this.showOverlay();
            }
            else
            {
                this.showBottomOverlay();
            }

            fetch(sourceUrl || url)
                .then((res) => res.blob())
                .then((blob) =>
                    heic2any(
                        {
                            blob,
                            toType: "image/jpeg",
                            quality: 0.5,
                        }
                    )
                )
                .then((conversionResult) => {
                    const convertedBlob = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
                    if (!convertedBlob)
                    {
                        throw new Error('HEIC conversion returned no output blob');
                    }

                    var t = URL.createObjectURL(convertedBlob);
                    if (document.getElementById(id))
                    {
                        document.getElementById(id).src = t;
                        if (document.getElementById(id).dataset)
                        {
                            document.getElementById(id).dataset.heicConverting = '0';
                        }
                    }

                    if (overlay)
                    {
                        this.hideOverlay();
                    }
                    this.hideBottomOverlay();
                })
                .catch((e) => {
                    if (document.getElementById(id))
                    {
                        document.getElementById(id).src = url;
                        if (document.getElementById(id).dataset)
                        {
                            document.getElementById(id).dataset.heicConverting = '0';
                        }
                    }

                    if (overlay)
                    {
                        this.hideOverlay();
                    }
                    this.hideBottomOverlay();
                });

            return url;
        },

        // choose file for upload by clicking on file_upload input
        fileUploadChoose(n, scope)
        {
            // Embedded composers opt into their own form; existing callers use the document.
            const requestedScope = arguments.length > 1 ? scope : document;
            const uploadScope = Array.isArray(requestedScope) ? requestedScope[0] : requestedScope;
            if (!uploadScope || typeof uploadScope.querySelector !== 'function') return;

            for (let i=1; i<=20; i++)
            {
                if (!this['file_upload'+n+i])
                {
                    const input = uploadScope.querySelector('[id="file_upload'+n+i+'"]');
                    if (input) input.click();

                    return;
                }
            }
        },

        // add chosen file from file_upload input for upload
        fileUploadChooseAdd(e, n, touchform=1)
        {
            if (!e || !e.target || !e.target.files || !e.target.files.length) return;

            if (touchform)
            {
                this.touchForm();
            }

            for (let i=1; i<=20; i++)
            {
                // prevent adding double, as on some browsers when dragging/dropping file will have already been added in fileUploadDrop and fileUploadChooseAdd will additionally be triggered
                if (this.uploadAssets[n] && this.uploadAssets[n].includes(e.target.files))
                {
                    return;
                }

                else if (!this['file_upload'+n+i])
                {
                    if (!this.uploadAssets[n])
                    {
                        this.uploadAssets[n] = [];
                    }
                    this.uploadAssets[n].push(e.target.files);

                    this['file_upload'+n+i] = e.target.files;

                    this.uploadAssetsCount += 1;

                    if (typeof this.fileUploadAdded === 'function')
                    {
                        this.fileUploadAdded(e.target.files, n, {counted: true});
                    }

                    return;
                }
            }
        },

        fileUploadDragTargetActive(n)
        {
            return !!(
                this.fileUploadDragActive
                && typeof this.fileUploadDragActive === 'object'
                && this.fileUploadDragActive[n]
            );
        },

        fileUploadSetDragActive(n, active)
        {
            if (!this.fileUploadDragActive || typeof this.fileUploadDragActive !== 'object')
            {
                this.fileUploadDragActive = {};
            }

            this.fileUploadDragActive[n] = !!active;
        },

        fileUploadDragEnter(e, n)
        {
            this.fileUploadSetDragActive(n, true);
        },

        fileUploadDragOver(e, n)
        {
            if (!this.fileUploadDragTargetActive(n))
            {
                this.fileUploadSetDragActive(n, true);
            }
        },

        fileUploadDragLeave(e, n)
        {
            const currentTarget = e && e.currentTarget ? e.currentTarget : null;
            const relatedTarget = e && e.relatedTarget ? e.relatedTarget : null;

            if (currentTarget && relatedTarget && currentTarget.contains(relatedTarget))
            {
                return;
            }

            this.fileUploadSetDragActive(n, false);
        },

        // drop file for upload
        fileUploadDrop(e,n,accept,touchform=1)
        {
            this.fileUploadSetDragActive(n, false);

            // validate file types
            if (accept)
            {
                for (let file of e.dataTransfer.files)
                {
                    if (file)
                    {
                        if (accept == 'image/*' && !file.type.includes('image/'))
                        {
                            this.openModalWarning('ERROR','This type of file is not allowed. Please only upload images (for example, .jpg or .png files).');
                            return;
                        }
                        else if (accept == 'application/pdf' && !file.type.includes('pdf'))
                        {
                            this.openModalWarning('ERROR','This type of file is not allowed. Please only upload PDFs.');
                            return;
                        }
                        else if (accept == 'text/csv' && !file.type.includes('text') && !file.type.includes('csv'))
                        {
                            this.openModalWarning('ERROR','This type of file is not allowed. Please only upload text/csv files.');
                            return;
                        }
                    }
                }
            }

            if (touchform)
            {
                this.touchForm();
            }

            for (let i=1; i<=20; i++)
            {
                if (!this['file_upload'+n+i])
                {
                    if (!this.uploadAssets[n])
                    {
                        this.uploadAssets[n] = [];
                    }
                    this.uploadAssets[n].push(e.dataTransfer.files);

                    this['file_upload'+n+i] = e.dataTransfer.files;
                    this.$refs['file_upload'+n+i].files = e.dataTransfer.files;

                    if (typeof this.fileUploadAdded === 'function')
                    {
                        this.fileUploadAdded(e.dataTransfer.files, n, {counted: false});
                    }

                    return;
                }
            }
        },

        fileUploadIsImage(file)
        {
            if (!file)
            {
                return false;
            }

            const type = typeof file.type === 'string' ? file.type.toLowerCase() : '';
            if (type.indexOf('image/') === 0)
            {
                return true;
            }

            const name = typeof file.name === 'string' ? file.name.toLowerCase() : '';
            return /\.(apng|avif|bmp|gif|heic|heif|jpe?g|png|svg|webp)$/.test(name);
        },

        fileUploadPreviewUrl(file)
        {
            if (!this.fileUploadIsImage(file))
            {
                return '';
            }

            if (file.__ponoUploadPreviewUrl)
            {
                return file.__ponoUploadPreviewUrl;
            }

            if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function')
            {
                return '';
            }

            const previewUrl = URL.createObjectURL(file);

            try
            {
                Object.defineProperty(file, '__ponoUploadPreviewUrl', {
                    value: previewUrl,
                    configurable: true,
                });
            }
            catch (error)
            {
                file.__ponoUploadPreviewUrl = previewUrl;
            }

            return previewUrl;
        },

        fileUploadRevokePreviewUrl(file)
        {
            if (!file || !file.__ponoUploadPreviewUrl || typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function')
            {
                return;
            }

            URL.revokeObjectURL(file.__ponoUploadPreviewUrl);

            try
            {
                delete file.__ponoUploadPreviewUrl;
            }
            catch (error)
            {
                file.__ponoUploadPreviewUrl = '';
            }
        },

        // remove previously chosen or dropped file from upload
        fileUploadRemove(e,n,file,slot=null,position=null)
        {
            // Chat supplies the exact input/position so matching filenames remain independent.
            if (slot !== null || position !== null)
            {
                if (!Number.isInteger(slot) || slot < 1 || slot > 20 || !Number.isInteger(position) || position < 0) return;
                const files = this['file_upload'+n+slot];
                const key = n+'-'+slot+'-'+position;
                if (!files || files[position] !== file || this.uploadAssetsRemoved.includes(key)) return;

                this.uploadAssetsRemoved.push(key);
                this.fileUploadRevokePreviewUrl(file);
                if (!Array.from(files).some((item, index) => item && !this.uploadAssetsRemoved.includes(n+'-'+slot+'-'+index)))
                {
                    this.uploadAssetsCount = Math.max(0, this.uploadAssetsCount - 1);
                }
                this.$forceUpdate();
                return;
            }

            for (let i=1; i<=20; i++)
            {
                if (this['file_upload'+n+i])
                {
                    let pos = 0;
                    for (let item of this['file_upload'+n+i])
                    {
                        if (item && item.name == file.name && item.size == file.size)
                        {
                            this.uploadAssetsRemoved.push(n+'-'+i+'-'+pos);
                            this.fileUploadRevokePreviewUrl(item);

                            this.uploadAssetsCount -= 1;

                            this.$forceUpdate();
                        }

                        pos++;
                    }
                }

            }
        },

        // format a URL that is being inputted in a form
        formatURLinput(vmodel) {
            let url = this[vmodel].trim();

            // If it's an external link (http or https), allow ., :, /, and ?
            if (/^https?:\/\//i.test(url)) {
                // Lowercase the protocol and domain but keep safe URL characters
                url = url
                    .toLowerCase()
                    .replace(/[^a-z0-9-._~:/?#[\]@!$&'()*+,;=%]/gi, ''); // RFC 3986 safe chars
                this[vmodel] = url;
            }

            else {
                // Internal/slug style: allow letters, numbers, dash, slash, and dot
                url = url
                    .toLowerCase()
                    .replace(/[^a-z0-9-./\s]/g, '') // allow dot
                    .replace(/\s+/g, '-'); // spaces to dash
                this[vmodel] = url;
            }
        },

        // format Loom link
        formatLoomLink(upload='')
        {
            let link = this['addLoom'+upload];
            if (!link)
            {
                return;
            }

            this['addLoom'+upload] = '';

            if (link.includes('loom.com/share'))
            {
                let url = /loom.com\/share\/([a-z0-9]+)/i.exec(link);
                link = url ? url[1] : '';
            }

            this['uploadLooms'+upload].push(link);
        },

        // remove just added Loom video
        removeLoom(link, upload='')
        {
            let index = this['uploadLooms'+upload].indexOf(link);
            if (index > -1)
            {
                this['uploadLooms'+upload].splice(index, 1);
            }
        },

        // format youtube link
        formatYoutubeLink(upload='')
        {
            let link = this['addYoutube'+upload];
            if (!link)
            {
                return;
            }

            this['addYoutube'+upload] = '';

            if (link.includes('https://www.youtube.com/watch?v='))
            {
                link = link.replace('https://www.youtube.com/watch?v=', '');
                link = link.split('?')[0];
            }

            if (link.includes('youtube') || link.includes('youtu.be'))
            {
                // Remove query parameters
                link = link.split('?')[0];

                // handle YouTube Shorts format
                if (link.includes('/shorts/'))
                {
                    link = link.replace('/shorts/', '/watch?v=');
                }

                let url = link.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
                link = url[2] !== undefined ? url[2].split(/[^0-9a-z_\-]/i)[0] : url[0];
            }

            // check if embeddable
            /*
            try {
                this.axios.post('/assets/has-video-error', {'url': link}).then(res => {
                    if (!res['data'])
                    {
                    }
                });
            } catch (e) {}
            */

            this['uploadYoutubes'+upload].push(link);
        },

        // remove just added youtube video
        removeYoutube(link, upload='')
        {
            let index = this['uploadYoutubes'+upload].indexOf(link);
            if (index > -1)
            {
                this['uploadYoutubes'+upload].splice(index, 1);
            }
        },

        // format vimeo link
        formatVimeoLink(upload='')
        {
            let link = this['addVimeo'+upload];
            if (!link)
            {
                return;
            }

            this['addVimeo'+upload] = '';

            if (link.includes('vimeo'))
            {
                let url = /vimeo.*\/(\d+)/i.exec(link);
                link = url ? url[1] : '';
            }

            this['uploadVimeos'+upload].push(link);
        },

        // remove just added vimeo video
        removeVimeo(link, upload='')
        {
            let index = this['uploadVimeos'+upload].indexOf(link);
            if (index > -1)
            {
                this['uploadVimeos'+upload].splice(index, 1);
            }
        },

        // format wistia link
        formatWistiaLink(upload='')
        {
            let link = this['addWistia'+upload];
            if (!link)
            {
                return;
            }

            this['addWistia'+upload] = '';

            let t = /[^/]*$/.exec(link);
            if (t.length > 0)
            {
                link = t[0];
            }

            this['uploadWistias'+upload].push(link);
        },

        // remove just added wistia video
        removeWistia(link, upload='')
        {
            let index = this['uploadWistias'+upload].indexOf(link);
            if (index > -1)
            {
                this['uploadWistias'+upload].splice(index, 1);
            }
        },

        // update quill editor box content when saving
        // mp++ this needs to be implemented for all quill boxes
        updateQuill(id, target, scope=null)
        {
            const root = scope ? scope.querySelector('#' + CSS.escape(id)) : document.getElementById(id);
            if (target.includes('.'))
            {
                let t = target.split('.');
                let t1 = root;
                if (t1)
                {
                    const editor = t1.querySelector('.ql-editor');
                    if (!editor || !this[t[0]])
                    {
                        return;
                    }

                    this[t[0]][t[1]] = this.normalizeQuillHtml(editor.innerHTML);

                    if (this[t[0]][t[1]] == '<p><br></p>')
                    {
                        this[t[0]][t[1]] = null;
                    }
                }
            }
            else
            {
                let t = root;
                if (t)
                {
                    const editor = t.querySelector('.ql-editor');
                    if (!editor)
                    {
                        return;
                    }

                    this[target] = this.normalizeQuillHtml(editor.innerHTML);

                    if (this[target] == '<p><br></p>')
                    {
                        this[target] = null;
                    }
                }
            }
        },

        // increase value of +/- button in a form/journal field
        increasePlusMinusValue(name, min=0, max=0)
        {
            this.touchForm();

            if (!this.pForm[name] || isNaN(this.pForm[name]))
            {
                this.pForm[name] = parseInt(0);
            }

            if (max == 0 || parseInt(this.pForm[name]) < max)
            {
                this.pForm[name] = parseInt(this.pForm[name]) + 1;
            }
        },

        // decrease value of +/- button in a form/journal field
        decreasePlusMinusValue(name, min=0, max=0)
        {
            this.touchForm();

            if (!this.pForm[name] || isNaN(this.pForm[name]))
            {
                this.pForm[name] = parseInt(0);
            }

            if (parseInt(this.pForm[name]) > min)
            {
                this.pForm[name] = parseInt(this.pForm[name]) - 1;
            }
        },

        // increase value of +/- button in the admin via inputPlusminus
        increasePlusMinusValueAdmin(name, min=0, max=0, touchform=1)
        {
            if (touchform) { this.touchForm(); }

            if (!this[name] || isNaN(this[name]))
            {
                this[name] = parseInt(0);
            }

            if (max == 0 || parseInt(this[name]) < max)
            {
                this[name] = parseInt(this[name]) + 1;
            }
        },

        // decrease value of +/- button in the admin via inputPlusminus
        decreasePlusMinusValueAdmin(name, min=0, max=0, touchform=1)
        {
            if (touchform) { this.touchForm(); }

            if (!this[name] || isNaN(this[name]))
            {
                this[name] = parseInt(0);
            }

            if (parseInt(this[name]) > min)
            {
                this[name] = parseInt(this[name]) - 1;
            }
        },

        // check product option inventory
        checkProductOptionInventory(id)
        {
            this.axios.post('/product-option-inventory/'+id, {}).then(res => {
                if (res && res.data && res.data.success)
                {
                    this.productOptionTrackInventory = res.data.track_inventory ? true : false;
                    this.productOptionInventory = res.data.inventory;
                    this.productOptionInventoryLoaded = true;
                }
                else
                {
                    this.productOptionTrackInventory = false;
                    this.productOptionInventoryLoaded = false;
                }
            });
        },

        // show/hide password in password input boxes
        showHidePassword()
        {
            this.passwordInputVisible = !this.passwordInputVisible;
        },

        // create clickable links within text
        convertTextToClickableLinks(str)
        {
            var res, pattern1, pattern2, pattern3, pattern4;

            // match all http, https and ftp
            pattern1 = /(\b(https|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
            res = str.replace(pattern1, '<a class="link" href="$1" target="_blank">$1</a>');

            // match all www
            pattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim;
            res = res.replace(pattern2, '$1<a class="link" href="http://$2" target="_blank">$2</a>');

            // match all with just domain name and ending
            pattern3 = /(\s)([a-zA-Z0-9.-]+\.[a-zA-Z]{2,6})\b/gim;
            res = res.replace(pattern3, '$1<a class="link" href="http://$2" target="_blank">$2</a>');

            // match all email
            pattern4 = /(([a-zA-Z0-9\-\_\.])+@[a-zA-Z\_]+?(\.[a-zA-Z]{2,6})+)/gim;
            res = res.replace(pattern4, '<a class="link" href="mailto:$1">$1</a>');

            return res;
        },
    },
    mounted: function ()
    {
        // add event listeners
        try
        {
            const handler = (event) => {
                if (this.handleWebsiteLeadCaptureTriggerClick(event)) {
                    return;
                }

                if (this.handleProductLeadCaptureTriggerClick(event)) {
                    return;
                }

                if (this.handleProductFinderDocumentClick(event)) {
                    return;
                }

                // 1) Existing “open internal links in same tab” behaviour
                try {
                    if (this.linksOpenInSameTab) {
                        let el = event.target;
                        if (el.tagName === 'A' && !el.isContentEditable && (!el.host || el.host == window.location.host)) {
                            el.setAttribute('target', '');
                        } else if (
                            el.parentElement &&
                            el.parentElement.tagName === 'A' &&
                            !el.parentElement.isContentEditable &&
                            (!el.parentElement.host || el.parentElement.host == window.location.host)
                        ) {
                            el.parentElement.setAttribute('target', '');
                        }
                    }
                } catch (e) {}

                // 2) “click screenshot to open modal” behaviour for PonoAI sections
                this.handlePonoUpdateAssetClick(event);

                // 3) close the admin profile menu when clicking away from it
                this.handleAdminProfileMenuDocumentClick(event);
            };

            // save ref so we can remove it later if needed
            this._globalClickHandler = handler;

            document.addEventListener('click', handler, true);
        }
        catch (e) {}

        try
        {
            if (this.account2026ClientDashboardRoot())
            {
                this.hideOverlay();
            }

            const account2026NavigationClickHandler = (event) => {
                if (this.account2026NavigationClickShouldShowOverlay(event))
                {
                    this.showOverlay();
                }
            };

            const account2026NavigationSubmitHandler = (event) => {
                if (this.account2026NavigationSubmitShouldShowOverlay(event))
                {
                    this.showOverlay();
                }
            };

            this._account2026NavigationClickHandler = account2026NavigationClickHandler;
            this._account2026NavigationSubmitHandler = account2026NavigationSubmitHandler;

            document.addEventListener('click', account2026NavigationClickHandler);
            document.addEventListener('submit', account2026NavigationSubmitHandler);
        }
        catch (e) {}

        try
        {
            const admin2026NavigationClickHandler = (event) => {
                if (this.admin2026NavigationClickShouldShowOverlay(event))
                {
                    this.showOverlay();
                }
            };

            this._admin2026NavigationClickHandler = admin2026NavigationClickHandler;

            document.addEventListener('click', admin2026NavigationClickHandler);
        }
        catch (e) {}

        try
        {
            const keydownHandler = (event) => {
                if (this.handleModalConfirmKeydown(event)) {
                    return;
                }

                if (this.handleProductFinderCardKeydown(event)) {
                    return;
                }

                if (event.key === 'Escape' && this.productLeadCaptureModalOpen) {
                    event.preventDefault();
                    this.closeProductLeadCaptureModal();
                    return;
                }

                if (event.key === 'Escape' && this.websiteLeadCapturePopupOpen) {
                    event.preventDefault();
                    this.dismissWebsiteLeadCapture();
                    return;
                }

                if (event.key === 'Escape' && this.productFiltersDrawerOpenId) {
                    event.preventDefault();
                    this.closeProductFiltersDrawer();
                    return;
                }

                if (this.handleProductFinderKeydown(event)) {
                    return;
                }

                if (event.key === 'Escape' && this.isAdminPopoverOpen) {
                    event.preventDefault();
                    this.closeAdminProfileMenu(true);
                }
            };

            this._globalKeydownHandler = keydownHandler;

            document.addEventListener('keydown', keydownHandler);
        }
        catch (e) {}

        try
        {
            if (typeof window !== 'undefined' && !!window.vAdmin2026Chrome)
            {
                const dirtyFormHandler = (event) => {
                    const target = event && event.target ? event.target : null;

                    // Let the target input/change handlers update v-model before dirty UI renders.
                    window.setTimeout(() => {
                        this.admin2026MarkDirtyForm({ target });
                    }, 0);
                };

                this._admin2026DirtyFormHandler = dirtyFormHandler;
                document.addEventListener('input', dirtyFormHandler, true);
                document.addEventListener('change', dirtyFormHandler, true);
            }
        }
        catch (e) {}

        try
        {
            if (typeof window !== 'undefined')
            {
                const overlayBrowserHistoryRestoreHandler = (event) => {
                    this.hideOverlayForBrowserHistoryRestore(event);
                };

                this._overlayBrowserHistoryRestoreHandler = overlayBrowserHistoryRestoreHandler;
                window.addEventListener('pageshow', overlayBrowserHistoryRestoreHandler);
            }
        }
        catch (e) {}

        try
        {
            const productFinderDisclosureHandler = (event) => {
                let toggle = event.target && typeof event.target.querySelector === 'function'
                    ? event.target.querySelector('[data-product-finder-toggle]')
                    : null;

                if (toggle)
                {
                    toggle.setAttribute('aria-expanded', event.target.open ? 'true' : 'false');
                }
            };

            this._productFinderDisclosureHandler = productFinderDisclosureHandler;
            this._productFinderDisclosureElements = Array.from(document.querySelectorAll('[data-product-finder-disclosure]'));
            this._productFinderDisclosureElements.forEach((el) => {
                el.addEventListener('toggle', productFinderDisclosureHandler);
            });
        }
        catch (e) {}

        try
        {
            let productFinderRoots = this.productFinderRoots();
            productFinderRoots.forEach((root) => {
                this.initProductFinderRoot(root);
            });

            if (productFinderRoots.length && typeof window !== 'undefined')
            {
                const productFinderPopStateHandler = (event) => {
                    this.handleProductFinderPopState(event);
                };

                this._productFinderPopStateHandler = productFinderPopStateHandler;
                window.addEventListener('popstate', productFinderPopStateHandler);
            }
        }
        catch (e) {}

        this.syncAdminProfileMenuState();
        this.admin2026ApplySidebarState();
        this.account2026ScheduleInitialDrawerFocus();
        this.admin2026InstallDrawerFocusObserver();
        this.admin2026FocusMinimalChromeDrawerFrame();
        this.admin2026StartScreenOnboardingIfAvailable();
        this.admin2026ClientsLoadSubmenuBadges();
        this.websiteLeadCaptureInit();
        this.websiteLeadCaptureInitInlineForms();

        if (typeof window !== 'undefined' && window.vAdmin2026SupervisorMode && window.vAdmin2026SupervisorMode.open)
        {
            if (!this.admin2026RunAfterScreenOnboarding(() => {
                this.openAdmin2026SupervisorModeDrawer();
            }))
            {
                this.openAdmin2026SupervisorModeDrawer();
            }
        }

        // load keep in touch and follow-ups
        try
        {
            const isAdmin2026Chrome = typeof window !== 'undefined' && !!window.vAdmin2026Chrome;

            if (!isAdmin2026Chrome && location.href.includes('/pono-admin'))
            {
                const pathname = window.location.pathname.replace(/\/+$/, '');
                const isAdminDashboard = pathname === '/pono-admin' || pathname === '/pono-admin/dashboard';

                if (isAdminDashboard)
                {
                    this.axios.post('/pono-admin/dashboard/keep-in-touch-count').then(res => {
                        if (res && res.data && res.data.success)
                        {
                            this.keepInTouchCount = res.data.count;
                        }
                    });

                    this.axios.post('/pono-admin/dashboard/keep-in-touch-overdue-count').then(res => {
                        if (res && res.data && res.data.success)
                        {
                            this.keepInTouchOverdueCount = res.data.count;
                        }
                    });

                    this.axios.post('/pono-admin/dashboard/follow-ups-count').then(res => {
                        if (res && res.data && res.data.success)
                        {
                            this.followUps = res.data.rows;
                        }
                    });
                }

            }
        }
        catch (e) {}
    },

    beforeUnmount() {
        if (this.toastAdminTimeoutId) {
            clearTimeout(this.toastAdminTimeoutId);
            this.toastAdminTimeoutId = null;
        }

        if (this._globalClickHandler) {
            document.removeEventListener('click', this._globalClickHandler, true);
        }

        if (this._account2026NavigationClickHandler) {
            document.removeEventListener('click', this._account2026NavigationClickHandler);
            this._account2026NavigationClickHandler = null;
        }

        if (this._account2026NavigationSubmitHandler) {
            document.removeEventListener('submit', this._account2026NavigationSubmitHandler);
            this._account2026NavigationSubmitHandler = null;
        }

        if (this._admin2026NavigationClickHandler) {
            document.removeEventListener('click', this._admin2026NavigationClickHandler);
            this._admin2026NavigationClickHandler = null;
        }

        this.account2026RemoveSystemAppearanceListener();

        if (this._globalKeydownHandler) {
            document.removeEventListener('keydown', this._globalKeydownHandler);
        }

        if (this._admin2026DirtyFormHandler) {
            document.removeEventListener('input', this._admin2026DirtyFormHandler, true);
            document.removeEventListener('change', this._admin2026DirtyFormHandler, true);
        }

        if (this._overlayBrowserHistoryRestoreHandler && typeof window !== 'undefined') {
            window.removeEventListener('pageshow', this._overlayBrowserHistoryRestoreHandler);
            this._overlayBrowserHistoryRestoreHandler = null;
        }

        if (this._productFinderDisclosureHandler && this._productFinderDisclosureElements) {
            this._productFinderDisclosureElements.forEach((el) => {
                el.removeEventListener('toggle', this._productFinderDisclosureHandler);
            });
            this._productFinderDisclosureElements = null;
        }

        if (this._productFinderPopStateHandler && typeof window !== 'undefined') {
            window.removeEventListener('popstate', this._productFinderPopStateHandler);
            this._productFinderPopStateHandler = null;
        }

        this.websiteLeadCaptureCleanupTriggers();

        this.websiteLeadCaptureUnwatchViewport();

        if (this._admin2026DrawerFocusObserver) {
            this._admin2026DrawerFocusObserver.disconnect();
            this._admin2026DrawerFocusObserver = null;
        }

        if (this._admin2026DrawerFocusTimer) {
            clearTimeout(this._admin2026DrawerFocusTimer);
            this._admin2026DrawerFocusTimer = null;
        }

        this.detachModalNotificationsDrawerListeners();
        this.detachModalPonoAIDrawerListeners();
        this.admin2026DetachScreenOnboardingListeners();

        if (this.productFiltersDrawerOpenId || this.productLeadCaptureModalOpen || this.websiteLeadCapturePopupOpen || this.websiteLeadCaptureSuccessPopupOpen) {
            document.documentElement.classList.remove('overflow-hidden');
        }
    },
    
    created: function ()
    {
        this.modalOpen['modal-flash-error'] = true;
        this.modalOpen['modal-flash-success'] = true;

        try
        {
            if (window.localStorage)
            {
                this.admin2026CategoryNamesVisible = window.localStorage.getItem('ponoAdmin2026CategoryNamesVisible') === '1';
            }
        }
        catch (e) {}

        // set dark mode
        try
        {
            if (localStorage && localStorage.ponoTheme === 'verylight')
            {
                this.darkMode = 1;
            }
            else if (localStorage && localStorage.ponoTheme === 'light')
            {
                this.darkMode = 2;
            }
            else if (localStorage && localStorage.ponoTheme === 'blue')
            {
                this.darkMode = 3;
            }
            else if (localStorage && localStorage.ponoTheme === 'dark')
            {
                this.darkMode = 4;
            }
            else
            {
                this.darkMode = 5;
            }
        }
        catch (e) {}

        this.account2026InitializeAppearance();

        try
        {
            if (typeof window !== 'undefined' && window.vAdmin2026FlashSuccess) {
                const timeout = Number(window.vAdmin2026FlashSuccessTimeout);

                this.showToastAdmin(
                    window.vAdmin2026FlashSuccess,
                    'check',
                    Number.isFinite(timeout) && timeout > 0 ? timeout : 10000
                );
            }
        }
        catch (e) {}

        try
        {
            const hiddenFeatureModal = typeof window !== 'undefined'
                && window.vAdmin2026HideFeatureConfirmationModal
                && typeof window.vAdmin2026HideFeatureConfirmationModal === 'object'
                    ? window.vAdmin2026HideFeatureConfirmationModal
                    : null;

            if (hiddenFeatureModal && hiddenFeatureModal.content) {
                this.openModalOk(
                    hiddenFeatureModal.title || 'Feature hidden',
                    hiddenFeatureModal.content,
                    hiddenFeatureModal.button || 'OK'
                );
            }
        }
        catch (e) {}

        this.vueAppLoading = false;

        window.addEventListener('scroll', this.scrollListener);
    },
    destroyed: function ()
    {
        window.removeEventListener('scroll', this.scrollListener);
    },
});

// add Tailwind components, rename some "H" for usage in Laravel screens
if (typeof window !== 'undefined' && window.admin2026ClientAccountChatRailComponent) {
    const chatRailComponent = window.admin2026ClientAccountChatRailComponent;
    const rootMethodProxies = Object.fromEntries(
        Object.keys(app._component.methods || {}).map((name) => [name, function (...args) {
            return this.$root[name](...args);
        }])
    );
    const chatRailUploadMethods = Object.fromEntries(
        ['fileUploadChoose', 'fileUploadChooseAdd', 'fileUploadRemove', 'fileUploadRevokePreviewUrl']
            .map(name => [name, app._component.methods[name]])
    );
    let resolvedChatRailComponent = null;
    let pendingChatRailComponent = null;

    app.component('admin2026-client-account-chat-rail', {
        data() {
            return {resolvedComponent: resolvedChatRailComponent, loading: false, error: false};
        },
        methods: {
            loadChatRail() {
                if (this.loading) return;
                this.loading = true;
                this.error = false;
                pendingChatRailComponent ||= axios.get(chatRailComponent.templateUrl, {
                    headers: {'Accept': 'application/json'},
                }).then(response => {
                    const payload = response && response.data ? response.data : {};
                    if (!payload.success || typeof payload.template !== 'string' || !payload.template) {
                        throw new Error('Chat rail template unavailable');
                    }
                    resolvedChatRailComponent = markRaw({
                        ...chatRailComponent,
                        template: payload.template,
                        methods: { ...rootMethodProxies, ...runtimeAppMixins.methods, ...chatRailUploadMethods, ...chatRailComponent.methods },
                    });
                    return resolvedChatRailComponent;
                }).catch(error => {
                    pendingChatRailComponent = null;
                    throw error;
                });
                pendingChatRailComponent.then(component => {
                    this.resolvedComponent = component;
                }).catch(() => {
                    this.error = true;
                }).finally(() => {
                    this.loading = false;
                });
            },
        },
        mounted() {
            if (!this.resolvedComponent) this.loadChatRail();
        },
        render() {
            if (this.resolvedComponent) return h(this.resolvedComponent);
            if (this.error) return h('div', {class: 'mt-3 text-sm text-amber-700 dark:text-amber-300', role: 'alert'}, [
                'Chat could not be loaded. ',
                h('button', {type: 'button', class: 'admin2026-client-account-toolbar-action cursor-pointer', onClick: this.loadChatRail}, 'Try again'),
            ]);
            return h('div', {class: 'mt-3 text-sm text-slate-500 dark:text-slate-400', role: 'status'}, 'Loading chat...');
        },
    });
}
app.component('admin2026-expandable-text', TrainingPlanQuickPreviewNote);
app.component('disclosure', Disclosure);
app.component('disclosurebutton', DisclosureButton);
app.component('disclosurepanel', DisclosurePanel);
app.component('hdialog', HDialog);
app.component('hdialogoverlay', HDialogOverlay);
app.component('hdialogpanel', HDialogPanel);
app.component('hdialogtitle', HDialogTitle);
app.component('hdialogdescription', HDialogDescription);
app.component('listbox', Listbox);
app.component('listboxbutton', ListboxButton);
app.component('listboxlabel', ListboxLabel);
app.component('listboxoption', ListboxOption);
app.component('listboxoptions', ListboxOptions);
app.component('hmenu', HMenu);
app.component('hmenubutton', HMenuButton);
app.component('hmenuitems', HMenuItems);
app.component('hmenuitem', HMenuItem);
app.component('popover', Popover);
app.component('popoverbutton', PopoverButton);
app.component('popovergroup', PopoverGroup);
app.component('popoverpanel', PopoverPanel);
app.component('radiogroup', RadioGroup);
app.component('radiogroupdescription', RadioGroupDescription);
app.component('radiogrouplabel', RadioGroupLabel);
app.component('radiogroupoption', RadioGroupOption);
app.component('hswitch', HSwitch);
app.component('hswitchgroup', HSwitchGroup);
app.component('hswitchlabel', HSwitchLabel);
app.component('tabgroup', TabGroup);
app.component('tablist', TabList);
app.component('tab', Tab);
app.component('tabpanels', TabPanels);
app.component('tabpanel', TabPanel);
app.component('transitionroot', TransitionRoot);
app.component('transitionchild', TransitionChild);

// Help drawer: recursive navigation tree node
app.component('helpnavnode', {
    name: 'HelpNavNode',
    props: {
        node: { type: Object, required: true },
        depth: { type: Number, default: 0 },
        selectedHelpId: { type: String, default: '' },
        parentLabel: { type: String, default: '' },
        rootKey: { type: String, default: '' },
        openRootKey: { type: String, default: '' },
        disableUnavailableBranches: { type: Boolean, default: false },
    },
    emits: ['select', 'toggle-root'],
    computed: {
        hasChildren() {
            return !!(this.node && !(this.disableUnavailableBranches && this.node.disabled)
                && Array.isArray(this.node.children) && this.node.children.length);
        },
        isRoot() {
            const depth = Number.isInteger(this.depth) ? this.depth : 0;
            return depth === 0;
        },
        isRootOpen() {
            return !!(this.isRoot && this.hasChildren && this.rootKey && this.openRootKey === this.rootKey);
        },
        // Allow opening articles even when the node has children.
        // (Missing / placeholder nodes will also have help_id.)
        canOpenArticle() {
            return !!(this.node && this.node.help_id && !this.node.disabled);
        },
        isSelected() {
            return !!(this.canOpenArticle && this.selectedHelpId === this.node.help_id);
        },
        isActiveBranch() {
            const selected = (this.selectedHelpId || '').toString();
            if (!selected || !this.node) {
                return false;
            }

            return this.nodeContainsHelpId(this.node, selected);
        },
        rawLabel() {
            if (!this.node) return 'Untitled';
            const nodeLabel = this.node.label !== null && this.node.label !== undefined
                ? this.node.label.toString()
                : '';

            if (nodeLabel.trim().length) {
                return nodeLabel;
            }

            if (this.node.help_id) {
                return this.node.help_id.toString();
            }

            return 'Untitled';
        },
        label() {
            const currentLabel = this.rawLabel;
            const parentLabel = (this.parentLabel || '').toString();
            const prefix = parentLabel ? `${parentLabel} - ` : '';

            if (prefix && currentLabel.startsWith(prefix)) {
                const trimmed = currentLabel.slice(prefix.length).trim();
                if (trimmed.length) {
                    return trimmed;
                }
            }

            return currentLabel;
        },
        rowStyle() {
            const depth = Number.isInteger(this.depth) ? this.depth : 0;
            return {
                paddingLeft: `${Math.max(0, depth) * 14}px`,
            };
        },
        missingNoteStyle() {
            const depth = Number.isInteger(this.depth) ? this.depth : 0;
            return {
                paddingLeft: `${Math.max(0, depth) * 14 + 24}px`,
            };
        },
        buttonClasses() {
            if (this.node && this.node.disabled) {
                return 'border-transparent text-slate-400 dark:text-slate-500';
            }

            if (this.isSelected) {
                return 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 font-semibold';
            }

            // If you keep missing_help flags in local/dev, still style them,
            // but DO NOT make them look disabled (they will open the placeholder).
            if (this.node && this.node.missing_help) {
                return 'border-amber-100 dark:border-amber-900/40 text-amber-700 dark:text-amber-300 hover:bg-amber-50/40 dark:hover:bg-amber-900/10';
            }

            return 'border-transparent hover:bg-white dark:hover:bg-slate-800 hover:border-slate-200 dark:hover:border-slate-700 text-slate-700 dark:text-slate-200';
        },
    },
    methods: {
        onGroupClick() {
            // Clicking a parent node should still expand/collapse as before,
            // AND also open the parent article if it exists.
            if (this.canOpenArticle) {
                this.$emit('select', this.node.help_id);
            }

            if (this.isRoot && this.hasChildren) {
                this.$emit('toggle-root', this.rootKey);
            }
        },
        onLeafClick() {
            if (this.canOpenArticle) {
                this.$emit('select', this.node.help_id);
            }
        },
        nodeContainsHelpId(node, helpId) {
            if (!node || !helpId) {
                return false;
            }

            const nodeHelpId = node.help_id ? node.help_id.toString() : '';
            if (nodeHelpId === helpId) {
                return true;
            }

            const children = Array.isArray(node.children) ? node.children : [];
            return children.some((child) => this.nodeContainsHelpId(child, helpId));
        },
    },
    template: `
        <div class="space-y-0.5">
            <template v-if="hasChildren && isRoot">
                <div :style="rowStyle">
                    <button type="button"
                            class="w-full cursor-pointer flex items-center justify-between gap-2 rounded-lg border px-2 py-1.5 text-left text-sm transition"
                            :class="buttonClasses"
                            @click="onGroupClick">
                        <span class="flex min-w-0 items-center gap-2"><i v-if="node.icon" :class="node.icon" class="w-4 shrink-0 text-center" aria-hidden="true"></i><span class="truncate">{{ label }}</span></span>
                        <span class="shrink-0 inline-flex items-center gap-1">
                            <i v-if="node && node.missing_help" class="fa-solid fa-triangle-exclamation text-[10px] text-amber-500 dark:text-amber-400"></i>
                            <i :class="isRootOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="text-[11px]"></i>
                        </span>
                    </button>
                </div>

                <div v-if="node && node.missing_help"
                     :style="missingNoteStyle"
                     class="text-[11px] text-amber-700 dark:text-amber-400 pb-1">
                    Missing help mapping
                </div>

                <div v-if="isRootOpen" class="space-y-0.5">
                    <helpnavnode v-for="(child, childIndex) in node.children"
                                 :key="'help-nav-child-'+depth+'-'+childIndex+'-'+(child.help_id || child.label || 'node')"
                                 :node="child"
                                 :depth="depth + 1"
                                 :selected-help-id="selectedHelpId"
                                 :parent-label="rawLabel"
                                 :root-key="rootKey"
                                 :open-root-key="openRootKey"
                                 :disable-unavailable-branches="disableUnavailableBranches"
                                 @select="$emit('select', $event)"
                                 @toggle-root="$emit('toggle-root', $event)">
                    </helpnavnode>
                </div>
            </template>

            <disclosure v-else-if="hasChildren"
                        as="div"
                        :defaultOpen="isActiveBranch"
                        :key="'help-nav-disclosure-'+depth+'-'+(node.help_id || rawLabel)+'-'+selectedHelpId">
                <template #default="{ open }">
                    <div :style="rowStyle">
                        <disclosurebutton type="button"
                                          class="w-full cursor-pointer flex items-center justify-between gap-2 rounded-lg border px-2 py-1.5 text-left text-sm transition"
                                          :class="buttonClasses"
                                          @click="onGroupClick">
                            <span class="flex min-w-0 items-center gap-2"><i v-if="node.icon" :class="node.icon" class="w-4 shrink-0 text-center" aria-hidden="true"></i><span class="truncate">{{ label }}</span></span>
                            <span class="shrink-0 inline-flex items-center gap-1">
                                <i v-if="node && node.missing_help" class="fa-solid fa-triangle-exclamation text-[10px] text-amber-500 dark:text-amber-400"></i>
                                <i :class="open ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" class="text-[11px]"></i>
                            </span>
                        </disclosurebutton>
                    </div>

                    <div v-if="node && node.missing_help"
                         :style="missingNoteStyle"
                         class="text-[11px] text-amber-700 dark:text-amber-400 pb-1">
                        Missing help mapping
                    </div>

                    <disclosurepanel as="div" class="space-y-0.5">
                        <helpnavnode v-for="(child, childIndex) in node.children"
                                     :key="'help-nav-child-'+depth+'-'+childIndex+'-'+(child.help_id || child.label || 'node')"
                                     :node="child"
                                     :depth="depth + 1"
                                     :selected-help-id="selectedHelpId"
                                     :parent-label="rawLabel"
                                     :root-key="rootKey"
                                     :open-root-key="openRootKey"
                                     :disable-unavailable-branches="disableUnavailableBranches"
                                     @select="$emit('select', $event)"
                                     @toggle-root="$emit('toggle-root', $event)">
                        </helpnavnode>
                    </disclosurepanel>
                </template>
            </disclosure>

            <div v-else :style="rowStyle">
                <button type="button"
                        class="w-full flex items-center justify-between gap-2 rounded-lg border px-2 py-1.5 text-left text-sm transition"
                        :class="[buttonClasses, canOpenArticle ? 'cursor-pointer' : (disableUnavailableBranches ? 'cursor-not-allowed' : 'cursor-default')]"
                        :disabled="!canOpenArticle"
                        :aria-disabled="!canOpenArticle ? 'true' : null"
                        @click="onLeafClick">
                    <span class="flex min-w-0 items-center gap-2"><i v-if="node.icon" :class="node.icon" class="w-4 shrink-0 text-center" aria-hidden="true"></i><span class="truncate">{{ label }}</span></span>
                    <span class="shrink-0 inline-flex items-center gap-1">
                        <i v-if="node && node.missing_help" class="fa-solid fa-triangle-exclamation text-[10px] text-amber-500 dark:text-amber-400"></i>
                    </span>
                </button>

                <div v-if="node && node.missing_help"
                     :style="missingNoteStyle"
                     class="text-[11px] text-amber-700 dark:text-amber-400 pb-1">
                    Missing help mapping
                </div>
            </div>
        </div>
    `,
});

registerClientAccountGoalDrawer(app);
registerMessageReplyComposer(app);

// add other components
app.component('ref', ref);
app.component('giphy-js-sdk-core', GphApiClient);
app.component('litepie-datepicker', LitepieDatepicker);
app.component('pono-litepie-datepicker', PonoLitepieDatepicker);
app.component('training-plan-quick-preview', TrainingPlanQuickPreview);
app.component('client-progress-report', ClientProgressReport);
app.component('quilleditor', QuillEditor);
app.component('vccarousel', VCCarousel)
app.component('vcslide', VCSlide)
app.component('vcpagination', VCPagination)
app.component('vcnavigation', VCNavigation)

// Compile only the trusted, server-rendered Admin 2026 message reader fragment.
// Vue does not compile directives inside v-html, which would disable asset previews.
const admin2026MessageReaderMounts = new WeakMap();
const admin2026MessageReaderMethods = [
    'openModalAsset1', 'openModalAsset11', 'openModalAsset13', 'openModalAsset14',
    'openModalAsset15', 'openModalAsset16', 'openModalAsset17', 'openLink',
    'copyToClipboard', 'copyText', 'isHeicUrl', 'heicToJpg', 'heicFallbackOnError',
];

function updateAdmin2026MessageReader(host, html, parent) {
    const previous = admin2026MessageReaderMounts.get(host);
    if (previous && previous.html === html) return;
    if (previous) previous.app.unmount();
    admin2026MessageReaderMounts.delete(host);
    if (!html) {
        host.replaceChildren();
        return;
    }

    const methods = {
        previewAsset(id) { this.preview_asset = id; },
    };
    admin2026MessageReaderMethods.forEach(name => {
        methods[name] = (...args) => parent?.[name]?.(...args);
    });
    const readerApp = createApp({
        template: `<div>${html}</div>`,
        data: () => ({ preview_asset: 0 }),
        methods,
    });
    Object.entries(app._context.components).forEach(([name, component]) => readerApp.component(name, component));
    readerApp.mount(host);
    admin2026MessageReaderMounts.set(host, { app: readerApp, html });
}

app.directive('admin2026-floating-menus', admin2026FloatingMenus);

app.directive('admin2026-message-reader', {
    mounted(host, binding) {
        updateAdmin2026MessageReader(host, binding.value, binding.instance);
    },
    updated(host, binding) {
        updateAdmin2026MessageReader(host, binding.value, binding.instance);
    },
    beforeUnmount(host) {
        admin2026MessageReaderMounts.get(host)?.app.unmount();
        admin2026MessageReaderMounts.delete(host);
    },
});

// Mount trusted Admin 2026 tab surfaces with their normal Vue controls after lazy loading.
const admin2026SnapshotSurfaceMounts = new WeakMap();

function updateAdmin2026SnapshotSurface(host, value, parent) {
    const key = String(value?.key || '').trim();
    const html = String(value?.html || '');
    const url = String(value?.url || '');
    const previous = admin2026SnapshotSurfaceMounts.get(host);
    if (previous && previous.html === html && previous.url === url) return;
    if (previous) previous.app.unmount();
    admin2026SnapshotSurfaceMounts.delete(host);
    if (!html || !parent) {
        host.replaceChildren();
        return;
    }

    const methods = {};
    Object.keys(parent.$options?.methods || {}).forEach(name => {
        methods[name] = (...args) => parent[name]?.(...args);
    });
    methods.submitForm = function (ref = 'form') {
        const form = this.$refs?.[ref];
        const target = Array.isArray(form) ? form[0] : form;
        if (target?.checkValidity && !target.checkValidity()) return;
        parent.showOverlay?.();
        target?.submit?.();
    };
    methods.hideAdminFilter = function (targetUrl) {
        parent.adminListFilterVisible = false;
        parent.loadCoachingSnapshotLazyTab?.(key, targetUrl);
    };
    if (key === 'messages') {
        methods.listUrl = function (options = {}) {
            const target = new URL(url || window.location.href, window.location.href);
            if (options.sort) target.searchParams.set('fsort', String(options.sort));
            if (options.direction) target.searchParams.set('fdirection', String(options.direction));
            target.searchParams.delete('page');
            return target.toString();
        };
    }
    if (key === 'check-ins-pagination') {
        methods.listUrl = function (options = {}) {
            const target = new URL(parent.listUrl(options), window.location.href);
            target.search = '';
            Object.entries(parent.admin2026CheckInsBuildQuery(options, ['page'])).forEach(([name, value]) => {
                if (value !== null && value !== undefined && value !== '') target.searchParams.set(name, String(value));
            });
            return target.toString();
        };
    }

    const surfaceApp = createApp({
        template: `<div>${html}</div>`,
        setup: () => parent.$data,
        methods,
    });
    Object.entries(app._context.components).forEach(([name, component]) => surfaceApp.component(name, component));
    surfaceApp.mount(host);
    admin2026SnapshotSurfaceMounts.set(host, { app: surfaceApp, html, url });
}

window.mountPonoAdmin2026Pagination = (scope, parent) => {
    scope?.querySelectorAll('[data-admin2026-check-ins-entry-pagination]').forEach(host => {
        // Compile only the trusted shared paginator, never the surrounding journal answers.
        if (!host.querySelector('hmenu')) return;
        updateAdmin2026SnapshotSurface(host, { key: 'check-ins-pagination', html: host.innerHTML }, parent);
    });
};

window.unmountPonoAdmin2026Pagination = scope => {
    scope?.querySelectorAll('[data-admin2026-check-ins-entry-pagination]').forEach(host => {
        admin2026SnapshotSurfaceMounts.get(host)?.app.unmount();
        admin2026SnapshotSurfaceMounts.delete(host);
    });
};

app.directive('admin2026-snapshot-surface', {
    mounted(host, binding) {
        updateAdmin2026SnapshotSurface(host, binding.value, binding.instance);
    },
    updated(host, binding) {
        updateAdmin2026SnapshotSurface(host, binding.value, binding.instance);
    },
    beforeUnmount(host) {
        admin2026SnapshotSurfaceMounts.get(host)?.app.unmount();
        admin2026SnapshotSurfaceMounts.delete(host);
    },
});

const ponoDynamicDateInputs = new WeakMap();

window.getPonoDynamicDateInput = (host) => host ? ponoDynamicDateInputs.get(host) || null : null;

window.mountPonoDynamicDateInput = (host, options = {}) => {
    const mountedController = window.getPonoDynamicDateInput(host);

    if (!host || mountedController) {
        return mountedController;
    }

    const journalId = Number.parseInt(options.journalId, 10) || 0;
    const initialValue = String(options.initialValue || '').trim();
    const dynamicApp = createApp({
        data() {
            return {
                admin2026CheckInsPhotoDates: {
                    [journalId]: initialValue,
                },
                dateFormatter: { date: 'DD MMM YYYY', month: 'MMM' },
                dateShortcuts: () => [],
                photoDateReady: false,
                photoDateSyncing: false,
                photoDateLastEmittedValue: initialValue,
            };
        },
        mounted() {
            this.$nextTick(() => {
                this.photoDateReady = true;
            });
        },
        watch: {
            admin2026CheckInsPhotoDates: {
                deep: true,
                handler() {
                    this.notifyPhotoDateChanged(journalId);
                },
            },
        },
        methods: {
            openDatepicker(name) {
                const picker = this.$refs[name];

                if (picker) {
                    picker.isShow = !picker.isShow;
                }
            },
            photoComparisonPhotoDateChanged(changedJournalId) {
                this.notifyPhotoDateChanged(changedJournalId);
            },
            notifyPhotoDateChanged(changedJournalId) {
                const value = String(this.admin2026CheckInsPhotoDates[changedJournalId] || '').trim();

                if (
                    !this.photoDateReady
                    || this.photoDateSyncing
                    || value === this.photoDateLastEmittedValue
                    || typeof options.onChange !== 'function'
                ) {
                    return;
                }

                this.photoDateLastEmittedValue = value;
                options.onChange(value);
            },
        },
    });

    dynamicApp.component('litepie-datepicker', LitepieDatepicker);
    const instance = dynamicApp.mount(host);
    const controller = {
        setValue(value) {
            const nextValue = String(value || '').trim();

            if (String(instance.admin2026CheckInsPhotoDates[journalId] || '') === nextValue) {
                return;
            }

            instance.photoDateSyncing = true;
            instance.photoDateLastEmittedValue = nextValue;
            instance.admin2026CheckInsPhotoDates[journalId] = nextValue;
            instance.$nextTick(() => {
                instance.photoDateSyncing = false;
            });
        },
        unmount() {
            dynamicApp.unmount();
            ponoDynamicDateInputs.delete(host);
        },
    };

    ponoDynamicDateInputs.set(host, controller);

    return controller;
};

// add ability for styles in Blade templates
app.component('vstyle',
{
    render: function ()
    {
        return h('style', this.$slots.default())
    }
});

// configs
app.config.devtools = false;
app.config.productionTip = false;
app.config.globalProperties.axios = axios;

// Hook Vue errors into the global reporter installed by the layout
app.config.errorHandler = (err, instance, info) => {
  if (window.ponoReportError) window.ponoReportError(err, { info, component: instance?.$?.type?.name });
};

function initializeWebsiteResourceEmbeds() {
    const resourceParams = new URLSearchParams(window.location.search);
    const requestedResourceSlug = resourceParams.get('id');
    const requestedResourceId = resourceParams.has('id') ? null : resourceParams.get('resource');
    let requestedResourceOpened = false;
    document.querySelectorAll('[data-resource-embed]').forEach((root) => {
        if (root.dataset.resourceEmbedReady === 'true') return;

        root.dataset.resourceEmbedReady = 'true';
        const search = root.querySelector('[data-resource-search]');
        const searchButton = root.querySelector('[data-resource-search-submit]');
        const filters = Array.from(root.querySelectorAll('[data-resource-category]'));
        const cards = Array.from(root.querySelectorAll('[data-resource-card]'));
        const empty = root.querySelector('[data-resource-empty]');
        const modalOpeners = Array.from(root.querySelectorAll('[data-resource-open-modal]'));
        const modals = Array.from(root.querySelectorAll('[data-resource-modal]'));
        const unlockOpeners = Array.from(root.querySelectorAll('[data-resource-open-unlock]'));
        const unlockModal = root.querySelector('[data-resource-unlock-modal]');
        const unlockConfirm = unlockModal ? unlockModal.querySelector('[data-resource-unlock-confirm]') : null;
        const pdfModal = root.querySelector('[data-resource-pdf-modal]');
        const pdfOpeners = Array.from(root.querySelectorAll('[data-resource-open-pdf]'));
        const imageOpeners = Array.from(root.querySelectorAll('[data-resource-open-image]'));
        const pdfObject = pdfModal ? pdfModal.querySelector('[data-resource-pdf-object]') : null;
        const imagePreview = pdfModal ? pdfModal.querySelector('[data-resource-image-preview]') : null;
        const pdfTitle = pdfModal ? pdfModal.querySelector('[data-resource-pdf-title]') : null;
        const pdfOpenNew = pdfModal ? pdfModal.querySelector('[data-resource-pdf-open-new]') : null;
        const pdfDownload = pdfModal ? pdfModal.querySelector('[data-resource-pdf-download]') : null;
        const pdfFallback = pdfModal ? pdfModal.querySelector('[data-resource-pdf-fallback]') : null;
        const searchUrl = String(root.dataset.resourceSearchUrl || '');
        const searchSectionId = Number(root.dataset.resourceSearchSection || root.dataset.resourceEmbed || 0);
        const searchStatus = root.querySelector('[data-resource-search-status]');
        const aiAnswer = root.querySelector('[data-resource-ai-answer]');
        const aiTitle = root.querySelector('[data-resource-ai-title]');
        const aiCopy = root.querySelector('[data-resource-ai-copy]');
        const aiRestrictedPreview = root.querySelector('[data-resource-ai-restricted-preview]');
        const aiRestrictedAction = root.querySelector('[data-resource-ai-restricted-action]');
        const aiChecklist = root.querySelector('[data-resource-ai-checklist]');
        const aiSource = root.querySelector('[data-resource-ai-source]');
        let selectedFilter = 'all';
        let semanticResourceIds = null;
        let searchRequestSequence = 0;
        let aiPending = false;

        const syncSearchButton = () => {
            if (!searchButton || !search) return;

            const question = String(search.value || '').trim();
            searchButton.hidden = question.length === 0;
            searchButton.disabled = question.length < 3 || aiPending;
        };

        const setSearchStatus = (message) => {
            if (!searchStatus) return;
            searchStatus.textContent = String(message || '');
            searchStatus.hidden = !message;
        };

        const clearAiAnswer = () => {
            if (aiAnswer) {
                aiAnswer.hidden = true;
                delete aiAnswer.dataset.resourceRestricted;
            }
            if (aiTitle) aiTitle.textContent = '';
            if (aiCopy) aiCopy.textContent = '';
            if (aiRestrictedPreview) aiRestrictedPreview.hidden = true;
            if (aiRestrictedAction) aiRestrictedAction.hidden = true;
            if (aiChecklist) {
                aiChecklist.replaceChildren();
                aiChecklist.hidden = true;
            }
            if (aiSource) {
                aiSource.textContent = '';
                aiSource.hidden = true;
            }
        };

        const renderAiAnswer = (answer) => {
            if (!aiAnswer || !answer || !answer.ready) {
                clearAiAnswer();
                return;
            }

            const restricted = answer.restricted === true;
            if (aiTitle) aiTitle.textContent = String(answer.title || '');
            if (aiCopy) aiCopy.textContent = String(answer.answer || '');
            if (aiRestrictedPreview) aiRestrictedPreview.hidden = !restricted;
            if (aiRestrictedAction) aiRestrictedAction.hidden = !restricted;
            if (aiChecklist) {
                aiChecklist.replaceChildren();
                const items = !restricted && Array.isArray(answer.checklist) ? answer.checklist : [];
                items.forEach((item) => {
                    const listItem = document.createElement('li');
                    listItem.textContent = String(item || '');
                    aiChecklist.appendChild(listItem);
                });
                aiChecklist.hidden = items.length === 0;
            }
            if (aiSource) {
                const source = String(answer.source || '');
                aiSource.textContent = source ? `Sources: ${source}` : '';
                aiSource.hidden = !source;
            }
            aiAnswer.dataset.resourceRestricted = restricted ? 'true' : 'false';
            aiAnswer.hidden = false;
        };

        const applyFilters = () => {
            let visibleCount = 0;

            cards.forEach((card) => {
                const categories = String(card.dataset.resourceCategories || '').split(',');
                const visible = selectedFilter === 'all'
                    || card.dataset.resourceAccess === selectedFilter
                    || categories.includes(selectedFilter);
                card.classList.toggle('hidden', !visible);
                if (visible) visibleCount += 1;
            });

            if (empty) empty.classList.toggle('hidden', visibleCount !== 0 || aiPending);
        };

        const runAiSearch = async (question, requestSequence) => {
            try {
                const response = await axios.post(searchUrl, {
                    section_id: searchSectionId,
                    question,
                });
                if (requestSequence !== searchRequestSequence) return;

                aiPending = false;
                syncSearchButton();
                const answer = response?.data?.answer || null;
                if (!response?.data?.success || !answer) throw new Error('Resource search response was unavailable.');

                if (!answer.ready) {
                    semanticResourceIds = null;
                    setSearchStatus(String(answer.answer || root.dataset.resourceSearchUnavailableLabel || ''));
                    clearAiAnswer();
                } else {
                    semanticResourceIds = Array.isArray(answer.matching_resource_ids)
                        ? answer.matching_resource_ids.map((id) => Number(id)).filter((id) => id > 0)
                        : [];
                    const rank = new Map(semanticResourceIds.map((id, index) => [id, index]));
                    cards.forEach((card, index) => {
                        const resourceId = Number(card.dataset.resourceId || 0);
                        card.style.order = String(rank.has(resourceId) ? rank.get(resourceId) : semanticResourceIds.length + index);
                    });
                    setSearchStatus('');
                    renderAiAnswer(answer);
                }
                applyFilters();
            } catch (error) {
                if (requestSequence !== searchRequestSequence) return;
                aiPending = false;
                syncSearchButton();
                semanticResourceIds = null;
                clearAiAnswer();
                setSearchStatus(root.dataset.resourceSearchUnavailableLabel || '');
                cards.forEach((card) => { card.style.order = ''; });
                applyFilters();
            }
        };

        const startSearch = () => {
            const question = String(search?.value || '').trim();
            if (question.length < 3 || !searchUrl || searchSectionId < 1 || aiPending) return;

            searchRequestSequence += 1;
            const requestSequence = searchRequestSequence;
            aiPending = true;
            syncSearchButton();
            setSearchStatus(root.dataset.resourceSearchingLabel || '');
            applyFilters();
            runAiSearch(question, requestSequence);
        };

        if (search) {
            search.addEventListener('input', () => {
                searchRequestSequence += 1;
                semanticResourceIds = null;
                aiPending = false;
                setSearchStatus('');
                clearAiAnswer();
                cards.forEach((card) => { card.style.order = ''; });
                applyFilters();
                syncSearchButton();
            });
            search.addEventListener('keydown', (event) => {
                if (event.key !== 'Enter') return;

                event.preventDefault();
                startSearch();
            });
        }
        if (searchButton) searchButton.addEventListener('click', startSearch);
        filters.forEach((filter) => {
            filter.addEventListener('click', () => {
                selectedFilter = String(filter.dataset.resourceCategory || 'all');
                filters.forEach((item) => {
                    item.setAttribute('aria-pressed', item === filter ? 'true' : 'false');
                });
                applyFilters();
            });
        });
        modalOpeners.forEach((opener) => {
            opener.addEventListener('click', () => {
                const modalId = opener.getAttribute('aria-controls');
                const modal = modalId ? document.getElementById(modalId) : null;
                if (modal && typeof modal.showModal === 'function') {
                    modal.showModal();
                    return;
                }

                const fallbackUrl = opener.dataset.resourceFallbackUrl;
                if (fallbackUrl) window.location.assign(fallbackUrl);
            });
        });
        unlockOpeners.forEach((opener) => {
            opener.addEventListener('click', () => {
                const destination = String(opener.dataset.resourceUnlockUrl || '');
                if (!destination) return;

                if (unlockModal && unlockConfirm && typeof unlockModal.showModal === 'function') {
                    unlockConfirm.href = destination;
                    unlockModal.showModal();
                    return;
                }

                window.location.assign(destination);
            });
        });
        modals.forEach((modal) => {
            modal.querySelectorAll('[data-resource-close-modal]').forEach((closer) => {
                closer.addEventListener('click', () => modal.close());
            });
            modal.addEventListener('click', (event) => {
                if (event.target === modal) modal.close();
            });
        });
        const openAssetPreview = (previewUrl, downloadUrl, name, previewType) => {
            if (!previewUrl) return false;
            if (!pdfModal || !pdfObject || !imagePreview || typeof pdfModal.showModal !== 'function') return false;

            const isPdf = previewType === 'pdf';
            if (pdfTitle) pdfTitle.textContent = name;
            if (pdfOpenNew) pdfOpenNew.href = previewUrl;
            if (pdfDownload) {
                pdfDownload.href = downloadUrl || previewUrl;
                pdfDownload.hidden = !downloadUrl;
            }
            if (pdfFallback) pdfFallback.href = previewUrl;

            pdfObject.hidden = !isPdf;
            imagePreview.hidden = isPdf;
            if (isPdf) {
                imagePreview.removeAttribute('src');
                imagePreview.alt = '';
                pdfObject.setAttribute('aria-label', `${name} preview`);
                pdfObject.setAttribute('data', previewUrl);
            } else {
                pdfObject.removeAttribute('data');
                imagePreview.src = previewUrl;
                imagePreview.alt = name;
            }

            if (!pdfModal.open) pdfModal.showModal();
            return true;
        };
        pdfOpeners.forEach((opener) => {
            opener.addEventListener('click', () => {
                const previewUrl = String(opener.dataset.resourcePdfUrl || '');
                const downloadUrl = String(opener.dataset.resourcePdfDownloadUrl || '');
                const name = String(opener.dataset.resourcePdfName || 'PDF resource');

                if (!previewUrl) return;
                if (openAssetPreview(previewUrl, downloadUrl, name, 'pdf')) return;

                const opened = window.open(previewUrl, '_blank');
                if (opened) opened.opener = null;
                else window.location.assign(previewUrl);
            });
        });
        imageOpeners.forEach((opener) => {
            opener.addEventListener('click', () => {
                const previewUrl = String(opener.dataset.resourceImageUrl || '');
                const name = String(opener.dataset.resourceImageName || 'Image resource');

                if (!previewUrl) return;
                if (openAssetPreview(previewUrl, '', name, 'image')) return;

                const opened = window.open(previewUrl, '_blank');
                if (opened) opened.opener = null;
            });
        });
        if (pdfModal) {
            pdfModal.querySelectorAll('[data-resource-close-pdf]').forEach((closer) => {
                closer.addEventListener('click', () => pdfModal.close());
            });
            pdfModal.addEventListener('click', (event) => {
                if (event.target === pdfModal) pdfModal.close();
            });
            pdfModal.addEventListener('close', () => {
                if (pdfObject) pdfObject.removeAttribute('data');
                if (imagePreview) {
                    imagePreview.removeAttribute('src');
                    imagePreview.alt = '';
                    imagePreview.hidden = true;
                }
            });
        }
        applyFilters();
        if (!requestedResourceOpened) {
            const matchingCards = requestedResourceSlug
                ? cards.filter((card) => card.dataset.resourceSlug === requestedResourceSlug)
                : (/^[1-9]\d*$/.test(requestedResourceId || '')
                    ? cards.filter((card) => card.dataset.resourceId === requestedResourceId)
                    : []);
            const requestedCard = matchingCards.length === 1 ? matchingCards[0] : null;
            const opener = requestedCard ? requestedCard.querySelector('[data-resource-open-modal]') : null;
            if (opener) {
                requestedResourceOpened = true;
                opener.click();
            }
        }
    });
}

app.config.compilerOptions.isCustomElement = (tag) => tag === 'emoji-picker';

// mount app
app.mount("#app");
initializeWebsiteResourceEmbeds();
